import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  Badge,
  Tooltip,
  InputAdornment,
  Fab,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Paper,
  IconButton,
  Menu,
  Chip,
  Card,
  CardContent,
  Avatar,
  Tabs,
  Tab,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  InputLabel,
  Select,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import jsPDF from 'jspdf';
import ResponsiveSignaturePad from '../../components/ResponsiveSignaturePad';
import AdminLayout from '../../components/AdminLayout';
import PatientFormDialog from '../../components/PatientFormDialog';
import HistoriaClinicaDialog from '../../components/HistoriaClinicaDialog';
import { debugConsentimientos } from '../../utils/debugConsentimientos';
import { patientsService, historiaClinicaService, recetariosService, remisionesService } from '../../services/firebaseServices';
import { consentimientosService } from '../../services/consentimientosService';
import { vacunasService, desparasitacionesService } from '../../services/medicinaPreventiva';
import { getAuth } from 'firebase/auth';
import app from '../../config/firebase';
import '../../debugFirebase'; // Importar funciones de debug
import {
  People as PeopleIcon,
  Pets as PetsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  History as HistoryIcon,
  Medication as MedicationIcon,
  MedicalServices as MedicalIcon,
  Assignment as AssignmentIcon,
  LocalPharmacy as PharmacyIcon,
  Vaccines as VaccinesIcon,
  Event as EventIcon,
  Print as PrintIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Policy as PolicyIcon,
  LocalHospital as LocalHospitalIcon,
  Block as BlockIcon,
  FileDownload as FileDownloadIcon,
  WarningAmberRounded as WarningIcon
} from '@mui/icons-material';

// Función de utilidad para actualizar la fecha de última visita de un paciente
const updatePatientLastVisit = async (patientId) => {
  if (!patientId) return { success: false, error: 'ID de paciente no válido' };
  
  try {
    // Usar el objeto Date directamente en lugar de una cadena formateada
    const today = new Date();
    
    // Actualizar sólo el campo de última visita
    const result = await patientsService.updatePatient(patientId, {
      ultimaVisita: today
    });
    
    console.log(`✅ Fecha de última visita actualizada para paciente ${patientId}: ${today.toLocaleDateString('es-ES')}`);
    return result;
  } catch (error) {
    console.error('❌ Error actualizando fecha de última visita:', error);
    return { success: false, error: error.message };
  }
};

// Inicializar servicios directamente
const auth = getAuth(app);

const EMPTY_PATIENT_DATA = {
  nombre: '',
  especie: 'Canino',
  raza: '',
  sexo: 'Macho',
  fechaNacimiento: '',
  peso: '',
  color: '',
  microchip: 'no',
  estado: 'activo',
  foto: '',
  propietario: '',
  propietarioApellido: '',
  propietarioTelefono: '',
  propietarioEmail: '',
  propietarioDireccion: '',
  propietarioCiudad: '',
  propietarioCedula: '',
  contactoEmergenciaNombre: '',
  contactoEmergenciaTelefono: '',
  contactoEmergenciaRelacion: '',
  observaciones: ''
};

const EMPTY_HISTORIA_DATA = {
  motivoConsulta: '',
  anamnesis: '',
  enfermedadesPrevias: '',
  dieta: '',
  cirugiasPrevias: '',
  esterilizado: '',
  esquemaVacunal: '',
  planDesparasitacion: '',
  tratamientosRecientes: '',
  numeroPartos: '',
  viajesRecientes: '',
  viveMascotas: '',
  comportamientoAnimal: '',
  estadoConciencia: '',
  estadoCorporal: '',
  peso: '',
  frecuenciaCardiaca: '',
  frecuenciaRespiratoria: '',
  temperatura: '',
  deshidratacion: '',
  mucosas: '',
  tllc: '',
  trpc: '',
  tegumentarioNormal: false,
  tegumentarioAnormal: false,
  tegumentarioEvaluacion: '',
  digestivoNormal: false,
  digestivoAnormal: false,
  digestivoEvaluacion: '',
  respiratorioNormal: false,
  respiratorioAnormal: false,
  respiratorioEvaluacion: '',
  musculoesqueleticoNormal: false,
  musculoesqueleticoAnormal: false,
  musculoesqueleticoEvaluacion: '',
  nerviosoNormal: false,
  nerviosoAnormal: false,
  nerviosoEvaluacion: '',
  urinarioNormal: false,
  urinarioAnormal: false,
  urinarioEvaluacion: '',
  reproductivoNormal: false,
  reproductivoAnormal: false,
  reproductivoEvaluacion: '',
  gangliosNormal: false,
  gangliosAnormal: false,
  gangliosEvaluacion: '',
  palpacionNormal: false,
  palpacionAnormal: false,
  palpacionEvaluacion: '',
  cavidadOralNormal: false,
  cavidadOralAnormal: false,
  cavidadOralEvaluacion: '',
  narizNormal: false,
  narizAnormal: false,
  narizEvaluacion: '',
  ojosNormal: false,
  ojosAnormal: false,
  ojosEvaluacion: '',
  oidosNormal: false,
  oidosAnormal: false,
  oidosEvaluacion: '',
  ayudasDiagnosticas: '',
  tratamientoEnCasa: '',
  formulaMedica: '',
  diagnostico: '',
  pronostico: '',
  observaciones: '',
  veterinario: ''
};

const EMPTY_RECETA_DATA = {
  medicamentos: [
    {
      medicamento: '',
      dosis: '',
      observacion: '',
    }
  ],
  observaciones: '',
  veterinario: '',
  tarjetaProfesional: ''
};

const cloneRecetaData = (data = EMPTY_RECETA_DATA) => ({
  ...EMPTY_RECETA_DATA,
  ...data,
  medicamentos: (data.medicamentos && data.medicamentos.length > 0
    ? data.medicamentos
    : EMPTY_RECETA_DATA.medicamentos
  ).map((med) => ({
    medicamento: med.medicamento || '',
    dosis: med.dosis || '',
    observacion: med.observacion || '',
  }))
});

// Estilos reutilizables para inputs de receta
const recetaInputSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': { borderColor: '#c46494' },
    '&.Mui-focused fieldset': { borderColor: '#c46494' }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#c46494' }
};

const RecetaDialog = React.memo(({
  open,
  onClose,
  onSave,
  editingReceta,
  selectedPatientName,
  initialData,
}) => {
  const [localData, setLocalData] = useState(cloneRecetaData(initialData));

  // Re-sincronizar el formulario cuando se abre o cambian los datos iniciales
  // (el diálogo permanece montado, por lo que el inicializador de useState no
  // vuelve a ejecutarse al editar una receta existente).
  useEffect(() => {
    if (!open) return;
    setLocalData(cloneRecetaData(initialData));
  }, [open, initialData]);

  const handleRecetaDataChange = (field, value) => {
    setLocalData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMedicamentoChange = (index, field, value) => {
    setLocalData((prev) => {
      const medicamentos = [...prev.medicamentos];
      medicamentos[index] = {
        ...medicamentos[index],
        [field]: value
      };

      return {
        ...prev,
        medicamentos
      };
    });
  };

  const handleAddMedicamento = () => {
    setLocalData((prev) => ({
      ...prev,
      medicamentos: [
        ...prev.medicamentos,
        {
          medicamento: '',
          dosis: '',
          observacion: '',
        }
      ]
    }));
  };

  const handleRemoveMedicamento = (index) => {
    setLocalData((prev) => {
      const medicamentos = [...prev.medicamentos];
      medicamentos.splice(index, 1);

      if (medicamentos.length === 0) {
        medicamentos.push({
          medicamento: '',
          dosis: '',
          observacion: '',
        });
      }

      return {
        ...prev,
        medicamentos
      };
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: '#c46494',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <MedicationIcon />
        {editingReceta ? 'Editar' : 'Nueva'} Receta para {selectedPatientName}
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#c46494' }}>
            <MedicationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Medicamentos
          </Typography>

          {localData.medicamentos.map((med, index) => (
            <Box
              key={index}
              sx={{
                mb: 3,
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                position: 'relative',
                bgcolor: '#fafafa'
              }}
            >
              <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveMedicamento(index)}
                  disabled={localData.medicamentos.length <= 1}
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Medicamento {index + 1}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Medicamento"
                    value={med.medicamento}
                    onChange={(e) => handleMedicamentoChange(index, 'medicamento', e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    sx={recetaInputSx}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Cantidad"
                    value={med.dosis}
                    onChange={(e) => handleMedicamentoChange(index, 'dosis', e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    placeholder="Ej: 1 tableta"
                    sx={recetaInputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Observación del medicamento"
                    value={med.observacion || ''}
                    onChange={(e) => handleMedicamentoChange(index, 'observacion', e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    placeholder="Instrucciones específicas para este medicamento"
                    sx={recetaInputSx}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={handleAddMedicamento}
              sx={{
                color: '#c46494',
                borderColor: '#c46494',
                '&:hover': {
                  backgroundColor: 'rgba(196, 100, 148, 0.04)',
                  borderColor: '#c46494'
                }
              }}
            >
              Agregar Medicamento
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Veterinario"
              value={localData.veterinario}
              onChange={(e) => handleRecetaDataChange('veterinario', e.target.value)}
              fullWidth
              required
              variant="outlined"
              placeholder="Nombre del veterinario que prescribe"
              sx={{ mb: 2, ...recetaInputSx }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Tarjeta Profesional"
              value={localData.tarjetaProfesional}
              onChange={(e) => handleRecetaDataChange('tarjetaProfesional', e.target.value)}
              fullWidth
              required
              variant="outlined"
              placeholder="Número de tarjeta profesional"
              sx={{ mb: 2, ...recetaInputSx }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Recomendaciones"
              value={localData.observaciones}
              onChange={(e) => handleRecetaDataChange('observaciones', e.target.value)}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              placeholder="Instrucciones adicionales, efectos secundarios a vigilar, etc."
              sx={recetaInputSx}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: '#666',
            borderColor: '#666',
            '&:hover': {
              borderColor: '#999',
              bgcolor: 'rgba(0,0,0,0.04)'
            }
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={() => onSave(localData)}
          variant="contained"
          sx={{
            bgcolor: '#c46494',
            '&:hover': { bgcolor: '#a05080' },
            '&:disabled': { bgcolor: '#e0e0e0' }
          }}
        >
          {editingReceta ? 'Actualizar Receta' : 'Guardar Receta'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const EMPTY_REMISION_DATA = {
  motivoRemision: '',
  clinicaRemision: '',
  veterinario: '',
  tarjetaProfesional: ''
};

const cloneRemisionData = (data = EMPTY_REMISION_DATA) => ({
  ...EMPTY_REMISION_DATA,
  ...data
});

const RemisionDialog = React.memo(({
  open,
  onClose,
  onSave,
  editingRemision,
  selectedPatientName,
  initialData,
  saving,
}) => {
  const [localData, setLocalData] = useState(cloneRemisionData(initialData));

  useEffect(() => {
    if (!open) return;
    setLocalData(cloneRemisionData(initialData));
  }, [open, initialData]);

  const handleFieldChange = (field, value) => {
    setLocalData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: '#c46494',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <LocalHospitalIcon />
        {editingRemision ? 'Editar' : 'Nueva'} Remisión Clínica para {selectedPatientName}
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label="Motivo de la remisión"
              value={localData.motivoRemision}
              onChange={(e) => handleFieldChange('motivoRemision', e.target.value)}
              fullWidth
              required
              multiline
              rows={4}
              variant="outlined"
              placeholder="Describa el motivo por el cual se remite al paciente"
              sx={recetaInputSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Clínica a la que se remite"
              value={localData.clinicaRemision}
              onChange={(e) => handleFieldChange('clinicaRemision', e.target.value)}
              fullWidth
              required
              variant="outlined"
              placeholder="Nombre de la clínica o especialista de destino"
              sx={recetaInputSx}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Veterinario"
              value={localData.veterinario}
              onChange={(e) => handleFieldChange('veterinario', e.target.value)}
              fullWidth
              required
              variant="outlined"
              placeholder="Nombre del veterinario que remite"
              sx={recetaInputSx}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Tarjeta Profesional"
              value={localData.tarjetaProfesional}
              onChange={(e) => handleFieldChange('tarjetaProfesional', e.target.value)}
              fullWidth
              required
              variant="outlined"
              placeholder="Número de tarjeta profesional"
              sx={recetaInputSx}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={saving}
          sx={{
            color: '#666',
            borderColor: '#666',
            '&:hover': {
              borderColor: '#999',
              bgcolor: 'rgba(0,0,0,0.04)'
            }
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={() => onSave(localData)}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : null}
          sx={{
            bgcolor: '#c46494',
            '&:hover': { bgcolor: '#a05080' },
            '&:disabled': { bgcolor: '#e0e0e0' }
          }}
        >
          {saving
            ? 'Guardando...'
            : (editingRemision ? 'Actualizar Remisión' : 'Guardar Remisión')}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const ConsentimientoDialog = React.memo(function ConsentimientoDialog({
  open,
  onClose,
  onSave,
  onPreview,
  onNotify,
  editingConsentimiento,
  initialData,
  selectedPatient,
  selectedPatientName,
  selectedOwnerName,
  pacientesMismoPropietario = []
}) {
  const [formData, setFormData] = useState(initialData);
  // IDs de las otras mascotas del mismo propietario que se incluirán en este consentimiento
  const [pacientesExtraIds, setPacientesExtraIds] = useState([]);
  const signatureRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!open) return;
    setFormData(initialData);
    setPacientesExtraIds([]);
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  }, [open, initialData]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTogglePacienteExtra = (pacienteId) => {
    setPacientesExtraIds((prev) =>
      prev.includes(pacienteId)
        ? prev.filter((id) => id !== pacienteId)
        : [...prev, pacienteId]
    );
  };

  // Lista completa de pacientes que cubre el consentimiento (el principal + los seleccionados)
  const getPacientesConsentimiento = () => {
    const extras = pacientesMismoPropietario.filter((p) => pacientesExtraIds.includes(p.id));
    return [selectedPatient, ...extras].filter(Boolean);
  };

  const handlePreview = () => {
    const prevData = {
      ...formData,
      fecha: new Date(),
      propietarioNombre: selectedOwnerName || '',
      pacientes: getPacientesConsentimiento()
    };
    onPreview(prevData);
  };

  const handleSave = async () => {
    if (!formData.nombreVeterinario || !formData.mpVeterinario) {
      onNotify({
        open: true,
        message: 'Por favor completa los campos requeridos: nombre del veterinario y matricula profesional',
        severity: 'error'
      });
      return;
    }

    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      onNotify({
        open: true,
        message: 'Se requiere la firma del propietario',
        severity: 'error'
      });
      return;
    }

    const firmaBase64 = signatureRef.current.getSignatureDataUrl();
    await onSave({
      ...formData,
      firmaPropietario: firmaBase64,
      pacientes: getPacientesConsentimiento()
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: '#c46494',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <PolicyIcon />
        {editingConsentimiento ? 'Editar' : 'Nuevo'} Consentimiento para {selectedPatientName}
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nombre del Veterinario"
                value={formData.nombreVeterinario}
                onChange={(e) => handleFieldChange('nombreVeterinario', e.target.value)}
                fullWidth
                required
                variant="outlined"
                placeholder="Dr. Juan Perez"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#c46494' },
                    '&.Mui-focused fieldset': { borderColor: '#c46494' }
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#c46494' }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Matricula Profesional"
                value={formData.mpVeterinario}
                onChange={(e) => handleFieldChange('mpVeterinario', e.target.value)}
                fullWidth
                required
                variant="outlined"
                placeholder="12345-A"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#c46494' },
                    '&.Mui-focused fieldset': { borderColor: '#c46494' }
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#c46494' }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Correo del Veterinario"
                value={formData.correoVeterinario}
                onChange={(e) => handleFieldChange('correoVeterinario', e.target.value)}
                fullWidth
                variant="outlined"
                placeholder="doctor@clinica.com"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#c46494' },
                    '&.Mui-focused fieldset': { borderColor: '#c46494' }
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#c46494' }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                label="Tipo de Consentimiento"
                value={formData.tipoConsentimiento}
                onChange={(e) => handleFieldChange('tipoConsentimiento', e.target.value)}
                fullWidth
                required
                variant="outlined"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#c46494' },
                    '&.Mui-focused fieldset': { borderColor: '#c46494' }
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#c46494' }
                }}
              >
                <MenuItem value="Consentimiento de Servicios a Domicilio">Consentimiento de Servicios a Domicilio</MenuItem>
                <MenuItem value="Consentimiento de Vacunacion">Consentimiento de Vacunacion</MenuItem>
                <MenuItem value="Consentimiento de Eutanasia">Consentimiento de Eutanasia</MenuItem>
                <MenuItem value="Consentimiento de Hospitalizacion">Consentimiento de Hospitalizacion</MenuItem>
                <MenuItem value="Consentimiento de Sedacion">Consentimiento de Sedacion</MenuItem>
                <MenuItem value="Consentimiento de Alta Voluntaria">Consentimiento de Alta Voluntaria</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {!editingConsentimiento && pacientesMismoPropietario.length > 0 && (
            <Box
              sx={{
                mt: 1,
                mb: 2,
                p: 2,
                border: '1px solid #e0c3d3',
                borderRadius: 1,
                bgcolor: '#fbf3f7'
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#a05080', fontWeight: 'bold', mb: 0.5 }}>
                Incluir otras mascotas del mismo propietario
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Al seleccionar más mascotas, el propietario firma una sola vez y se genera un consentimiento para cada una.
              </Typography>
              <FormGroup>
                {pacientesMismoPropietario.map((p) => (
                  <FormControlLabel
                    key={p.id}
                    control={
                      <Checkbox
                        checked={pacientesExtraIds.includes(p.id)}
                        onChange={() => handleTogglePacienteExtra(p.id)}
                        sx={{ color: '#c46494', '&.Mui-checked': { color: '#c46494' } }}
                      />
                    }
                    label={`${p.nombre || 'Sin nombre'}${p.especie ? ` · ${p.especie}` : ''}${p.raza ? ` · ${p.raza}` : ''}`}
                  />
                ))}
              </FormGroup>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<ViewIcon />}
              onClick={handlePreview}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
            >
              Visualizar Consentimiento
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Despues de revisar el consentimiento, el propietario debe firmar a continuacion si esta de acuerdo.
          </Alert>

          <Grid item xs={12}>
            <Box
              sx={{
                border: '1px solid #ccc',
                p: 2,
                borderRadius: 1,
                width: '100%'
              }}
            >
              <ResponsiveSignaturePad
                ref={signatureRef}
                label="Firma del Propietario"
                height={isMobile ? 300 : 150}
              />
            </Box>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
        <Button
          onClick={onClose}
          variant="outlined"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          sx={{ bgcolor: '#c46494', '&:hover': { bgcolor: '#a05080' } }}
        >
          Guardar Consentimiento
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const GestionPacientes = () => {
  // Material-UI theme y media queries
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Estados principales
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const getPropietarioCedula = (p) => {
    if (!p) return '';
    return p.propietarioCedula || '';
  };
  const getPropietarioNombreCompleto = (p) => {
    if (!p) return '';
    return `${p.propietario || ''} ${p.propietarioApellido || ''}`.trim();
  };
  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  const boldHtml = (value = '') => (value ? `<strong>${escapeHtml(value)}</strong>` : '');

  // Une nombres en lenguaje natural: "A", "A y B", "A, B y C"
  const unirNombres = (items = []) => {
    const lista = items.filter(Boolean);
    if (lista.length === 0) return '';
    if (lista.length === 1) return lista[0];
    return `${lista.slice(0, -1).join(', ')} y ${lista[lista.length - 1]}`;
  };

  // Devuelve las demás mascotas que comparten propietario (misma cédula) con el paciente dado
  const getPacientesMismoPropietario = (patient) => {
    if (!patient) return [];
    const cedula = getPropietarioCedula(patient);
    if (!cedula) return [];
    return patients.filter((p) =>
      p.id !== patient.id &&
      getPropietarioCedula(p) === cedula
    );
  };

  // Fragmento "del paciente X, especie..., raza..." (o plural) para los textos HTML del consentimiento
  const buildResponsableFraseHtml = (pacientes = []) => {
    const lista = (pacientes && pacientes.length) ? pacientes : [selectedPatient].filter(Boolean);
    if (lista.length <= 1) {
      const p = lista[0] || {};
      return `del paciente ${boldHtml(p.nombre || '[nombre del paciente]')}, especie ${p.especie || '[especie]'}, raza ${p.raza || '[raza]'}`;
    }
    const items = lista.map((p) => `${boldHtml(p.nombre || '[nombre del paciente]')} (especie ${p.especie || '[especie]'}, raza ${p.raza || '[raza]'})`);
    return `de los pacientes ${unirNombres(items)}`;
  };

  // Igual que el anterior pero en texto plano (nombres en mayúsculas) para el PDF
  const buildResponsableFrasePlain = (pacientes = []) => {
    const lista = (pacientes && pacientes.length) ? pacientes : [selectedPatient].filter(Boolean);
    if (lista.length <= 1) {
      const p = lista[0] || {};
      return `del paciente ${(p.nombre || '[nombre del paciente]').toUpperCase()}, especie ${p.especie || '[especie]'}, raza ${p.raza || '[raza]'}`;
    }
    const items = lista.map((p) => `${(p.nombre || '[nombre del paciente]').toUpperCase()} (especie ${p.especie || '[especie]'}, raza ${p.raza || '[raza]'})`);
    return `de los pacientes ${unirNombres(items)}`;
  };

  // Nombres de los pacientes del consentimiento unidos en lenguaje natural ("Lula y Max")
  const buildNombresPacientes = (pacientes = []) => {
    const lista = (pacientes && pacientes.length) ? pacientes : [selectedPatient].filter(Boolean);
    return unirNombres(lista.map((p) => p?.nombre || '').filter(Boolean));
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Estado para el diálogo de confirmación de eliminación (reemplaza window.confirm)
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: 'Eliminar',
    onConfirm: null,
    loading: false
  });

  const closeConfirmDialog = () => setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));

  const handleConfirmDialog = async () => {
    if (typeof confirmDialog.onConfirm !== 'function') {
      closeConfirmDialog();
      return;
    }
    setConfirmDialog((prev) => ({ ...prev, loading: true }));
    try {
      await confirmDialog.onConfirm();
    } finally {
      closeConfirmDialog();
    }
  };

  // Estados para el expediente expandido
  const [expedienteOpen, setExpedienteOpen] = useState(false);
  const [expedienteTabValue, setExpedienteTabValue] = useState(0);
  const [historiaClinica, setHistoriaClinica] = useState([]);
  const [recetas, setRecetas] = useState([]);
  const [consentimientos, setConsentimientos] = useState([]);
  const [loadingHistorias, setLoadingHistorias] = useState(false);
  const [loadingConsentimientos, setLoadingConsentimientos] = useState(false);
  const [consentimientosLoadedFor, setConsentimientosLoadedFor] = useState(null);
  
  // Estados para medicina preventiva
  const [vacunas, setVacunas] = useState([]);
  const [desparasitaciones, setDesparasitaciones] = useState([]);
  const [, setLoadingVacunas] = useState(false);
  const [, setLoadingDesparasitaciones] = useState(false);
  const [nuevaVacunaOpen, setNuevaVacunaOpen] = useState(false);
  const [nuevaDesparasitacionOpen, setNuevaDesparasitacionOpen] = useState(false);
  const [editingVacuna, setEditingVacuna] = useState(null);
  const [editingDesparasitacion, setEditingDesparasitacion] = useState(null);
  const [vacunaData, setVacunaData] = useState({
    nombreVacuna: '',
    fabricante: '',
    fechaAplicacion: '',
    proximaDosis: '',
    lote: '',
    veterinario: '',
    observaciones: ''
  });
  const [desparasitacionData, setDesparasitacionData] = useState({
    nombreProducto: '',
    tipo: 'interno', // interno/externo/interno y externo
    principioActivo: '',
    fechaAplicacion: '',
    proximaDosis: '',
    dosis: '',
    veterinario: '',
    observaciones: ''
  });
  
  // Estado para el menú de cambio de estado del paciente
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState({});  // Para los menús de estado en la lista

  // Estados para nueva historia clínica
  const [nuevaHistoriaOpen, setNuevaHistoriaOpen] = useState(false);
  
  // Estados para consentimientos informados
  const [nuevoConsentimientoOpen, setNuevoConsentimientoOpen] = useState(false);
  const [editingConsentimiento, setEditingConsentimiento] = useState(null);
  const [consentimientoParaImprimir, setConsentimientoParaImprimir] = useState(null);
  const [imprimirConsentimientoModalOpen, setImprimirConsentimientoModalOpen] = useState(false);
  const [consentimientoFormInitialData, setConsentimientoFormInitialData] = useState({
    nombreVeterinario: '',
    mpVeterinario: '',
    correoVeterinario: auth.currentUser?.email || '',
    firmaPropietario: null,
    tipoConsentimiento: 'Consentimiento de Servicios a Domicilio',
  });
  
  // Estados para nueva receta
  const [nuevaRecetaOpen, setNuevaRecetaOpen] = useState(false);
  const [recetaFormInitialData, setRecetaFormInitialData] = useState(EMPTY_RECETA_DATA);
  const [savingHistoria, setSavingHistoria] = useState(false);
  const [historiaFormInitialData, setHistoriaFormInitialData] = useState(EMPTY_HISTORIA_DATA);

  const [imprimirModalOpen, setImprimirModalOpen] = useState(false);
  const [recetaParaImprimir, setRecetaParaImprimir] = useState(null);

  // Estados para remisiones clínicas
  const [remisiones, setRemisiones] = useState([]);
  const [nuevaRemisionOpen, setNuevaRemisionOpen] = useState(false);
  const [remisionFormInitialData, setRemisionFormInitialData] = useState(EMPTY_REMISION_DATA);
  const [editingRemision, setEditingRemision] = useState(null);
  const [remisionParaImprimir, setRemisionParaImprimir] = useState(null);
  const [imprimirRemisionModalOpen, setImprimirRemisionModalOpen] = useState(false);
  const [savingRemision, setSavingRemision] = useState(false);

  // Estados de búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEspecie, setFilterEspecie] = useState('todos');
  const [filterEstado, setFilterEstado] = useState('todos');
  const [showFilters, setShowFilters] = useState(false);

  // Estados del formulario
  const [patientFormInitialData, setPatientFormInitialData] = useState(EMPTY_PATIENT_DATA);

  // Cargar datos desde Firebase (optimizado con useCallback)
  const loadPatientsFromFirebase = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Cargando pacientes desde Firebase...');
      const result = await patientsService.getAllPatients();
      
      if (result.success) {
        console.log('Pacientes cargados exitosamente:', result.data);
        setPatients(result.data);
        // No establecer filteredPatients aquí, dejar que el efecto de filtrado lo maneje
        setSnackbar({
          open: true,
          message: `${result.data.length} pacientes cargados desde Firebase`,
          severity: 'success'
        });
      } else {
        console.error('Error cargando pacientes:', result.error);
        setSnackbar({
          open: true,
          message: `Error cargando pacientes: ${result.error}`,
          severity: 'error'
        });
        setPatients([]);
      }
    } catch (error) {
      console.error('Error en loadPatientsFromFirebase:', error);
      setSnackbar({
        open: true,
        message: `Error de conexión: ${error.message}`,
        severity: 'error'
      });
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    loadPatientsFromFirebase();
  }, [loadPatientsFromFirebase]);
  
  // Función optimizada para cargar consentimientos (lazy loading)
  const cargarConsentimientos = useCallback(async (pacienteId) => {
    if (loadingConsentimientos) return; // Prevenir múltiples cargas
    
    setLoadingConsentimientos(true);
    try {
      console.log('🔧 Cargando consentimientos para:', pacienteId);
      const debugResult = await debugConsentimientos(pacienteId);
      
      if (debugResult.success) {
        let consentimientosList = debugResult.forms || [];
        
        // Ordenar los consentimientos por fecha (el más reciente primero)
        consentimientosList = consentimientosList.sort((a, b) => {
          const fechaA = a.fecha?.toDate?.() || new Date(a.fecha || 0);
          const fechaB = b.fecha?.toDate?.() || new Date(b.fecha || 0);
          return fechaB - fechaA;
        });
        
        setConsentimientos(consentimientosList);
        console.log('✅ Consentimientos cargados:', consentimientosList.length);
      } else {
        console.error('❌ Error cargando consentimientos:', debugResult.error);
      }
    } catch (error) {
      console.error('❌ Excepción cargando consentimientos:', error);
    } finally {
      setLoadingConsentimientos(false);
      setConsentimientosLoadedFor(String(pacienteId));
    }
  }, [loadingConsentimientos]);

  useEffect(() => {
    if (selectedPatient?.id) {
      setConsentimientosLoadedFor(null);
    }
  }, [selectedPatient?.id]);

  // Efecto para cargar consentimientos cuando se cambia a la tab de consentimientos (optimizado)
  useEffect(() => {
    // Solo cargar cuando:
    // 1. Estamos en la tab de consentimientos (índice 2)
    // 2. Tenemos un paciente seleccionado
    // 3. No tenemos consentimientos cargados
    // 4. No estamos ya cargando
    if (
      expedienteTabValue === 2 &&
      selectedPatient?.id &&
      !loadingConsentimientos &&
      consentimientosLoadedFor !== String(selectedPatient.id)
    ) {
      console.log('🔄 Carga lazy de consentimientos al cambiar a la tab');
      cargarConsentimientos(String(selectedPatient.id));
    }
  }, [
    expedienteTabValue,
    selectedPatient?.id,
    consentimientosLoadedFor,
    loadingConsentimientos,
    cargarConsentimientos
  ]);

  // Función auxiliar para convertir fecha a timestamp (memoizada)
  const parseDate = useCallback((fecha) => {
    if (!fecha) return 0;
    
    try {
      let fechaObj;
      
      // Manejar diferentes tipos de fecha de Firebase
      if (fecha.toDate && typeof fecha.toDate === 'function') {
        fechaObj = fecha.toDate();
      } else if (fecha.seconds && fecha.nanoseconds) {
        fechaObj = new Date(fecha.seconds * 1000);
      } else if (typeof fecha === 'string') {
        if (fecha.includes('/')) {
          // Formato DD/MM/YYYY -> convertir a formato ISO
          const [day, month, year] = fecha.split('/');
          fechaObj = new Date(year, month - 1, day);
        } else {
          fechaObj = new Date(fecha);
        }
      } else if (fecha instanceof Date) {
        fechaObj = fecha;
      } else {
        fechaObj = new Date(fecha);
      }
      
      return isNaN(fechaObj.getTime()) ? 0 : fechaObj.getTime();
    } catch (error) {
      console.warn('Error parsing date:', fecha, error);
      return 0;
    }
  }, []);

  // Función para obtener prioridad de estado (memoizada)
  const getEstadoPrioridad = useCallback((estado) => {
    switch (estado?.toLowerCase()) {
      case 'critico': return 0;      // Críticos primero
      case 'en_tratamiento': return 1; // En tratamiento segundo
      case 'activo': return 2;       // Activos tercero
      case 'inactivo': return 3;     // Inactivos (fallecidos) al final
      default: return 2;             // Por defecto como activos
    }
  }, []);

  // Filtrado y ordenamiento optimizado con useMemo
  const filteredPatients = useMemo(() => {
    let filtered = [...patients]; // Crear copia para evitar mutaciones

    // Filtro por término de búsqueda
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(patient => 
        patient.nombre.toLowerCase().includes(searchLower) ||
        patient.propietario.toLowerCase().includes(searchLower) ||
        patient.especie.toLowerCase().includes(searchLower) ||
        patient.raza.toLowerCase().includes(searchLower) ||
        patient.microchip.includes(searchTerm)
      );
    }

    // Filtro por especie
    if (filterEspecie !== 'todos') {
      const especieLower = filterEspecie.toLowerCase();
      filtered = filtered.filter(patient => 
        patient.especie.toLowerCase() === especieLower
      );
    }

    // Filtro por estado
    if (filterEstado !== 'todos') {
      filtered = filtered.filter(patient => patient.estado === filterEstado);
    }

    // Ordenar primero por estado (inactivos al final), luego por fecha de última visita
    return filtered.sort((a, b) => {
      const prioridadA = getEstadoPrioridad(a.estado);
      const prioridadB = getEstadoPrioridad(b.estado);

      // Si tienen diferente prioridad de estado, ordenar por prioridad
      if (prioridadA !== prioridadB) {
        return prioridadA - prioridadB;
      }

      // Si tienen el mismo estado, ordenar por fecha de última visita (más reciente primero)
      const timestampA = parseDate(a.ultimaVisita);
      const timestampB = parseDate(b.ultimaVisita);
      
      return timestampB - timestampA;
    });
  }, [patients, searchTerm, filterEspecie, filterEstado, parseDate, getEstadoPrioridad]);

  // Función para calcular edad (optimizada)
  const calcularEdad = useCallback((fechaNacimiento) => {
    if (!fechaNacimiento) return 'N/A';
    
    let fecha;
    // Manejar diferentes tipos de fecha de Firebase
    if (fechaNacimiento.toDate) {
      fecha = fechaNacimiento.toDate();
    } else if (typeof fechaNacimiento === 'string') {
      fecha = new Date(fechaNacimiento);
    } else {
      fecha = new Date(fechaNacimiento);
    }
    
    // Verificar que la fecha sea válida
    if (isNaN(fecha.getTime())) {
      return 'N/A';
    }
    
    const hoy = new Date();
    
    // Calcular diferencia total
    let años = hoy.getFullYear() - fecha.getFullYear();
    let meses = hoy.getMonth() - fecha.getMonth();
    let días = hoy.getDate() - fecha.getDate();
    
    // Ajustar si los días son negativos
    if (días < 0) {
      meses--;
      // Obtener días del mes anterior
      const ultimoDiaDelMesAnterior = new Date(hoy.getFullYear(), hoy.getMonth(), 0).getDate();
      días += ultimoDiaDelMesAnterior;
    }
    
    // Ajustar si los meses son negativos
    if (meses < 0) {
      años--;
      meses += 12;
    }
    
    // Formatear la edad de manera inteligente
    let resultado = '';
    
    if (años > 0) {
      resultado += `${años} año${años === 1 ? '' : 's'}`;
      
      if (meses > 0) {
        resultado += `, ${meses} mes${meses === 1 ? '' : 'es'}`;
      }
      
      // Solo mostrar días si es menor a 2 años para no sobrecargar la información
      if (años < 2 && días > 0) {
        resultado += `, ${días} día${días === 1 ? '' : 's'}`;
      }
    } else if (meses > 0) {
      resultado += `${meses} mes${meses === 1 ? '' : 'es'}`;
      
      if (días > 0) {
        resultado += `, ${días} día${días === 1 ? '' : 's'}`;
      }
    } else {
      resultado = `${días} día${días === 1 ? '' : 's'}`;
    }
    
    return resultado;
  }, []);

  // Función para formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    
    try {
      let fechaObj;
      
      // Manejar diferentes tipos de fecha de Firebase
      if (fecha.toDate && typeof fecha.toDate === 'function') {
        // Timestamp de Firestore
        fechaObj = fecha.toDate();
      } else if (fecha.seconds && fecha.nanoseconds) {
        // Otro formato de timestamp de Firebase
        fechaObj = new Date(fecha.seconds * 1000);
      } else if (typeof fecha === 'string') {
        // Si es una cadena, intentar convertirla a fecha
        if (fecha.includes('/')) {
          // Formato DD/MM/YYYY
          const [day, month, year] = fecha.split('/');
          fechaObj = new Date(year, month - 1, day);
        } else {
          // Otros formatos de cadena
          fechaObj = new Date(fecha);
        }
      } else if (fecha instanceof Date) {
        // Ya es un objeto Date
        fechaObj = fecha;
      } else {
        // Último recurso
        fechaObj = new Date(fecha);
      }
      
      // Verificar si la fecha es válida
      if (isNaN(fechaObj.getTime())) {
        console.warn('Fecha inválida:', fecha);
        return 'N/A';
      }
      
      return fechaObj.toLocaleDateString('es-ES');
    } catch (error) {
      console.error('Error formateando fecha:', error, fecha);
      return 'N/A';
    }
  };
  
  // Función para obtener color del estado (optimizada)
  const getEstadoColor = useCallback((estado) => {
    switch (estado) {
      case 'activo': return 'success';
      case 'inactivo': return 'default';
      case 'en_tratamiento': return 'warning';
      case 'critico': return 'error';
      default: return 'default';
    }
  }, []);

  const generarCartaCondolenciasPDF = async (patient) => {
    if (!patient) return;

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const marginX = 18;
      const headerHeight = 26;
      let yPos = 18;

      const nombreMascota = patient.nombre || 'tu mascota';
      const fechaArchivoSafe = new Date().toLocaleDateString('es-ES').replace(/\//g, '-');
      const nombreArchivoSafe = nombreMascota.replace(/[\\/:*?"<>|]+/g, '-');

      doc.setFillColor(252, 246, 250);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      try {
        const imgUrl = window.location.origin + '/logo192.png';
        doc.addImage(imgUrl, 'PNG', marginX, 4, 18, 18);
      } catch (imgError) {
        console.error('Error al cargar el logo:', imgError);
      }

      doc.setFontSize(16);
      doc.setTextColor(196, 100, 148);
      doc.text('BELKYS VETERINARIA', marginX + 24, 12);
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text('Carta de condolencias', marginX + 24, 19);

      const fechaCarta = new Date().toLocaleDateString('es-ES');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Fecha: ${fechaCarta}`, pageWidth - marginX, 12, { align: 'right' });

      yPos = headerHeight + 10;

      if (patient.foto) {
        try {
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          img.src = patient.foto;

          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });

          const maxSize = 70;
          let imgWidth = img.width || maxSize;
          let imgHeight = img.height || maxSize;
          const scale = Math.min(maxSize / imgWidth, maxSize / imgHeight, 1);
          imgWidth *= scale;
          imgHeight *= scale;

          const x = (pageWidth - imgWidth) / 2;
          const format = (typeof patient.foto === 'string' && patient.foto.startsWith('data:image/png')) ? 'PNG' : 'JPEG';
          doc.addImage(img, format, x, yPos, imgWidth, imgHeight);
          yPos += imgHeight + 8;
        } catch (imgError) {
          console.error('Error al cargar la foto del paciente:', imgError);
        }
      }

      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);

      const bodyText = `Hoy ${nombreMascota} descansa en paz, libre de dolor y rodeado(a) de todo el amor que recibió durante su vida.\n\nGracias por haberlo(a) cuidado, acompañado y amado en cada etapa. Su familia fue su lugar seguro y el hogar donde vivió momentos felices.\n\nAunque hoy deban despedirse físicamente, su huella permanecerá siempre en los recuerdos, en las rutinas compartidas y en el cariño que dejó en sus corazones.\n\nTomar esta decisión también fue un acto de amor y compasión.\n\nCon cariño,\n\nBelkys Hernández\nMédico Veterinario`;

      const bodyLines = doc.splitTextToSize(bodyText, pageWidth - marginX * 2);
      doc.text(bodyLines, marginX, yPos);

      doc.save(`Carta_Condolencias_${nombreArchivoSafe}_${fechaArchivoSafe}.pdf`);
    } catch (error) {
      console.error('Error generando carta de condolencias:', error);
      setSnackbar({
        open: true,
        message: 'Error generando la carta de condolencias: ' + error.message,
        severity: 'error'
      });
    }
  };

  // Función para cambiar el estado de un paciente
  const handleChangePatientStatus = async (pacienteId, nuevoEstado) => {
    try {
      console.log(`🔄 Cambiando estado del paciente ${pacienteId} a: ${nuevoEstado}`);
      setLoading(true);

      const pacienteActual = patients.find(p => p.id === pacienteId);
      
      const result = await patientsService.updatePatient(pacienteId, {
        estado: nuevoEstado,
        ultimaActualizacion: new Date()
      });
      
      if (result.success) {
        // Actualizar la lista de pacientes con el nuevo estado
        // filteredPatients se actualizará automáticamente gracias a useMemo
        setPatients(prevPatients => 
          prevPatients.map(p => 
            p.id === pacienteId ? { ...p, estado: nuevoEstado } : p
          )
        );
        
        setSnackbar({
          open: true,
          message: `Estado del paciente actualizado a: ${nuevoEstado.replace('_', ' ')}`,
          severity: 'success'
        });

        if (nuevoEstado === 'inactivo') {
          await generarCartaCondolenciasPDF(pacienteActual);
        }
      } else {
        console.error('❌ Error actualizando estado:', result.error);
        setSnackbar({
          open: true,
          message: `Error al actualizar estado: ${result.error}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('❌ Excepción actualizando estado:', error);
      setSnackbar({
        open: true,
        message: `Error inesperado: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setFilterEspecie('todos');
    setFilterEstado('todos');
    setShowFilters(false);
  };

  // Función para abrir formulario de nuevo paciente
  const handleAddPatient = () => {
    setEditingPatient(null);
    setPatientFormInitialData(EMPTY_PATIENT_DATA);
    setDialogOpen(true);
  };

  // Función para editar paciente
  const handleEditPatient = (patient) => {
    setEditingPatient(patient);
    setPatientFormInitialData({ ...EMPTY_PATIENT_DATA, ...patient });
    setDialogOpen(true);
    // Cerrar el expediente si está abierto
    if (expedienteOpen) {
      setExpedienteOpen(false);
    }
  };

  // Función para ver expediente
  const handleViewPatient = (patient) => {
    console.log('Abriendo expediente para paciente:', patient);
    setSelectedPatient(patient);
    setExpedienteOpen(true);

    // Siempre comenzar en la pestaña de Historia Clínica
    setExpedienteTabValue(0);

    // Limpiar listas previas y activar indicadores de carga
    setHistoriaClinica([]);
    setRecetas([]);
    setRemisiones([]);
    setConsentimientos([]);
    setVacunas([]);
    setDesparasitaciones([]);
    setLoadingHistorias(true);
    setLoadingConsentimientos(true);
    setLoadingVacunas(true);
    setLoadingDesparasitaciones(true);

    if (!patient.id) {
      console.error('❌ Error: paciente.id es undefined o vacío');
      setLoadingHistorias(false);
      setLoadingConsentimientos(false);
      setLoadingVacunas(false);
      setLoadingDesparasitaciones(false);
      setSnackbar({
        open: true,
        message: 'Error: ID de paciente no válido',
        severity: 'error'
      });
      return;
    }

    const pacienteId = String(patient.id);
    const ordenarPorFecha = (lista) => [...lista].sort((a, b) => {
      const fechaA = a.fecha?.toDate?.() || new Date(a.fecha || 0);
      const fechaB = b.fecha?.toDate?.() || new Date(b.fecha || 0);
      return fechaB - fechaA;
    });

    // Cada bloque se carga de forma independiente y actualiza su propio estado
    // en cuanto resuelve, por lo que las pestañas y contadores aparecen apenas
    // está disponible cada consulta (en lugar de esperar a todas en serie).
    const cargaHistorias = (async () => {
      try {
        const r = await historiaClinicaService.getHistoriasByPaciente(patient.id);
        setHistoriaClinica(r.success ? (r.data || []) : []);
        if (!r.success) console.error('Error cargando historias clínicas:', r.error);
      } catch (error) {
        console.error('Excepción cargando historias clínicas:', error);
        setHistoriaClinica([]);
      } finally {
        setLoadingHistorias(false);
      }
    })();

    const cargaRecetas = (async () => {
      try {
        const r = await recetariosService.getRecetasByPaciente(patient.id);
        if (r.success) {
          setRecetas(r.data || []);
        } else {
          // Solo ante un error real recurrimos al método alternativo
          console.warn('⚠️ Error en getRecetasByPaciente, usando método alternativo:', r.error);
          const todas = await recetariosService.getAllRecetas();
          setRecetas(todas.success
            ? (todas.data || []).filter((receta) => receta.pacienteId === patient.id)
            : []);
        }
      } catch (error) {
        console.error('❌ Excepción cargando recetas:', error);
        setRecetas([]);
      }
    })();

    const cargaRemisiones = (async () => {
      try {
        const r = await remisionesService.getRemisionesByPaciente(patient.id);
        setRemisiones(r.success ? (r.data || []) : []);
        if (!r.success) console.error('❌ Error cargando remisiones:', r.error);
      } catch (error) {
        console.error('❌ Excepción cargando remisiones:', error);
        setRemisiones([]);
      }
    })();

    const cargaConsentimientos = (async () => {
      try {
        const debugResult = await debugConsentimientos(pacienteId);
        setConsentimientos(debugResult.success ? ordenarPorFecha(debugResult.forms || []) : []);
        if (debugResult.success) {
          setConsentimientosLoadedFor(pacienteId);
        } else {
          console.error('❌ Error cargando consentimientos:', debugResult.error);
        }
      } catch (error) {
        console.error('❌ Excepción cargando consentimientos:', error);
        setConsentimientos([]);
      } finally {
        setLoadingConsentimientos(false);
      }
    })();

    const cargaVacunas = (async () => {
      try {
        const r = await vacunasService.getVacunasByPaciente(patient.id);
        setVacunas(r.success ? (r.data || []) : []);
        if (!r.success) console.error('❌ Error cargando vacunas:', r.error);
      } catch (error) {
        console.error('❌ Error cargando vacunas:', error);
        setVacunas([]);
      } finally {
        setLoadingVacunas(false);
      }
    })();

    const cargaDesparasitaciones = (async () => {
      try {
        const r = await desparasitacionesService.getDesparasitacionesByPaciente(patient.id);
        setDesparasitaciones(r.success ? (r.data || []) : []);
        if (!r.success) console.error('❌ Error cargando desparasitaciones:', r.error);
      } catch (error) {
        console.error('❌ Error cargando desparasitaciones:', error);
        setDesparasitaciones([]);
      } finally {
        setLoadingDesparasitaciones(false);
      }
    })();

    // Aviso final cuando todo terminó (sin bloquear la aparición de cada sección)
    Promise.allSettled([
      cargaHistorias,
      cargaRecetas,
      cargaRemisiones,
      cargaConsentimientos,
      cargaVacunas,
      cargaDesparasitaciones
    ]).then(() => {
      setSnackbar({
        open: true,
        message: `Expediente de ${patient.nombre} cargado correctamente`,
        severity: 'success'
      });
    });
  };

  // Función para cerrar expediente
  const handleCloseExpediente = () => {
    setSelectedPatient(null);
    setExpedienteOpen(false);
    setExpedienteTabValue(0);
  };

  // Estado para edición de historia
  const [editingHistoria, setEditingHistoria] = useState(null);

  // Funciones para manejo de nueva historia clínica
  const handleOpenNuevaHistoria = () => {
    setNuevaHistoriaOpen(true);
    setEditingHistoria(null); // Asegurarse de que no estamos editando
    setHistoriaFormInitialData({
      ...EMPTY_HISTORIA_DATA,
      peso: selectedPatient?.peso || ''
    });
  };

  const handleCloseNuevaHistoria = () => {
    setNuevaHistoriaOpen(false);
    setSavingHistoria(false);
    setEditingHistoria(null);
  };

  // Función para editar una historia clínica
  const handleEditHistoria = (historia) => {
    console.log('✏️ Editando historia clínica:', historia);
    setEditingHistoria(historia);
    setHistoriaFormInitialData({ ...EMPTY_HISTORIA_DATA, ...historia });
    
    // Abrir el diálogo de nueva historia para editar
    setNuevaHistoriaOpen(true);
  };

  const handleExportHistoriaClinica = (historia) => {
    if (!selectedPatient || !historia) return;

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const marginX = 14;
      const headerHeight = 24;
      const columnGap = 6;
      const columnWidth = (pageWidth - marginX * 2 - columnGap) / 2;
      let yPos = 16;

      const ensureSpace = (heightNeeded) => {
        if (yPos + heightNeeded > pageHeight - 14) {
          doc.addPage();
          yPos = 16;
        }
      };

      const addHeader = () => {
        doc.setFillColor(250, 244, 248);
        doc.rect(0, 0, pageWidth, headerHeight, 'F');

        doc.setFontSize(14);
        doc.setTextColor(196, 100, 148);
        doc.text('BELKYS VETERINARIA', marginX + 28, 12);
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.text('Historia Clinica', marginX + 28, 18);

        try {
          const imgUrl = window.location.origin + '/logo192.png';
          doc.addImage(imgUrl, 'PNG', marginX, 4, 20, 20);
        } catch (imgError) {
          console.error('Error al cargar el logo:', imgError);
        }

        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text(`Fecha: ${formatearFecha(historia.fecha)}`, pageWidth - marginX, 12, { align: 'right' });
        doc.text(`Veterinario: ${historia.veterinario || 'N/A'}`, pageWidth - marginX, 18, { align: 'right' });
        yPos = headerHeight + 6;
      };

      const addSectionTitle = (title) => {
        ensureSpace(10);
        doc.setFontSize(11);
        doc.setTextColor(196, 100, 148);
        doc.text(title, marginX, yPos);
        yPos += 7;
        doc.setTextColor(0, 0, 0);
      };

      const addFieldColumn = (x, y, label, value, width) => {
        const formattedValue = value === 0 ? '0' : value;
        if (!formattedValue) return y;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(label, x, y);
        doc.setFont('helvetica', 'normal');

        const lines = doc.splitTextToSize(String(formattedValue), width);
        doc.text(lines, x, y + 4);
        return y + 4 + lines.length * 4 + 2;
      };

      const addTwoColumnBlock = (leftFields, rightFields) => {
        ensureSpace(24);
        let leftY = yPos;
        let rightY = yPos;

        leftFields.forEach(({ label, value }) => {
          leftY = addFieldColumn(marginX, leftY, label, value, columnWidth);
        });

        rightFields.forEach(({ label, value }) => {
          rightY = addFieldColumn(marginX + columnWidth + columnGap, rightY, label, value, columnWidth);
        });

        yPos = Math.max(leftY, rightY) + 2;
      };

      const addFullWidthField = (label, value) => {
        const formattedValue = value === 0 ? '0' : value;
        if (!formattedValue) return;

        ensureSpace(12);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(label, marginX, yPos);
        yPos += 4;
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(String(formattedValue), pageWidth - marginX * 2);
        doc.text(lines, marginX, yPos);
        yPos += lines.length * 4 + 2;
      };

      addHeader();

      addSectionTitle('Datos del Paciente');
      addTwoColumnBlock(
        [
          { label: 'Nombre', value: selectedPatient.nombre || '' },
          { label: 'Especie', value: selectedPatient.especie || '' },
          { label: 'Raza', value: selectedPatient.raza || '' },
          { label: 'Sexo', value: selectedPatient.sexo || '' }
        ],
        [
          { label: 'Fecha nacimiento', value: formatearFecha(selectedPatient.fechaNacimiento) },
          { label: 'Edad', value: calcularEdad(selectedPatient.fechaNacimiento) },
          { label: 'Peso', value: selectedPatient.peso ? `${selectedPatient.peso} kg` : '' },
          { label: 'Color', value: selectedPatient.color || '' }
        ]
      );
      yPos += 6;

      addSectionTitle('Datos del Propietario');
      addTwoColumnBlock(
        [
          { label: 'Nombre', value: `${selectedPatient.propietario || ''} ${selectedPatient.propietarioApellido || ''}`.trim() },
          { label: 'Cedula', value: selectedPatient.propietarioCedula || '' }
        ],
        [
          { label: 'Telefono', value: selectedPatient.propietarioTelefono || '' },
          { label: 'Direccion', value: `${selectedPatient.propietarioDireccion || ''} ${selectedPatient.propietarioCiudad || ''}`.trim() }
        ]
      );
      addFullWidthField('Email', selectedPatient.propietarioEmail || '');
      yPos += 6;

      addSectionTitle('Resumen Clinico');
      addFullWidthField('Motivo de consulta', historia.motivoConsulta || historia.motivo || '');
      addFullWidthField('Anamnesis', historia.anamnesis || '');
      yPos += 6;

      // Antecedentes
      addSectionTitle('Antecedentes');
      addTwoColumnBlock(
        [
          { label: 'Enfermedades Previas', value: historia.enfermedadesPrevias || '' },
          { label: 'Cirugias Previas', value: historia.cirugiasPrevias || '' },
          { label: 'Esterilizado', value: historia.esterilizado || '' }
        ],
        [
          { label: 'Dieta', value: historia.dieta || '' },
          { label: 'Esquema Vacunal', value: historia.esquemaVacunal || '' },
          { label: 'Numero de Partos', value: historia.numeroPartos || '' }
        ]
      );
      addFullWidthField('Plan de Desparasitacion', historia.planDesparasitacion || '');
      addFullWidthField('Tratamientos Recientes', historia.tratamientosRecientes || '');
      yPos += 4;

      // Informacion Social
      addSectionTitle('Informacion Social');
      addTwoColumnBlock(
        [
          { label: 'Viajes Recientes', value: historia.viajesRecientes || '' },
          { label: 'Vive con otras mascotas', value: historia.viveMascotas || '' }
        ],
        [
          { label: 'Comportamiento Animal', value: historia.comportamientoAnimal || '' }
        ]
      );
      yPos += 6;

      addSectionTitle('Signos Vitales');
      addTwoColumnBlock(
        [
          { label: 'Temperatura', value: historia.temperatura || '' },
          { label: 'Frecuencia cardiaca', value: historia.frecuenciaCardiaca || '' },
          { label: 'Frecuencia respiratoria', value: historia.frecuenciaRespiratoria || '' }
        ],
        [
          { label: 'Mucosas', value: historia.mucosas || '' },
          { label: 'TLLC', value: historia.tllc || '' },
          { label: 'TRPC (seg)', value: historia.trpc || '' },
          { label: 'Deshidratacion', value: historia.deshidratacion || '' }
        ]
      );
      yPos += 6;
      // Evaluacion por Sistemas - mostrar tabla/listado si hay datos
      addSectionTitle('Evaluacion por Sistemas');
      const sistemasList = [
        { key: 'tegumentario', label: 'Tegumentario' },
        { key: 'digestivo', label: 'Digestivo' },
        { key: 'respiratorio', label: 'Respiratorio' },
        { key: 'musculoesqueletico', label: 'Musculoesqueletico' },
        { key: 'nervioso', label: 'Nervioso' },
        { key: 'urinario', label: 'Urinario' },
        { key: 'reproductivo', label: 'Reproductivo' },
        { key: 'ganglios', label: 'Ganglios' },
        { key: 'palpacion', label: 'Palpacion Abdominal' },
        { key: 'nariz', label: 'Nariz' },
        { key: 'ojos', label: 'Ojos' },
        { key: 'oidos', label: 'Oidos' },
        { key: 'cavidadOral', label: 'Cavidad Oral' }
      ];

      sistemasList.forEach((s) => {
        const normal = historia[`${s.key}Normal`];
        const anormal = historia[`${s.key}Anormal`];
        const evaluacion = historia[`${s.key}Evaluacion`] || '';
        const status = normal ? 'Normal' : (anormal ? 'Anormal' : 'No evaluado');
        if (normal || anormal || evaluacion) {
          addFullWidthField(`${s.label} - Estado`, `${status}${evaluacion ? ' - ' + evaluacion : ''}`);
        }
      });

      yPos += 6;

      addSectionTitle('Diagnostico y Tratamiento');
      addFullWidthField('Examenes complementarios', historia.ayudasDiagnosticas || '');
      addFullWidthField('Diagnostico', historia.diagnostico || '');
      addFullWidthField('Tratamiento en casa', historia.tratamientoEnCasa || '');
      addFullWidthField('Formula medica', historia.formulaMedica || '');
      addFullWidthField('Pronostico', historia.pronostico || '');
      addFullWidthField('Observaciones', historia.observaciones || '');

      const fechaArchivo = formatearFecha(historia.fecha);
      const fechaArchivoSafe = fechaArchivo !== 'N/A' ? fechaArchivo : new Date().toLocaleDateString('es-ES');
      const nombreArchivo = `Historia_Clinica_${selectedPatient.nombre || 'Paciente'}_${fechaArchivoSafe.replace(/\//g, '-')}.pdf`;
      doc.save(nombreArchivo);

      setSnackbar({
        open: true,
        message: 'Historia clinica exportada correctamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error generando PDF de historia clinica:', error);
      setSnackbar({
        open: true,
        message: 'Error generando el PDF de historia clinica',
        severity: 'error'
      });
    }
  };
  
  // Función para eliminar una historia clínica
  const handleDeleteHistoria = async (historiaId) => {
    // Confirmar antes de eliminar
    if (window.confirm('¿Estás seguro de que deseas eliminar esta historia clínica? Esta acción no se puede deshacer.')) {
      try {
        console.log('🗑️ Eliminando historia clínica:', historiaId);
        setLoadingHistorias(true);
        
        const result = await historiaClinicaService.deleteHistoriaClinica(historiaId);
        
        if (result.success) {
          console.log('✅ Historia clínica eliminada correctamente');
          
          // Actualizar la lista de historias clínicas
          setHistoriaClinica(prev => prev.filter(h => h.id !== historiaId));
          
          setSnackbar({
            open: true,
            message: 'Historia clínica eliminada exitosamente',
            severity: 'success'
          });
        } else {
          console.error('❌ Error eliminando historia clínica:', result.error);
          
          setSnackbar({
            open: true,
            message: `Error al eliminar: ${result.error}`,
            severity: 'error'
          });
        }
      } catch (error) {
        console.error('❌ Excepción al eliminar historia clínica:', error);
        
        setSnackbar({
          open: true,
          message: `Error inesperado: ${error.message}`,
          severity: 'error'
        });
      } finally {
        setLoadingHistorias(false);
      }
    }
  };

  const handleSaveHistoriaClinica = async (historiaData) => {
    // Validaciones básicas
    if (!historiaData.motivoConsulta || !historiaData.anamnesis || !historiaData.veterinario) {
      setSnackbar({
        open: true,
        message: 'Por favor completa al menos el motivo de consulta, anamnesis y veterinario',
        severity: 'error'
      });
      return;
    }

    console.log('💾 GUARDAR HISTORIA: Iniciando proceso de guardado');
    console.log('👤 Paciente seleccionado:', selectedPatient);
    console.log('📋 ID del paciente:', selectedPatient?.id);
    console.log('📝 Datos de la historia:', historiaData);
    console.log('✏️ Modo edición:', editingHistoria ? 'SI' : 'NO');

    if (!selectedPatient || !selectedPatient.id) {
      console.error('❌ Error: No hay paciente seleccionado o ID inválido');
      setSnackbar({
        open: true,
        message: 'Error: No se pudo identificar el paciente',
        severity: 'error'
      });
      return;
    }

    setSavingHistoria(true);

    try {
      let result;
      
      if (editingHistoria) {
        // Actualización de historia existente
        const historiaActualizada = {
          ...historiaData,
          pacienteId: selectedPatient.id,
          pacienteNombre: selectedPatient.nombre,
          // Mantener la fecha original de la historia
          fecha: editingHistoria.fecha || new Date().toISOString().split('T')[0]
        };
        
        console.log('✏️ Actualizando historia existente:', editingHistoria.id);
        console.log('📝 Datos a actualizar:', historiaActualizada);
        
        result = await historiaClinicaService.updateHistoriaClinica(editingHistoria.id, historiaActualizada);
        console.log('💾 Resultado de la actualización:', result);
      } else {
        // Creación de nueva historia
        const nuevaHistoria = {
          ...historiaData,
          pacienteId: selectedPatient.id,
          pacienteNombre: selectedPatient.nombre,
          fecha: new Date().toISOString().split('T')[0]
        };

        console.log('💾 Datos finales a guardar:', nuevaHistoria);
        console.log('🔑 pacienteId específico:', nuevaHistoria.pacienteId);
        
        result = await historiaClinicaService.createHistoriaClinica(nuevaHistoria);
        console.log('💾 Resultado del guardado:', result);
      }

      if (result.success) {
        console.log('✅ Historia guardada exitosamente, recargando historias...');
        
        // Actualizar la fecha de última visita del paciente
        await updatePatientLastVisit(selectedPatient.id);
        
        // Recargar historias inmediatamente después del guardado
        const historiasResult = await historiaClinicaService.getHistoriasByPaciente(selectedPatient.id);
        console.log('🔄 Resultado de recarga inmediata:', historiasResult);
        
        if (historiasResult.success) {
          setHistoriaClinica(historiasResult.data);
          console.log('✅ Historias actualizadas en el estado:', historiasResult.data.length);
        }

        setSnackbar({
          open: true,
          message: `Historia clínica ${editingHistoria ? 'actualizada' : 'guardada'} exitosamente en Firebase`,
          severity: 'success'
        });

        handleCloseNuevaHistoria();
      } else {
        throw new Error(result.error);
      }

    } catch (error) {
      console.error('❌ Error guardando historia clínica:', error);
      setSnackbar({
        open: true,
        message: `Error al ${editingHistoria ? 'actualizar' : 'guardar'} la historia clínica: ` + error.message,
        severity: 'error'
      });
    } finally {
      setSavingHistoria(false);
    }
  };

  // Estado para edición de recetas
  const [editingReceta, setEditingReceta] = useState(null);

  // Funciones para manejo de recetas
  const handleOpenNuevaReceta = () => {
    console.log('🆕 NUEVA RECETA: Abriendo formulario para paciente:', selectedPatient?.nombre);
    setEditingReceta(null); // Asegurarse de que no estamos editando
    setRecetaFormInitialData(EMPTY_RECETA_DATA);
    setNuevaRecetaOpen(true);
  };

  const handleCloseNuevaReceta = () => {
    setNuevaRecetaOpen(false);
    setEditingReceta(null);
    setRecetaFormInitialData(EMPTY_RECETA_DATA);
  };
  
  // Función para editar una receta existente
  const handleEditReceta = (receta) => {
    console.log('✏️ Editando receta:', receta);
    setEditingReceta(receta);
    
    // Verificar si la receta ya tiene un array de medicamentos o está en formato antiguo
    let medicamentos = [];
    
    if (receta.medicamentos && Array.isArray(receta.medicamentos)) {
      // Nueva estructura con array de medicamentos
      medicamentos = [...receta.medicamentos];
    } else {
      // Estructura antigua con un solo medicamento
      medicamentos = [{
        medicamento: receta.medicamento || '',
        dosis: receta.dosis || '',
        observacion: '', // Campo nuevo, vacío para recetas antiguas
      }];
    }
    
    // Llenar el formulario con los datos existentes
    setRecetaFormInitialData({
      medicamentos,
      observaciones: receta.observaciones || '',
      veterinario: receta.veterinario || '',
      tarjetaProfesional: receta.tarjetaProfesional || ''
    });
    
    // Abrir el diálogo de nueva receta para editar
    setNuevaRecetaOpen(true);
  };
  
  // Función para eliminar una receta
  const handleDeleteReceta = (recetaId) => {
    setConfirmDialog({
      open: true,
      title: 'Eliminar receta',
      message: '¿Estás seguro de que deseas eliminar esta receta? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar receta',
      loading: false,
      onConfirm: async () => {
        try {
          console.log('🗑️ Eliminando receta:', recetaId);

          const result = await recetariosService.deleteReceta(recetaId);

          if (result.success) {
            console.log('✅ Receta eliminada correctamente');

            // Actualizar la lista de recetas
            setRecetas(prev => prev.filter(r => r.id !== recetaId));

            setSnackbar({
              open: true,
              message: 'Receta eliminada exitosamente',
              severity: 'success'
            });
          } else {
            console.error('❌ Error eliminando receta:', result.error);

            setSnackbar({
              open: true,
              message: `Error al eliminar: ${result.error}`,
              severity: 'error'
            });
          }
        } catch (error) {
          console.error('❌ Excepción al eliminar receta:', error);

          setSnackbar({
            open: true,
            message: `Error inesperado: ${error.message}`,
            severity: 'error'
          });
        }
      }
    });
  };
  
  // Función para manejar la impresión de una receta
  const handleImprimirReceta = (receta) => {
    // Guardamos la receta seleccionada en el estado
    setRecetaParaImprimir(receta);
    // Abrimos el modal de vista previa
    setImprimirModalOpen(true);
  };
  
  // Función para generar e imprimir el PDF
  const generarPDFReceta = () => {
    if (!recetaParaImprimir || !selectedPatient) return;
    
    // Crear un nuevo documento PDF
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    try {
      // Obtener la fecha actual para el recetario
      const fechaActual = new Date();
      const fechaFormateada = `${fechaActual.getDate()}/${fechaActual.getMonth() + 1}/${fechaActual.getFullYear()}`;
      
      // Configuración de fuentes y estilos
      doc.setFont("helvetica", "normal");
      
      // Logo y encabezado
      doc.setFontSize(18);
      doc.setTextColor(196, 100, 148); // Color rosado similar al usado en la app
      
      // Texto centrado para el nombre de la clínica
      doc.text("BELKYSVETERINARIA", pageWidth / 2, 20, { align: "center" });
      
      // Información del veterinario
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      // Usar el veterinario guardado en la receta o correo como respaldo
      doc.text(`Dr(a). ${recetaParaImprimir.veterinario || auth.currentUser?.email || ""}`, 20, 30);
      // Añadir el número de tarjeta profesional debajo del doctor
      doc.setFontSize(10);
      doc.text(`M.P.: ${recetaParaImprimir.tarjetaProfesional || ""}`, 20, 36);
      
      // Título
      doc.setFontSize(16);
      doc.setTextColor(196, 100, 148);
      doc.text("FÓRMULA MÉDICA", pageWidth / 2, 46, { align: "center" });
      
      // Información del propietario y paciente
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(`Propietario: ${selectedPatient.propietario || ""}`, 20, 56);
      doc.text(`Fecha: ${fechaFormateada}`, pageWidth - 60, 56);
      
      doc.text(`Paciente: ${selectedPatient.nombre || ""}`, 20, 66);
      doc.text(`Peso: ${selectedPatient.peso || ""} Kg`, pageWidth - 60, 66);
      
      // Línea divisoria
      doc.line(20, 71, pageWidth - 20, 71);
      
      // Medicamentos
      doc.setFontSize(14);
      doc.setTextColor(196, 100, 148);
      doc.text("MEDICAMENTOS", 20, 81);
      
      // Función para convertir números a romanos
      const toRoman = (num) => {
        const romanNumerals = [
          { value: 10, symbol: 'X' },
          { value: 9, symbol: 'IX' },
          { value: 5, symbol: 'V' },
          { value: 4, symbol: 'IV' },
          { value: 1, symbol: 'I' }
        ];
        let roman = '';
        let number = num;
        
        romanNumerals.forEach(numeral => {
          while (number >= numeral.value) {
            roman += numeral.symbol;
            number -= numeral.value;
          }
        });
        
        return roman;
      };
      
      // Verificar si es el formato nuevo o antiguo
      if (recetaParaImprimir.medicamentos && Array.isArray(recetaParaImprimir.medicamentos)) {
        // Formato nuevo con múltiples medicamentos en formato actualizado
        let yPos = 91;
        
        recetaParaImprimir.medicamentos.forEach((med, index) => {
          doc.setFontSize(11);
          doc.setTextColor(0, 0, 0);
          
          // Línea 1: Número romano y nombre del medicamento
          const romanoIndex = toRoman(index + 1);
          let medicamentoInfo = `${romanoIndex}. ${med.medicamento || ""}`;
          
          // Calculamos posición para mostrar la cantidad a la derecha
          // Texto del medicamento
          doc.text(medicamentoInfo, 20, yPos);
          
          // Cantidad a la derecha (cantidad)
          doc.text(`#${med.dosis || ""}`, pageWidth - 40, yPos);
          
          yPos += 7; // Espacio para la siguiente línea
          
          // Ya no mostramos frecuencia y duración
          // Saltamos directamente a la observación
          
          // Línea 3: Observación en una línea separada y con sangría
          if (med.observacion) {
            doc.setFontSize(9);
            // Dividir la observación si es muy larga
            const splitObservacion = doc.splitTextToSize(`${med.observacion}`, pageWidth - 60);
            doc.text(splitObservacion, 30, yPos);
            yPos += splitObservacion.length * 5 + 2; // Ajustar según el número de líneas
          }
          
          yPos += 5; // Espacio adicional entre medicamentos
        });
        
        // Observaciones
        if (recetaParaImprimir.observaciones) {
          doc.setFontSize(12);
          doc.setTextColor(196, 100, 148);
          doc.text("RECOMENDACIONES:", 20, yPos + 5);
          
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          
          // Maneja observaciones largas con múltiples líneas
          const splitObservaciones = doc.splitTextToSize(recetaParaImprimir.observaciones, pageWidth - 40);
          doc.text(splitObservaciones, 20, yPos + 15);
        }
      } else {
        // Formato antiguo con un solo medicamento (actualizado al nuevo formato con número romano)
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        
        // Línea 1: Número romano y nombre del medicamento
        let medicamentoInfo = `I. ${recetaParaImprimir.medicamento || ""}`;
        doc.text(medicamentoInfo, 20, 91);
        
        // Cantidad a la derecha (cantidad)
        doc.text(`#${recetaParaImprimir.dosis || ""}`, pageWidth - 40, 91);
        
        // Ya no mostramos frecuencia y duración
        let yPos = 98;
        
        // Línea 3: Observación si existe
        if (recetaParaImprimir.observacion) {
          doc.setFontSize(9);
          const splitObservacion = doc.splitTextToSize(`${recetaParaImprimir.observacion || ""}`, pageWidth - 60);
          doc.text(splitObservacion, 30, yPos);
        }
        
        // Observaciones
        if (recetaParaImprimir.observaciones) {
          doc.setFontSize(12);
          doc.setTextColor(196, 100, 148);
          doc.text("RECOMENDACIONES:", 20, 115);
          
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          
          const splitObservaciones = doc.splitTextToSize(recetaParaImprimir.observaciones, pageWidth - 40);
          doc.text(splitObservaciones, 20, 125);
        }
      }
      
      // Pie de página
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      const pieY = doc.internal.pageSize.height - 10;
      doc.text("Contacto: +57 321 610 3829 • belkys.veterinaria@gmail.com", pageWidth / 2, pieY, { align: "center" });
      
      // Intenta agregar la imagen del logo
      try {
        const imgUrl = window.location.origin + '/logo192.png';
        doc.addImage(imgUrl, 'PNG', pageWidth - 30, 10, 20, 20);
      } catch (imgError) {
        console.error("Error al cargar el logo:", imgError);
      }
      
      // Guardar o imprimir el PDF
      doc.save(`Receta_${selectedPatient.nombre}_${fechaFormateada.replace(/\//g, '-')}.pdf`);
      
      // Cerrar el modal después de imprimir
      setImprimirModalOpen(false);
      
      setSnackbar({
        open: true,
        message: 'Receta generada correctamente',
        severity: 'success'
      });
    } catch (error) {
      console.error("Error al generar el PDF:", error);
      setSnackbar({
        open: true,
        message: 'Error al generar la receta',
        severity: 'error'
      });
    }
  };

  const handleSaveReceta = async (recetaFormData) => {
    console.log('💊 GUARDAR RECETA: Verificando datos...');
    
    try {
      const recetaDataActual = cloneRecetaData(recetaFormData);

      // Realizar un log detallado para ver el estado actual
      console.log('👤 Paciente seleccionado:', selectedPatient);
      console.log('💊 Datos actuales de la receta:', recetaDataActual);
      console.log('✏️ Modo edición:', editingReceta ? 'SI' : 'NO');
      
      // Verificar explícitamente cada campo requerido
      let errores = [];
      
      if (!recetaDataActual.veterinario || recetaDataActual.veterinario.trim() === '') {
        errores.push('nombre del veterinario');
      }
      
      if (!recetaDataActual.tarjetaProfesional || recetaDataActual.tarjetaProfesional.trim() === '') {
        errores.push('número de tarjeta profesional');
      }
      
      if (recetaDataActual.medicamentos.length === 0) {
        errores.push('al menos un medicamento');
      } else {
        // Verificar cada medicamento
        recetaDataActual.medicamentos.forEach((med, index) => {
          if (!med.medicamento || med.medicamento.trim() === '') {
            errores.push(`nombre del medicamento ${index + 1}`);
          }
          if (!med.dosis || med.dosis.trim() === '') {
            errores.push(`cantidad del medicamento ${index + 1}`);
          }
        });
      }
      
      // Si hay errores, mostrarlos y detener
      if (errores.length > 0) {
        console.log('❌ Errores encontrados:', errores);
        setSnackbar({
          open: true,
          message: `Por favor completa los siguientes campos: ${errores.join(', ')}`,
          severity: 'error'
        });
        return;
      }
      
      console.log('✅ Validación exitosa, procesando datos para guardado...');
      
      // Preparar datos finales asegurando que los campos de observación existan
      const medicamentosFinales = recetaDataActual.medicamentos.map(med => ({
        ...med,
        medicamento: med.medicamento?.trim(),
        dosis: med.dosis?.trim(),
        observacion: med.observacion?.trim() || ''
      }));
      
      // Datos finales a guardar
      const datosFinales = {
        ...recetaDataActual,
        veterinario: recetaDataActual.veterinario.trim(),
        observaciones: recetaDataActual.observaciones?.trim() || '',
        medicamentos: medicamentosFinales
      };
      
      let result;
      
      if (editingReceta) {
        // Estamos editando una receta existente
        const recetaActualizada = {
          medicamentos: [...datosFinales.medicamentos],
          observaciones: datosFinales.observaciones,
          veterinario: datosFinales.veterinario,
          tarjetaProfesional: datosFinales.tarjetaProfesional,
          // Mantener los campos que no se editan
          pacienteId: editingReceta.pacienteId,
          pacienteNombre: editingReceta.pacienteNombre,
          fecha: editingReceta.fecha,
          estado: editingReceta.estado || 'activa'
        };
        
        console.log('✏️ Actualizando receta existente:', editingReceta.id);
        console.log('💊 Datos actualizados:', recetaActualizada);
        
        // Verificar si existe el método updateReceta en el servicio
        if (typeof recetariosService.updateReceta === 'function') {
          result = await recetariosService.updateReceta(editingReceta.id, recetaActualizada);
        } else {
          // Si no existe, podemos implementar una alternativa usando deleteReceta y createReceta
          console.log('⚠️ Método updateReceta no disponible, usando alternativa...');
          
          // 1. Eliminar la receta antigua
          const deleteResult = await recetariosService.deleteReceta(editingReceta.id);
          if (!deleteResult.success) {
            throw new Error(`Error eliminando receta antigua: ${deleteResult.error}`);
          }
          
          // 2. Crear una nueva con los datos actualizados
          const createData = {
            ...recetaActualizada,
            fecha: new Date(),
            veterinario: datosFinales.veterinario,
            tarjetaProfesional: datosFinales.tarjetaProfesional,
          };
          
          result = await recetariosService.createReceta(createData);
        }
      } else {
        // Estamos creando una nueva receta
        const recetaData = {
          pacienteId: selectedPatient.id,
          pacienteNombre: selectedPatient.nombre,
          fecha: new Date(),
          veterinario: datosFinales.veterinario,
          tarjetaProfesional: datosFinales.tarjetaProfesional,
          estado: 'activa',
          medicamentos: [...datosFinales.medicamentos],
          observaciones: datosFinales.observaciones
        };

        result = await recetariosService.createReceta(recetaData);
      }
      
      if (result.success) {
        console.log('✅ Receta guardada exitosamente:', result.id);
        
        // Actualizar la fecha de última visita del paciente
        await updatePatientLastVisit(selectedPatient.id);
        
        if (!editingReceta) {
          // Si es nueva receta, añadirla al estado
          const nuevaReceta = {
            id: result.id,
            medicamentos: [...datosFinales.medicamentos],
            observaciones: datosFinales.observaciones,
            pacienteId: selectedPatient.id,
            pacienteNombre: selectedPatient.nombre,
            fecha: new Date(),
            veterinario: datosFinales.veterinario,
            tarjetaProfesional: datosFinales.tarjetaProfesional,
            estado: 'activa'
          };
          
          setRecetas(prev => [nuevaReceta, ...prev]);
        } else {
          // Si estamos editando, actualizar la receta en el estado
          setRecetas(prev => prev.map(r => 
            r.id === editingReceta.id 
              ? { 
                  ...r, 
                  ...datosFinales,
                  fechaActualizacion: new Date() 
                } 
              : r
          ));
        }
        
        setSnackbar({
          open: true,
          message: editingReceta ? 'Receta actualizada exitosamente' : 'Receta guardada exitosamente',
          severity: 'success'
        });

        handleCloseNuevaReceta();
      } else {
        throw new Error(result.error);
      }

    } catch (error) {
      console.error('❌ Error guardando receta:', error);
      setSnackbar({
        open: true,
        message: `Error al ${editingReceta ? 'actualizar' : 'guardar'} la receta: ` + error.message,
        severity: 'error'
      });
    }
  };

  // ===== Funciones para manejo de remisiones clínicas =====
  const handleOpenNuevaRemision = () => {
    setEditingRemision(null);
    setRemisionFormInitialData(EMPTY_REMISION_DATA);
    setNuevaRemisionOpen(true);
  };

  const handleCloseNuevaRemision = () => {
    setNuevaRemisionOpen(false);
    setEditingRemision(null);
    setRemisionFormInitialData(EMPTY_REMISION_DATA);
  };

  const handleEditRemision = (remision) => {
    setEditingRemision(remision);
    setRemisionFormInitialData({
      motivoRemision: remision.motivoRemision || '',
      clinicaRemision: remision.clinicaRemision || '',
      veterinario: remision.veterinario || '',
      tarjetaProfesional: remision.tarjetaProfesional || ''
    });
    setNuevaRemisionOpen(true);
  };

  const handleDeleteRemision = (remisionId) => {
    setConfirmDialog({
      open: true,
      title: 'Eliminar remisión',
      message: '¿Estás seguro de que deseas eliminar esta remisión clínica? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar remisión',
      loading: false,
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, loading: true }));
          const result = await remisionesService.deleteRemision(remisionId);

          if (result.success) {
            setRemisiones(prev => prev.filter(r => r.id !== remisionId));
            setSnackbar({
              open: true,
              message: 'Remisión eliminada exitosamente',
              severity: 'success'
            });
          } else {
            throw new Error(result.error);
          }
        } catch (error) {
          console.error('❌ Excepción al eliminar remisión:', error);
          setSnackbar({
            open: true,
            message: 'Error al eliminar la remisión: ' + error.message,
            severity: 'error'
          });
        } finally {
          setConfirmDialog(prev => ({ ...prev, open: false, loading: false }));
        }
      }
    });
  };

  const handleImprimirRemision = (remision) => {
    setRemisionParaImprimir(remision);
    setImprimirRemisionModalOpen(true);
  };

  const handleSaveRemision = async (remisionFormData) => {
    if (savingRemision) return; // Evitar guardados duplicados por doble clic

    try {
      const datos = cloneRemisionData(remisionFormData);

      const errores = [];
      if (!datos.motivoRemision || datos.motivoRemision.trim() === '') {
        errores.push('motivo de la remisión');
      }
      if (!datos.clinicaRemision || datos.clinicaRemision.trim() === '') {
        errores.push('clínica a la que se remite');
      }
      if (!datos.veterinario || datos.veterinario.trim() === '') {
        errores.push('nombre del veterinario');
      }
      if (!datos.tarjetaProfesional || datos.tarjetaProfesional.trim() === '') {
        errores.push('número de tarjeta profesional');
      }

      if (errores.length > 0) {
        setSnackbar({
          open: true,
          message: `Por favor completa los siguientes campos: ${errores.join(', ')}`,
          severity: 'error'
        });
        return;
      }

      setSavingRemision(true);

      const datosFinales = {
        motivoRemision: datos.motivoRemision.trim(),
        clinicaRemision: datos.clinicaRemision.trim(),
        veterinario: datos.veterinario.trim(),
        tarjetaProfesional: datos.tarjetaProfesional.trim()
      };

      let result;

      if (editingRemision) {
        const remisionActualizada = {
          ...datosFinales,
          pacienteId: editingRemision.pacienteId,
          pacienteNombre: editingRemision.pacienteNombre,
          fecha: editingRemision.fecha,
          estado: editingRemision.estado || 'activa'
        };
        result = await remisionesService.updateRemision(editingRemision.id, remisionActualizada);
      } else {
        const remisionData = {
          ...datosFinales,
          pacienteId: selectedPatient.id,
          pacienteNombre: selectedPatient.nombre,
          fecha: new Date(),
          estado: 'activa'
        };
        result = await remisionesService.createRemision(remisionData);
      }

      if (result.success) {
        await updatePatientLastVisit(selectedPatient.id);

        if (!editingRemision) {
          const nuevaRemision = {
            id: result.id,
            ...datosFinales,
            pacienteId: selectedPatient.id,
            pacienteNombre: selectedPatient.nombre,
            fecha: new Date(),
            estado: 'activa'
          };
          setRemisiones(prev => [nuevaRemision, ...prev]);
        } else {
          setRemisiones(prev => prev.map(r =>
            r.id === editingRemision.id
              ? { ...r, ...datosFinales, fechaActualizacion: new Date() }
              : r
          ));
        }

        setSnackbar({
          open: true,
          message: editingRemision ? 'Remisión actualizada exitosamente' : 'Remisión guardada exitosamente',
          severity: 'success'
        });

        handleCloseNuevaRemision();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error guardando remisión:', error);
      setSnackbar({
        open: true,
        message: `Error al ${editingRemision ? 'actualizar' : 'guardar'} la remisión: ` + error.message,
        severity: 'error'
      });
    } finally {
      setSavingRemision(false);
    }
  };

  const generarPDFRemision = () => {
    if (!remisionParaImprimir || !selectedPatient) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;

    try {
      const fechaActual = new Date();
      const fechaFormateada = `${fechaActual.getDate()}/${fechaActual.getMonth() + 1}/${fechaActual.getFullYear()}`;

      doc.setFont("helvetica", "normal");

      // Encabezado
      doc.setFontSize(18);
      doc.setTextColor(196, 100, 148);
      doc.text("BELKYSVETERINARIA", pageWidth / 2, 20, { align: "center" });

      // Información del veterinario
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Dr(a). ${remisionParaImprimir.veterinario || auth.currentUser?.email || ""}`, 20, 30);
      doc.setFontSize(10);
      doc.text(`M.P.: ${remisionParaImprimir.tarjetaProfesional || ""}`, 20, 36);

      // Título
      doc.setFontSize(16);
      doc.setTextColor(196, 100, 148);
      doc.text("REMISIÓN CLÍNICA", pageWidth / 2, 46, { align: "center" });

      // Datos de propietario / paciente
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      const nombreCompletoPropietario = `${selectedPatient.propietario || ""} ${selectedPatient.propietarioApellido || ""}`.trim();
      doc.text(`Propietario: ${nombreCompletoPropietario}`, 20, 56);
      doc.text(`Fecha: ${fechaFormateada}`, pageWidth - 60, 56);

      doc.text(`Paciente: ${selectedPatient.nombre || ""}`, 20, 64);
      doc.text(`Especie: ${selectedPatient.especie || ""}`, pageWidth - 60, 64);

      doc.text(`Raza: ${selectedPatient.raza || ""}`, 20, 72);
      doc.text(`Sexo: ${selectedPatient.sexo || ""}`, pageWidth - 60, 72);

      doc.text(`Edad: ${calcularEdad(selectedPatient.fechaNacimiento)}`, 20, 80);
      doc.text(`Color: ${selectedPatient.color || ""}`, pageWidth - 60, 80);

      // Línea divisoria
      doc.line(20, 85, pageWidth - 20, 85);

      // Clínica de destino
      doc.setFontSize(14);
      doc.setTextColor(196, 100, 148);
      doc.text("SE REMITE A:", 20, 95);
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      const splitClinica = doc.splitTextToSize(remisionParaImprimir.clinicaRemision || "", pageWidth - 40);
      doc.text(splitClinica, 20, 103);

      let yPos = 103 + splitClinica.length * 6 + 6;

      // Motivo de remisión
      doc.setFontSize(14);
      doc.setTextColor(196, 100, 148);
      doc.text("MOTIVO DE LA REMISIÓN:", 20, yPos);
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      const splitMotivo = doc.splitTextToSize(remisionParaImprimir.motivoRemision || "", pageWidth - 40);
      doc.text(splitMotivo, 20, yPos + 8);

      // Pie de página
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      const pieY = doc.internal.pageSize.height - 10;
      doc.text("Contacto: +57 321 610 3829 • belkys.veterinaria@gmail.com", pageWidth / 2, pieY, { align: "center" });

      // Logo
      try {
        const imgUrl = window.location.origin + '/logo192.png';
        doc.addImage(imgUrl, 'PNG', pageWidth - 30, 10, 20, 20);
      } catch (imgError) {
        console.error("Error al cargar el logo:", imgError);
      }

      doc.save(`Remision_${selectedPatient.nombre}_${fechaFormateada.replace(/\//g, '-')}.pdf`);

      setImprimirRemisionModalOpen(false);
      setSnackbar({
        open: true,
        message: 'Remisión generada correctamente',
        severity: 'success'
      });
    } catch (error) {
      console.error("Error al generar el PDF de remisión:", error);
      setSnackbar({
        open: true,
        message: 'Error al generar la remisión',
        severity: 'error'
      });
    }
  };

  // Funciones para manejo de consentimientos
  const handleOpenNuevoConsentimiento = () => {
    // Reseteamos el estado del consentimiento
    setConsentimientoFormInitialData({
      nombreVeterinario: '',
      mpVeterinario: '',
      correoVeterinario: auth.currentUser?.email || '',
      firmaPropietario: null,
      tipoConsentimiento: 'Consentimiento de Servicios a Domicilio',
    });
    setEditingConsentimiento(null);
    setNuevoConsentimientoOpen(true);
  };

  const handleCloseNuevoConsentimiento = () => {
    setNuevoConsentimientoOpen(false);
    // Resetear los datos del consentimiento
    setConsentimientoFormInitialData({
      nombreVeterinario: '',
      mpVeterinario: '',
      correoVeterinario: auth.currentUser?.email || '',
      firmaPropietario: null,
      tipoConsentimiento: 'Consentimiento de Servicios a Domicilio',
    });
    setEditingConsentimiento(null);
  };

  const handleEditConsentimiento = (consentimiento) => {
    console.log('✏️ Editando consentimiento:', consentimiento);
    setEditingConsentimiento(consentimiento);
    
    // Cargar datos del consentimiento en el formulario
    setConsentimientoFormInitialData({
      nombreVeterinario: consentimiento.nombreVeterinario || '',
      mpVeterinario: consentimiento.mpVeterinario || '',
      correoVeterinario: consentimiento.correoVeterinario || auth.currentUser?.email || '',
      firmaPropietario: consentimiento.firmaPropietario || null,
      tipoConsentimiento: consentimiento.tipoConsentimiento || 'Consentimiento de Servicios a Domicilio',
    });
    
    setNuevoConsentimientoOpen(true);
  };

  const handleDeleteConsentimiento = (consentimientoId) => {
    setConfirmDialog({
      open: true,
      title: 'Eliminar consentimiento',
      message: '¿Estás seguro de que deseas eliminar este consentimiento? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar consentimiento',
      loading: false,
      onConfirm: async () => {
        try {
          console.log('🗑️ Eliminando consentimiento:', consentimientoId);

          const result = await consentimientosService.deleteConsentimiento(consentimientoId);

          if (result.success) {
            console.log('✅ Consentimiento eliminado correctamente');

            // Actualizar la lista de consentimientos
            setConsentimientos(prev => prev.filter(c => c.id !== consentimientoId));

            setSnackbar({
              open: true,
              message: 'Consentimiento eliminado exitosamente',
              severity: 'success'
            });
          } else {
            console.error('❌ Error eliminando consentimiento:', result.error);

            setSnackbar({
              open: true,
              message: `Error al eliminar: ${result.error}`,
              severity: 'error'
            });
          }
        } catch (error) {
          console.error('❌ Excepción al eliminar consentimiento:', error);

          setSnackbar({
            open: true,
            message: `Error inesperado: ${error.message}`,
            severity: 'error'
          });
        }
      }
    });
  };

  const handleSaveConsentimiento = async (formData) => {
    try {
      console.log('💾 Guardando consentimiento...');
      
      // Validar campos requeridos
      // Verificar ID de paciente
      if (!selectedPatient || !selectedPatient.id) {
        console.error('❌ Error: No hay un paciente seleccionado con ID válido');
        throw new Error('No hay un paciente seleccionado con ID válido');
      }
      
      // Mascotas que cubre este consentimiento: SIEMPRE el principal (selectedPatient)
      // más las seleccionadas del mismo propietario, deduplicadas por id.
      const pacientesPorId = new Map();
      [selectedPatient, ...(formData.pacientes || [])].forEach((p) => {
        if (p && p.id != null) {
          pacientesPorId.set(String(p.id), p);
        }
      });
      const pacientesConsentimiento = Array.from(pacientesPorId.values());

      // Datos comunes a todas las mascotas del grupo (firma única, misma fecha y grupo compartido)
      const fechaConsentimiento = new Date();
      const grupoId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `grp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const pacientesGrupo = pacientesConsentimiento.map((p) => ({
        id: String(p.id),
        nombre: p.nombre || '',
        especie: p.especie || '',
        raza: p.raza || ''
      }));

      const datosComunes = {
        propietarioNombre: selectedPatient.propietario,
        nombreVeterinario: formData.nombreVeterinario.trim(),
        mpVeterinario: formData.mpVeterinario.trim(),
        correoVeterinario: formData.correoVeterinario.trim(),
        tipoConsentimiento: formData.tipoConsentimiento || "Consentimiento de Servicios a Domicilio",
        firmaPropietario: formData.firmaPropietario,
        fecha: fechaConsentimiento
      };

      let result;

      if (editingConsentimiento) {
        // Estamos editando un consentimiento existente (siempre individual)
        result = await consentimientosService.updateConsentimiento(
          editingConsentimiento.id,
          {
            ...datosComunes,
            pacienteId: String(selectedPatient.id),
            pacienteNombre: selectedPatient.nombre
          }
        );
      } else {
        // Creamos un consentimiento por cada mascota seleccionada, compartiendo firma y grupo
        const esGrupal = pacientesGrupo.length > 1;
        result = { success: true };
        for (const p of pacientesConsentimiento) {
          const consentimientoCompleto = {
            ...datosComunes,
            pacienteId: String(p.id),
            pacienteNombre: p.nombre,
            ...(esGrupal ? { grupoId, pacientesGrupo } : {})
          };
          console.log('📋 Datos de consentimiento a guardar:', consentimientoCompleto);
          const r = await consentimientosService.createConsentimiento(consentimientoCompleto);
          if (!r.success) {
            result = r;
            break;
          }
        }
      }

      if (result.success) {
        console.log('✅ Consentimiento(s) guardado(s) exitosamente');

        // Actualizar la fecha de última visita de cada mascota involucrada
        await Promise.all(
          pacientesConsentimiento.map((p) => updatePatientLastVisit(p.id))
        );
        
        setSnackbar({
          open: true,
          message: editingConsentimiento
            ? 'Consentimiento actualizado exitosamente'
            : (pacientesConsentimiento.length > 1
              ? `Consentimiento guardado para ${pacientesConsentimiento.length} mascotas`
              : 'Consentimiento guardado exitosamente'),
          severity: 'success'
        });
        
        // Recargar la lista de consentimientos del paciente actual usando la misma
        // función que la pestaña (debugConsentimientos, sin orderBy → no depende de índices).
        const consentimientosResult = await debugConsentimientos(String(selectedPatient.id));
        if (consentimientosResult.success) {
          let consentimientosList = consentimientosResult.forms || [];

          // Ordenar los consentimientos por fecha (el más reciente primero)
          consentimientosList = consentimientosList.sort((a, b) => {
            const fechaA = a.fecha?.toDate?.() || new Date(a.fecha || 0);
            const fechaB = b.fecha?.toDate?.() || new Date(b.fecha || 0);
            return fechaB - fechaA;
          });

          setConsentimientos(consentimientosList);
          setConsentimientosLoadedFor(String(selectedPatient.id));
        } else {
          // Si falla, forzar recarga perezosa al volver a la pestaña
          setConsentimientosLoadedFor(null);
        }
        
        // Cerrar el diálogo
        handleCloseNuevoConsentimiento();
      } else {
        throw new Error(result.error || 'Error desconocido');
      }
    } catch (error) {
      console.error('❌ Error guardando consentimiento:', error);
      
      setSnackbar({
        open: true,
        message: `Error al ${editingConsentimiento ? 'actualizar' : 'guardar'} el consentimiento: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleImprimirConsentimiento = (consentimiento) => {
    setConsentimientoParaImprimir(consentimiento);
    try {
      const rawTipo = (consentimiento.tipoConsentimiento || '').trim();
      const tipoNorm = (rawTipo && rawTipo.normalize)
        ? rawTipo.normalize('NFD').replace(/[ -\u036f]/g, '').toLowerCase()
        : rawTipo.toLowerCase();

      let tipoKey = 'domicilio';
      if (tipoNorm.includes('vacun')) tipoKey = 'vacunacion';
      else if (tipoNorm.includes('eutanas') || tipoNorm.includes('eutanasia')) tipoKey = 'eutanasia';
      else if (tipoNorm.includes('hospital') || tipoNorm.includes('hospitaliz')) tipoKey = 'hospitalizacion';
      else if (tipoNorm.includes('sedac') || tipoNorm.includes('sedat') || tipoNorm.includes('sedaci')) tipoKey = 'sedacion';
      else if (tipoNorm.includes('alta') && tipoNorm.includes('volunt')) tipoKey = 'altaVoluntaria';
      else if (tipoNorm.includes('domic')) tipoKey = 'domicilio';

      console.log('➡️ Abrir preview consentimiento — tipo raw:', rawTipo, 'tipoNorm:', tipoNorm, 'tipoKey:', tipoKey, 'consentimiento:', consentimiento, 'selectedPatient:', selectedPatient);
    } catch (err) {
      console.log('Error calculando tipo de consentimiento en handleImprimirConsentimiento', err, consentimiento);
    }

    setImprimirConsentimientoModalOpen(true);
  };

  const generarPDFConsentimiento = () => {
    if (!consentimientoParaImprimir || !selectedPatient) return;
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      let yPos = 20;

      const rawTipo = (consentimientoParaImprimir.tipoConsentimiento || '').trim();
      const tipoNorm = (rawTipo && rawTipo.normalize)
        ? rawTipo.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        : rawTipo.toLowerCase();

      let tipoKey = 'domicilio';
      if (tipoNorm.includes('vacun')) tipoKey = 'vacunacion';
      else if (tipoNorm.includes('eutanas') || tipoNorm.includes('eutanasia')) tipoKey = 'eutanasia';
      else if (tipoNorm.includes('hospital') || tipoNorm.includes('hospitaliz')) tipoKey = 'hospitalizacion';
      else if (tipoNorm.includes('sedac') || tipoNorm.includes('sedat') || tipoNorm.includes('sedaci')) tipoKey = 'sedacion';
      else if (tipoNorm.includes('alta') && tipoNorm.includes('volunt')) tipoKey = 'altaVoluntaria';
      else if (tipoNorm.includes('domic')) tipoKey = 'domicilio';

      const title = tipoKey === 'vacunacion'
        ? 'CONSENTIMIENTO INFORMADO PARA VACUNACIÓN'
        : (tipoKey === 'eutanasia' ? 'CONSENTIMIENTO INFORMADO PARA PROCEDIMIENTO DE EUTANASIA' : (tipoKey === 'hospitalizacion' ? 'CONSENTIMIENTO INFORMADO PARA HOSPITALIZACIÓN' : (tipoKey === 'sedacion' ? 'CONSENTIMIENTO INFORMADO PARA SEDACIÓN' : (tipoKey === 'altaVoluntaria' ? 'CONSTANCIA DE ALTA VOLUNTARIA' : 'CONSENTIMIENTO INFORMADO'))));

      doc.setFontSize(14);
      doc.text(title, pageWidth / 2, yPos, { align: 'center' });
      yPos += 12;

      const pacientesConsent = consentimientoParaImprimir.pacientes || consentimientoParaImprimir.pacientesGrupo || [selectedPatient];
      const responsableFrasePdf = buildResponsableFrasePlain(pacientesConsent);

      doc.setFontSize(11);
      doc.text(`Propietario: ${getPropietarioNombreCompleto(selectedPatient) || ''}`, 20, yPos); yPos += 7;
      doc.text(`${pacientesConsent.length > 1 ? 'Mascotas' : 'Paciente'}: ${buildNombresPacientes(pacientesConsent) || ''}`, 20, yPos); yPos += 7;
      doc.text(`Veterinario: ${consentimientoParaImprimir.nombreVeterinario || ''}`, 20, yPos); yPos += 7;
      doc.text(`M.P.: ${consentimientoParaImprimir.mpVeterinario || ''}`, 20, yPos); yPos += 10;

      const ownerPdf = (getPropietarioNombreCompleto(selectedPatient) || '[nombre del propietario]').toUpperCase();
      const cedulaPdf = getPropietarioCedula(selectedPatient);
      const cedulaPdfDisplay = cedulaPdf ? cedulaPdf.toUpperCase() : '';
      let bodyText = '';
      if (tipoKey === 'vacunacion') {
        bodyText = `La vacunación es un procedimiento médico preventivo cuyo objetivo es estimular el sistema inmune del paciente frente a diferentes enfermedades infecciosas propias de la especie.\n\nComo todo procedimiento médico veterinario, pueden presentarse efectos secundarios leves y transitorios posteriores a la aplicación de la vacuna, entre ellos: dolor o inflamación en el sitio de aplicación, decaimiento, somnolencia o disminución temporal del apetito, fiebre leve durante las primeras 24–48 horas posteriores a la vacunación. En casos poco frecuentes pueden presentarse reacciones adversas asociadas a la respuesta individual del paciente, tales como vómito o diarrea, inflamación facial, urticaria o reacciones alérgicas, dificultad respiratoria.\n\nEn caso de observar cualquiera de estos signos, el propietario deberá comunicarse inmediatamente con el Médico Veterinario tratante o acudir al centro veterinario más cercano. El propietario declara haber suministrado información veraz sobre el estado de salud actual del paciente y comprende los beneficios y posibles riesgos asociados al procedimiento.`;
      } else if (tipoKey === 'eutanasia') {
        const fecha = formatearFecha(consentimientoParaImprimir.fecha || new Date());
        const idSegmentPdf = cedulaPdfDisplay ? ` identificado(a) con documento No. ${cedulaPdfDisplay},` : '';
        bodyText = `CONSENTIMIENTO INFORMADO PARA PROCEDIMIENTO DE EUTANASIA\n\nLa eutanasia es un procedimiento médico veterinario humanitario cuyo objetivo es evitar sufrimiento innecesario y proporcionar una muerte digna al paciente, de acuerdo con criterios médicos, éticos y de bienestar animal.\n\nYo, ${ownerPdf}${idSegmentPdf} en calidad de propietario(a) y/o responsable ${responsableFrasePdf}, autorizo de manera libre y voluntaria la realización del procedimiento de eutanasia el día ${fecha}.\n\nDeclaro haber recibido información clara y comprensible acerca de:\n• El estado de salud y pronóstico del paciente.\n• El objetivo y desarrollo del procedimiento.\n• Las posibles alternativas terapéuticas, en caso de existir.\n• El carácter irreversible de la eutanasia.\n\nComprendo que el procedimiento será realizado mediante la administración de medicamentos veterinarios destinados a inducir una pérdida de conciencia seguida del fallecimiento del paciente de manera tranquila y humanitaria.\n\nAsi mismo, entiendo que durante el procedimiento pueden presentarse algunas respuestas involuntarias del organismo, tales como respiraciones agónicas, movimientos reflejos, vocalizaciones, relajación de esfínteres u otras manifestaciones fisiológicas posteriores a la pérdida de conciencia, las cuales no indican dolor o sufrimiento.\n\nDeclaro haber leído y comprendido la información anteriormente descrita y autorizo voluntariamente la realización del procedimiento.`;
      } else if (tipoKey === 'hospitalizacion') {
        const fecha = formatearFecha(consentimientoParaImprimir.fecha || new Date());
        const idSegmentPdf = cedulaPdfDisplay ? ` identificado(a) con documento No. ${cedulaPdfDisplay},` : '';
        bodyText = `Yo, ${ownerPdf}${idSegmentPdf} en calidad de propietario(a) y/o responsable ${responsableFrasePdf}, autorizo voluntariamente la atención y manejo médico bajo modalidad de hospitalización domiciliaria el día ${fecha}.\n\nDeclaro haber recibido información clara y comprensible acerca de:\n• El estado de salud actual y diagnóstico presuntivo o definitivo del paciente.\n• El plan médico y tratamientos recomendados.\n• Los posibles riesgos, beneficios y complicaciones asociadas a la condición clínica y evolución del paciente.\n• Las limitaciones que puede tener el manejo domiciliario en comparación con una hospitalización intrahospitalaria.\n\nComprendo que durante la hospitalización domiciliaria podrán requerirse procedimientos médicos complementarios, administración de medicamentos, monitoreo y controles periódicos según la evolución del paciente.\n\nEntiendo que, aunque el Médico Veterinario brindará atención y cuidados profesionales de acuerdo con los principios médicos y de bienestar animal, no es posible garantizar la recuperación total del paciente debido a la naturaleza variable de cada enfermedad y respuesta individual al tratamiento.\n\nAsimismo, comprendo que la condición clínica del paciente puede cambiar durante su evolución y que, en caso de deterioro, compromiso de la vida del paciente o necesidad de monitoreo continuo, podrá recomendarse traslado e ingreso a hospitalización en clínica veterinaria para un manejo más intensivo.\n\nMe comprometo a:\n• Seguir las recomendaciones médicas indicadas.\n• Mantener comunicación activa con el Médico Veterinario tratante.\n• Informar oportunamente cualquier cambio en el estado del paciente.\n• Facilitar un ambiente adecuado y seguro para la atención médica domiciliaria.\n• Asumir los costos derivados de la atención, tratamientos y procedimientos autorizados.\n\nDeclaro haber leído y comprendido la información anteriormente descrita y autorizo voluntariamente la hospitalización domiciliaria del paciente.`;
      } else if (tipoKey === 'sedacion') {
        const idSegmentPdf = cedulaPdfDisplay ? ` identificado(a) con documento No. ${cedulaPdfDisplay},` : '';
        bodyText = `Yo, ${ownerPdf}${idSegmentPdf} en calidad de propietario(a) y/o responsable ${responsableFrasePdf}, autorizo voluntariamente la realización del procedimiento médico y/o quirúrgico\n\nDeclaro haber recibido información clara y comprensible acerca de:\n\n• El procedimiento recomendado y su finalidad.\n• Los beneficios esperados y posibles riesgos asociados.\n• El uso de sedación y/o anestesia requerido para la realización del procedimiento.\n• Las posibles complicaciones relacionadas con la condición clínica del paciente, enfermedades preexistentes o respuesta individual a medicamentos y anestésicos.\n\nComprendo que, aunque se realizarán las valoraciones y medidas médicas necesarias para disminuir riesgos y brindar un manejo anestésico seguro, ningún procedimiento anestésico o quirúrgico está completamente exento de complicaciones.\n\nEntiendo que durante o posterior al procedimiento pueden presentarse complicaciones como:\n\n• Reacciones adversas o alérgicas a medicamentos.\n• Hemorragias.\n• Alteraciones respiratorias o cardiovasculares.\n• Hipotensión, hipotermia o arritmias.\n• Recuperación anestésica prolongada o alteraciones neurológicas.\n• Infecciones, inflamación o dehiscencia postquirúrgica.\n• Complicaciones derivadas de enfermedades no diagnosticadas previamente.\n• Paro cardiorrespiratorio.\n• Fallecimiento del paciente, aun tomando las medidas médicas y anestésicas correspondientes.\n\nAsimismo, comprendo que los exámenes prequirúrgicos y la valoración clínica disminuyen riesgos anestésicos y quirúrgicos, pero no los eliminan completamente.\n\nEn caso de presentarse una situación médica inesperada que comprometa la vida o bienestar del paciente durante el procedimiento, autorizo al Médico Veterinario tratante a realizar las maniobras, procedimientos o tratamientos que considere necesarios según su criterio profesional.\n\nEntiendo que no es posible garantizar resultados absolutos ni la recuperación total del paciente debido a la respuesta individual de cada animal y a la evolución propia de cada procedimiento o enfermedad.\n\nDeclaro haber tenido la oportunidad de resolver mis dudas, haber leído y comprendido la información anteriormente descrita y autorizo voluntariamente la realización del procedimiento mencionado.`;
      } else if (tipoKey === 'altaVoluntaria') {
        const idSegmentPdf = cedulaPdfDisplay ? ` identificado(a) con documento No. ${cedulaPdfDisplay},` : '';
        bodyText = `Yo, ${ownerPdf}${idSegmentPdf} en calidad de propietario(a) y/o responsable ${responsableFrasePdf}, autorizo voluntariamente la realización del procedimiento médico y/o quirúrgico.\n\nAsimismo, declaro haber sido informado(a) sobre la necesidad y beneficios del manejo médico, hospitalización, monitoreo, procedimientos diagnósticos o tratamientos recomendados para el bienestar del paciente.\n\nDe manera libre y voluntaria decido no continuar y/o rechazar el manejo médico recomendado, incluyendo hospitalización domiciliaria, hospitalización en clínica veterinaria, procedimientos diagnósticos, tratamientos o monitoreo sugerido según la condición clínica del paciente.\n\nComprendo que esta decisión puede implicar riesgos para la salud del paciente, incluyendo empeoramiento de su condición clínica, progresión de la enfermedad, complicaciones médicas e incluso fallecimiento.\n\nDeclaro que el Médico Veterinario tratante me explicó las posibles consecuencias derivadas de esta decisión y que tuve la oportunidad de resolver mis dudas antes de tomarla.\n\nAsimismo, asumo la responsabilidad del manejo y cuidados posteriores del paciente en casa y me comprometo a seguir las recomendaciones médicas entregadas.\n\nDeclaro haber leído y comprendido la información anteriormente descrita y firmo el presente documento de manera libre y voluntaria.`;
      } else {
        bodyText = `El objetivo de este documento es informar al propietario de la mascota sobre el procedimiento y obtener su consentimiento para la realización de servicios veterinarios a domicilio. Se realizarán visitas domiciliarias para proporcionar atención veterinaria que puede incluir, pero no se limita a, exámenes físicos, vacunaciones, tratamientos y procedimientos menores.\n Todos los procedimientos serán explicados detalladamente al propietario antes de su realización. Este servicio ayuda a disminuir el estrés de mascotas que no están acostumbradas a salir de sus casas y son sensibles al ruido, al contar con una atención personalizada en la comodidad de su hogar, evitamos desplazamientos y largas esperas en una recepción. Toda la información relacionada con la salud de su mascota será tratada con la máxima confidencialidad y solo se compartirá con terceros con su consentimiento expreso.`;
      }

      const split = doc.splitTextToSize(bodyText, pageWidth - 40);
      doc.text(split, 20, yPos);
      yPos += split.length * 6 + 10;

      if (consentimientoParaImprimir.firmaPropietario) {
        try { doc.addImage(consentimientoParaImprimir.firmaPropietario, 'PNG', 20, yPos, 70, 30); } catch (e) { console.error('Firma addImage error', e); }
      }
      yPos += 35;

      const fechaFormateada = new Date().toLocaleDateString('es-ES');
      doc.text(`Fecha: ${fechaFormateada}`, 20, yPos);

      doc.save(`Consentimiento_${selectedPatient.nombre}_${fechaFormateada.replace(/\//g, '-')}.pdf`);
      setImprimirConsentimientoModalOpen(false);
      setSnackbar({ open: true, message: 'Consentimiento generado correctamente', severity: 'success' });
    } catch (error) {
      console.error('Error generando PDF:', error);
      setSnackbar({ open: true, message: 'Error generando el PDF: ' + error.message, severity: 'error' });
    }
  };

  // Funciones para manejo de medicina preventiva
  
  // Funciones para vacunas
  const handleOpenNuevaVacuna = () => {
    setEditingVacuna(null);
    setVacunaData({
      nombreVacuna: '',
      fabricante: '',
      fechaAplicacion: new Date().toISOString().split('T')[0],
      proximaDosis: '',
      lote: '',
      veterinario: '',
      observaciones: ''
    });
    setNuevaVacunaOpen(true);
  };

  const handleCloseNuevaVacuna = () => {
    setNuevaVacunaOpen(false);
    setEditingVacuna(null);
  };

  const handleVacunaDataChange = (field, value) => {
    setVacunaData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditVacuna = (vacuna) => {
    console.log('✏️ Editando vacuna:', vacuna);
    setEditingVacuna(vacuna);
    setVacunaData({
      nombreVacuna: vacuna.nombreVacuna || '',
      fabricante: vacuna.fabricante || '',
      fechaAplicacion: vacuna.fechaAplicacion || '',
      proximaDosis: vacuna.proximaDosis || '',
      lote: vacuna.lote || '',
      veterinario: vacuna.veterinario || '',
      observaciones: vacuna.observaciones || ''
    });
    setNuevaVacunaOpen(true);
  };

  const handleDeleteVacuna = (vacunaId) => {
    setConfirmDialog({
      open: true,
      title: 'Eliminar vacuna',
      message: '¿Estás seguro de que deseas eliminar este registro de vacuna? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar vacuna',
      loading: false,
      onConfirm: async () => {
        try {
          console.log('🗑️ Eliminando vacuna:', vacunaId);
          setLoadingVacunas(true);

          const result = await vacunasService.deleteVacuna(vacunaId);

          if (result.success) {
            setVacunas(prev => prev.filter(v => v.id !== vacunaId));
            setSnackbar({
              open: true,
              message: 'Vacuna eliminada exitosamente de Firebase',
              severity: 'success'
            });
          } else {
            throw new Error(result.error);
          }
        } catch (error) {
          console.error('❌ Error eliminando vacuna:', error);
          setSnackbar({
            open: true,
            message: `Error eliminando vacuna: ${error.message}`,
            severity: 'error'
          });
        } finally {
          setLoadingVacunas(false);
        }
      }
    });
  };

  const handleSaveVacuna = async () => {
    if (!vacunaData.nombreVacuna || !vacunaData.fechaAplicacion || !vacunaData.veterinario) {
      setSnackbar({
        open: true,
        message: 'Por favor completa los campos requeridos: nombre de vacuna, fecha de aplicación y veterinario',
        severity: 'error'
      });
      return;
    }

    try {
      setLoadingVacunas(true);
      
      const datosVacuna = {
        ...vacunaData,
        pacienteId: selectedPatient.id,
        pacienteNombre: selectedPatient.nombre
      };

      let result;
      if (editingVacuna) {
        // Actualizar vacuna existente
        console.log('✏️ Actualizando vacuna:', editingVacuna.id);
        result = await vacunasService.updateVacuna(editingVacuna.id, datosVacuna);
        
        if (result.success) {
          // Actualizar en el estado local
          setVacunas(prev => prev.map(v => v.id === editingVacuna.id ? result.data : v));
          setSnackbar({
            open: true,
            message: 'Vacuna actualizada exitosamente en Firebase',
            severity: 'success'
          });
        }
      } else {
        // Crear nueva vacuna
        console.log('💉 Creando nueva vacuna para paciente:', selectedPatient.nombre);
        result = await vacunasService.createVacuna(datosVacuna);
        
        if (result.success) {
          // Agregar al estado local
          setVacunas(prev => [...prev, result.data]);
          setSnackbar({
            open: true,
            message: 'Vacuna registrada exitosamente en Firebase',
            severity: 'success'
          });
        }
      }

      if (!result.success) {
        throw new Error(result.error);
      }

      handleCloseNuevaVacuna();
    } catch (error) {
      console.error('❌ Error guardando vacuna:', error);
      setSnackbar({
        open: true,
        message: `Error guardando vacuna: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoadingVacunas(false);
    }
  };

  // Funciones para desparasitaciones
  const handleOpenNuevaDesparasitacion = () => {
    setEditingDesparasitacion(null);
    setDesparasitacionData({
      nombreProducto: '',
      tipo: 'interno',
      principioActivo: '',
      fechaAplicacion: '',
      proximaDosis: '',
      dosis: '',
      veterinario: '',
      observaciones: ''
    });
    setNuevaDesparasitacionOpen(true);
  };

  const handleCloseNuevaDesparasitacion = () => {
    setNuevaDesparasitacionOpen(false);
    setEditingDesparasitacion(null);
  };

  const handleDesparasitacionDataChange = (field, value) => {
    setDesparasitacionData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditDesparasitacion = (desparasitacion) => {
    console.log('✏️ Editando desparasitación:', desparasitacion);
    setEditingDesparasitacion(desparasitacion);
    setDesparasitacionData({
      nombreProducto: desparasitacion.nombreProducto || '',
      tipo: desparasitacion.tipo || 'interno',
      principioActivo: desparasitacion.principioActivo || '',
      fechaAplicacion: desparasitacion.fechaAplicacion || '',
      proximaDosis: desparasitacion.proximaDosis || '',
      dosis: desparasitacion.dosis || '',
      veterinario: desparasitacion.veterinario || '',
      observaciones: desparasitacion.observaciones || ''
    });
    setNuevaDesparasitacionOpen(true);
  };

  const handleDeleteDesparasitacion = (desparasitacionId) => {
    setConfirmDialog({
      open: true,
      title: 'Eliminar desparasitación',
      message: '¿Estás seguro de que deseas eliminar este registro de desparasitación? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar desparasitación',
      loading: false,
      onConfirm: async () => {
        try {
          console.log('🗑️ Eliminando desparasitación:', desparasitacionId);
          setLoadingDesparasitaciones(true);

          const result = await desparasitacionesService.deleteDesparasitacion(desparasitacionId);

          if (result.success) {
            // Actualizar el estado local
            setDesparasitaciones(prev => prev.filter(d => d.id !== desparasitacionId));
            setSnackbar({
              open: true,
              message: 'Desparasitación eliminada exitosamente de Firebase',
              severity: 'success'
            });
          } else {
            throw new Error(result.error);
          }
        } catch (error) {
          console.error('❌ Error eliminando desparasitación:', error);
          setSnackbar({
            open: true,
            message: `Error eliminando desparasitación: ${error.message}`,
            severity: 'error'
          });
        } finally {
          setLoadingDesparasitaciones(false);
        }
      }
    });
  };

  const handleSaveDesparasitacion = async () => {
    if (!desparasitacionData.nombreProducto || !desparasitacionData.fechaAplicacion || !desparasitacionData.veterinario) {
      setSnackbar({
        open: true,
        message: 'Por favor completa los campos requeridos: nombre del producto, fecha de aplicación y veterinario',
        severity: 'error'
      });
      return;
    }

    try {
      setLoadingDesparasitaciones(true);
      
      const datosDesparasitacion = {
        ...desparasitacionData,
        pacienteId: selectedPatient.id,
        pacienteNombre: selectedPatient.nombre
      };

      let result;
      if (editingDesparasitacion) {
        // Actualizar desparasitación existente
        console.log('✏️ Actualizando desparasitación:', editingDesparasitacion.id);
        result = await desparasitacionesService.updateDesparasitacion(editingDesparasitacion.id, datosDesparasitacion);
        
        if (result.success) {
          // Actualizar en el estado local
          setDesparasitaciones(prev => prev.map(d => d.id === editingDesparasitacion.id ? result.data : d));
          setSnackbar({
            open: true,
            message: 'Desparasitación actualizada exitosamente en Firebase',
            severity: 'success'
          });
        }
      } else {
        // Crear nueva desparasitación
        console.log('🐛 Creando nueva desparasitación para paciente:', selectedPatient.nombre);
        result = await desparasitacionesService.createDesparasitacion(datosDesparasitacion);
        
        if (result.success) {
          // Agregar al estado local
          setDesparasitaciones(prev => [...prev, result.data]);
          setSnackbar({
            open: true,
            message: 'Desparasitación registrada exitosamente en Firebase',
            severity: 'success'
          });
        }
      }

      if (!result.success) {
        throw new Error(result.error);
      }

      handleCloseNuevaDesparasitacion();
    } catch (error) {
      console.error('❌ Error guardando desparasitación:', error);
      setSnackbar({
        open: true,
        message: `Error guardando desparasitación: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoadingDesparasitaciones(false);
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          // Calcular las nuevas dimensiones manteniendo la proporción
          const MAX_SIZE = 800; // Tamaño máximo en píxeles
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_SIZE) {
              height = Math.round((height * MAX_SIZE) / width);
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width = Math.round((width * MAX_SIZE) / height);
              height = MAX_SIZE;
            }
          }
          
          // Crear un canvas para redimensionar la imagen
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          // Dibujar la imagen redimensionada
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convertir a base64 con calidad reducida
          const quality = 0.7; // 70% de calidad, ajustar según necesidad
          const base64 = canvas.toDataURL('image/jpeg', quality);
          
          console.log(`Imagen redimensionada: ${width}x${height}, tamaño estimado: ${Math.round(base64.length/1024)}KB`);
          
          // Verificar que el tamaño sea menor que el límite de Firestore
          if (base64.length > 900000) { // ~900KB para tener margen
            console.warn("La imagen sigue siendo muy grande. Reduciendo calidad adicional.");
            const lowerQuality = canvas.toDataURL('image/jpeg', 0.5);
            resolve(lowerQuality);
          } else {
            resolve(base64);
          }
        };
        img.onerror = error => reject(error);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Función para guardar paciente
  const handleSavePatient = async (patientData) => {
    if (!patientData.nombre || !patientData.propietario) {
      setSnackbar({
        open: true,
        message: 'Por favor completa los campos obligatorios (Nombre del paciente y Propietario)',
        severity: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const dataToSave = {
        ...patientData
      };

      if (editingPatient) {
        // Actualizar paciente existente
        console.log('Actualizando paciente:', editingPatient.id, dataToSave);
        
        // Incluir la fecha de última visita al actualizar
        const today = new Date();
        
        // Usar directamente el objeto Date para evitar problemas de formato
        dataToSave.ultimaVisita = today;
        
        const result = await patientsService.updatePatient(editingPatient.id, dataToSave);
        
        if (result.success) {
          setSnackbar({
            open: true,
            message: 'Paciente actualizado exitosamente',
            severity: 'success'
          });
          // Recargar la lista de pacientes
          await loadPatientsFromFirebase();
        } else {
          setSnackbar({
            open: true,
            message: `Error actualizando paciente: ${result.error}`,
            severity: 'error'
          });
        }
      } else {
        // Crear nuevo paciente
        console.log('Creando nuevo paciente:', dataToSave);
        
        // Incluir la fecha de última visita al crear un nuevo paciente
        const today = new Date();
        
        // Usar directamente el objeto Date para evitar problemas de formato
        dataToSave.ultimaVisita = today;
        
        const result = await patientsService.createPatient(dataToSave);
        
        if (result.success) {
          setSnackbar({
            open: true,
            message: 'Nuevo paciente agregado exitosamente',
            severity: 'success'
          });
          // Recargar la lista de pacientes
          await loadPatientsFromFirebase();
        } else {
          setSnackbar({
            open: true,
            message: `Error creando paciente: ${result.error}`,
            severity: 'error'
          });
        }
      }
      
      setDialogOpen(false);
    } catch (error) {
      console.error('Error en handleSavePatient:', error);
      setSnackbar({
        open: true,
        message: `Error de conexión: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar paciente
  const handleDeletePatient = async (patientId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este paciente? Esta acción marcará el paciente como inactivo.')) {
      setLoading(true);
      
      try {
        console.log('Eliminando paciente:', patientId);
        const pacienteActual = patients.find(p => p.id === patientId);
        const result = await patientsService.deletePatient(patientId);
        
        if (result.success) {
          setSnackbar({
            open: true,
            message: 'Paciente marcado como inactivo exitosamente',
            severity: 'warning'
          });
          await generarCartaCondolenciasPDF(pacienteActual);
          // Recargar la lista de pacientes
          await loadPatientsFromFirebase();
        } else {
          setSnackbar({
            open: true,
            message: `Error eliminando paciente: ${result.error}`,
            severity: 'error'
          });
        }
      } catch (error) {
        console.error('Error en handleDeletePatient:', error);
        setSnackbar({
          open: true,
          message: `Error de conexión: ${error.message}`,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ 
        flexGrow: 1, 
        p: { xs: 1, sm: 2, md: 3 }, 
        maxWidth: '100%', 
        overflowX: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: 1,
            maxWidth: '100%'
          }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#c46494',
                fontSize: { xs: '1.5rem', sm: '2.125rem' },
                mb: { xs: 0.5, sm: 0 }
              }}
            >
              🐾 Gestión de Pacientes
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              mt: { xs: 0.5, sm: 0 },
              alignSelf: { xs: 'flex-end', sm: 'auto' }
            }}>
              <Chip
                label="🔥 Conectado"
                color="success"
                size="small"
                sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
              />
              {loading && (
                <Chip
                  label="Cargando..."
                  color="warning"
                  size="small"
                  icon={<CircularProgress size={16} />}
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
            </Box>
          </Box>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
              mt: { xs: 0, sm: 0.5 }
            }}
          >
            Información de pacientes veterinarios
          </Typography>
        </Box>

        {/* Reducir trabajo de render durante escritura en el formulario */}
        {!dialogOpen && (
        <>
        {/* Estadísticas rápidas */}
        <Grid container spacing={1} sx={{ mb: { xs: 1.5, sm: 3 } }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: '#e3f2fd', 
              border: '1px solid #90caf9',
              height: '100%',
              boxShadow: 'none',
              borderRadius: 2,
              overflow: 'hidden',
            }}>
              <CardContent sx={{ p: { xs: 0.75, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Avatar sx={{ 
                    bgcolor: '#1976d2', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 0.5, sm: 0 },
                    width: { xs: 24, sm: 40 },
                    height: { xs: 24, sm: 40 }
                  }}>
                    <PeopleIcon sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' } }} />
                  </Avatar>
                  <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 'bold', 
                      color: '#1976d2',
                      fontSize: { xs: '1.125rem', sm: '1.5rem' }
                    }}>
                      {patients.length}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.625rem', sm: '0.875rem' } }}
                    >
                      Total Pacientes
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: '#e8f5e8', 
              border: '1px solid #81c784',
              height: '100%',
              boxShadow: 'none',
              borderRadius: 2,
              overflow: 'hidden',
            }}>
              <CardContent sx={{ p: { xs: 0.75, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Avatar sx={{ 
                    bgcolor: '#4caf50', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 0.5, sm: 0 },
                    width: { xs: 24, sm: 40 },
                    height: { xs: 24, sm: 40 }
                  }}>
                    <PetsIcon sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' } }} />
                  </Avatar>
                  <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 'bold', 
                      color: '#4caf50',
                      fontSize: { xs: '1.125rem', sm: '1.5rem' }
                    }}>
                      {patients.filter(p => p.estado === 'activo').length}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.625rem', sm: '0.875rem' } }}
                    >
                      Activos
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: '#fff3e0', 
              border: '1px solid #ffb74d',
              height: '100%',
              boxShadow: 'none',
              borderRadius: 2,
              overflow: 'hidden',
            }}>
              <CardContent sx={{ p: { xs: 0.75, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Avatar sx={{ 
                    bgcolor: '#ff9800', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 0.5, sm: 0 },
                    width: { xs: 24, sm: 40 },
                    height: { xs: 24, sm: 40 }
                  }}>
                    <MedicalIcon sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' } }} />
                  </Avatar>
                  <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 'bold', 
                      color: '#ff9800',
                      fontSize: { xs: '1.125rem', sm: '1.5rem' }
                    }}>
                      {patients.filter(p => p.estado === 'en_tratamiento').length}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.625rem', sm: '0.875rem' } }}
                    >
                      En Tratamiento
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: '#f3e5f5', 
              border: '1px solid #ba68c8',
              height: '100%',
              boxShadow: 'none',
              borderRadius: 2,
              overflow: 'hidden',
            }}>
              <CardContent sx={{ p: { xs: 0.75, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Avatar sx={{ 
                    bgcolor: '#9c27b0', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 0.5, sm: 0 },
                    width: { xs: 24, sm: 40 },
                    height: { xs: 24, sm: 40 }
                  }}>
                    <EventIcon sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' } }} />
                  </Avatar>
                  <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 'bold', 
                      color: '#9c27b0',
                      fontSize: { xs: '1.125rem', sm: '1.5rem' }
                    }}>
                      {filteredPatients.length}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.625rem', sm: '0.875rem' } }}
                    >
                      Filtrados
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Barra de búsqueda y acciones */}
        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar paciente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setSearchTerm('')} size="small">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1.5, height: { xs: '40px', sm: '56px' } }
                  }}
                  sx={{ mb: { xs: 1, sm: 0 } }}
                />
              </Grid>

              <Grid item xs={6} sm={6} md={2} lg={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FilterIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ 
                    height: { xs: '40px', sm: '56px' },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    borderRadius: 1.5,
                    whiteSpace: 'nowrap',
                    px: { xs: 1, sm: 2 }
                  }}
                >
                  {showFilters ? 'Ocultar filtros' : 'Filtros'}
                </Button>
              </Grid>

              <Grid item xs={6} sm={6} md={2} lg={2}>
                {(searchTerm || filterEspecie !== 'todos' || filterEstado !== 'todos') && (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ClearIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                    onClick={clearFilters}
                    sx={{ 
                      height: { xs: '40px', sm: '56px' },
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      borderRadius: 1.5,
                      whiteSpace: 'nowrap',
                      px: { xs: 1, sm: 2 }
                    }}
                  >
                    Limpiar
                  </Button>
                )}
              </Grid>

              <Grid item xs={12} md={4} lg={4}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  justifyContent: { xs: 'space-between', md: 'flex-end' },
                  mt: { xs: 1, sm: 1, md: 0 }
                }}>
                  <Button
                    variant="outlined"
                    startIcon={<EventIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                    onClick={loadPatientsFromFirebase}
                    disabled={loading}
                    sx={{ 
                      height: { xs: '40px', sm: '56px' },
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      borderRadius: 1.5,
                      whiteSpace: 'nowrap',
                      px: { xs: 1, sm: 2 },
                      minWidth: { xs: '100px', sm: 'auto' }
                    }}
                  >
                    {loading ? <CircularProgress size={16} /> : 'Recargar'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                    onClick={handleAddPatient}
                    sx={{ 
                      bgcolor: '#c46494',
                      '&:hover': { bgcolor: '#a05080' },
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      height: { xs: '40px', sm: '56px' },
                      borderRadius: 1.5,
                      whiteSpace: 'nowrap',
                      px: { xs: 1, sm: 2 },
                      minWidth: { xs: '120px', sm: 'auto' }
                    }}
                  >
                    Nuevo Paciente
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Filtros expandibles */}
            <Collapse in={showFilters}>
              <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" sx={{ minHeight: '56px' }}>
                      <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Especie</InputLabel>
                      <Select
                        value={filterEspecie}
                        onChange={(e) => setFilterEspecie(e.target.value)}
                        label="Especie"
                        sx={{ 
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          borderRadius: 1.5
                        }}
                      >
                        <MenuItem value="todos">Todas las especies</MenuItem>
                        <MenuItem value="Canino">Canino</MenuItem>
                        <MenuItem value="Felino">Felino</MenuItem>
                        <MenuItem value="Ave">Ave</MenuItem>
                        <MenuItem value="Roedor">Roedor</MenuItem>
                        <MenuItem value="Otro">Otro</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" sx={{ minHeight: '56px' }}>
                      <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Estado</InputLabel>
                      <Select
                        value={filterEstado}
                        onChange={(e) => setFilterEstado(e.target.value)}
                        label="Estado"
                        sx={{ 
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          borderRadius: 1.5
                        }}
                      >
                        <MenuItem value="todos">Todos los estados</MenuItem>
                        <MenuItem value="activo">Activo</MenuItem>
                        <MenuItem value="inactivo">Inactivo</MenuItem>
                        <MenuItem value="en_tratamiento">En Tratamiento</MenuItem>
                        <MenuItem value="critico">Crítico</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mt: { xs: 1, sm: 2 }, 
                        textAlign: { xs: 'center', md: 'right' },
                        fontSize: { xs: '0.7rem', sm: '0.875rem' }
                      }}
                    >
                      Mostrando {filteredPatients.length} de {patients.length} pacientes
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Contenido principal - Lista de pacientes */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: { xs: 1, sm: 2 }, 
                    color: '#c46494',
                    fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <AssignmentIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                  Lista de Pacientes
                </Typography>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={40} sx={{ color: '#c46494' }} />
                  </Box>
                ) : filteredPatients.length === 0 ? (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      borderRadius: 2,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  >
                    {searchTerm || filterEspecie !== 'todos' || filterEstado !== 'todos' 
                      ? 'No se encontraron pacientes que coincidan con los filtros aplicados.'
                      : 'No hay pacientes registrados. ¡Agrega el primer paciente!'}
                  </Alert>
                ) : (
                  <Box sx={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 2, 
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    '& .MuiPaper-root': { 
                      boxShadow: 'none',
                      borderRadius: 0
                    }
                  }}>
                    {/* Vista móvil: Tarjetas de pacientes */}
                    <Box sx={{ 
                      display: { xs: 'flex', sm: 'none' },
                      flexDirection: 'column',
                      gap: 1.5,
                      p: 1.5,
                      bgcolor: '#f8f9fa'
                    }}>
                      {filteredPatients.map((paciente) => (
                        <Card 
                          key={paciente.id}
                          sx={{ 
                            display: 'flex', 
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            border: '1px solid #f0f0f0',
                            overflow: 'hidden'
                          }}
                          onClick={() => handleViewPatient(paciente)}
                        >
                          <Box sx={{ 
                            width: 80, 
                            bgcolor: '#f5f0f5', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            position: 'relative'
                          }}>
                            <Avatar
                              src={paciente.foto}
                              sx={{ 
                                width: 60, 
                                height: 60, 
                                border: '2px solid white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                              }}
                            >
                              {!paciente.foto && <PetsIcon />}
                            </Avatar>
                            <Chip
                              label={paciente.especie}
                              sx={{ 
                                position: 'absolute', 
                                top: 5, 
                                left: 5, 
                                height: 16, 
                                fontSize: '0.6rem',
                                bgcolor: '#c46494',
                                color: 'white',
                                fontWeight: 'bold',
                                '& .MuiChip-label': { px: 0.75 }
                              }}
                            />
                          </Box>
                          <Box sx={{ flexGrow: 1, p: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                                {paciente.nombre}
                              </Typography>
                              <Chip
                                label={paciente.estado?.replace('_', ' ') || 'activo'}
                                color={getEstadoColor(paciente.estado)}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.6rem',
                                  textTransform: 'capitalize',
                                  '& .MuiChip-label': { px: 0.75 }
                                }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                              Propietario: <span style={{ fontWeight: '500' }}>{paciente.propietario}</span>
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                              ID: <span style={{ fontWeight: '500' }}>{paciente.microchip || 'no'}</span>
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                {formatearFecha(paciente.ultimaVisita) || formatearFecha(paciente.fechaCreacion)}
                              </Typography>
                              <Box>
                                <Tooltip title="Editar">
                                  <IconButton 
                                    size="small"
                                    sx={{ color: '#1976d2', p: 0.5, mr: 0.5 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditPatient(paciente);
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Ver Expediente">
                                  <IconButton 
                                    size="small"
                                    sx={{ color: '#c46494', p: 0.5 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewPatient(paciente);
                                    }}
                                  >
                                    <ViewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Box>
                        </Card>
                      ))}
                    </Box>

                    {/* Vista de escritorio: Tabla */}
                    <TableContainer 
                      component={Paper} 
                      sx={{ 
                        display: { xs: 'none', sm: 'block' },
                        maxHeight: { sm: 450, md: 600 }, 
                        overflowX: 'auto',
                        width: '100%',
                        '&::-webkit-scrollbar': {
                          width: '8px',
                          height: '8px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: '#c46494',
                          borderRadius: '4px'
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                    >
                      <Table 
                        stickyHeader 
                        size="small" 
                        sx={{ 
                          '& .MuiTableCell-root': { 
                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                            padding: { xs: '8px 6px', sm: '16px 8px' }
                          },
                          '& .MuiTableCell-head': {
                            backgroundColor: '#f8f2f7',
                            color: '#c46494',
                            fontWeight: 'bold'
                          }
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Paciente</strong></TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Especie/Raza</strong></TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Edad</strong></TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Propietario</strong></TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Estado</strong></TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Última Visita</strong></TableCell>
                            <TableCell align="center"><strong>Acciones</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredPatients.map((paciente) => (
                            <TableRow 
                              key={paciente.id} 
                              hover 
                              sx={{ 
                                '&:hover': { backgroundColor: '#fafafa' },
                                cursor: { xs: 'pointer', sm: 'default' },
                                transition: 'background-color 0.2s'
                              }}
                              onClick={(event) => {
                                // Solo activar click en fila en móviles
                                if (window.innerWidth < 600) {
                                  handleViewPatient(paciente);
                                }
                              }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                                  <Avatar 
                                    sx={{ 
                                      mr: { xs: 0, sm: 2 }, 
                                      mb: { xs: 1, sm: 0 },
                                      bgcolor: '#c46494',
                                      width: { xs: 40, sm: 48 },
                                      height: { xs: 40, sm: 48 },
                                      border: '2px solid #f0f0f0'
                                    }}
                                    src={paciente.foto}
                                  >
                                    {!paciente.foto && <PetsIcon sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' } }} />}
                                  </Avatar>
                                  <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, width: '100%' }}>
                                    <Typography 
                                      variant="body1" 
                                      sx={{ 
                                        fontWeight: 'bold', 
                                        fontSize: { xs: '0.8rem', sm: 'inherit' },
                                        color: '#333',
                                        mb: { xs: 0.5, sm: 0 }
                                      }}
                                    >
                                      {paciente.nombre}
                                    </Typography>
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary" 
                                      sx={{ 
                                        display: { xs: 'none', sm: 'block' },
                                        fontSize: { xs: '0.65rem', sm: '0.75rem' }
                                      }}
                                    >
                                      ID: {paciente.microchip || 'no'}
                                    </Typography>
                                    {/* En móviles mostrar especie y estado junto al nombre */}
                                    <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1, mt: 0.5 }}>
                                      <Typography variant="caption" sx={{ color: '#c46494', fontWeight: 'bold', fontSize: '0.65rem' }}>
                                        {paciente.especie || 'N/A'}
                                      </Typography>
                                      <Chip
                                        label={paciente.estado?.replace('_', ' ') || 'activo'}
                                        color={getEstadoColor(paciente.estado)}
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setStatusMenuAnchorEl({
                                            ...statusMenuAnchorEl,
                                            [paciente.id]: event.currentTarget
                                          });
                                        }}
                                        sx={{
                                          fontWeight: 'bold',
                                          textTransform: 'capitalize',
                                          cursor: 'pointer',
                                          '&:hover': {
                                            opacity: 0.9,
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                          },
                                          height: 20,
                                      fontSize: '0.6rem',
                                          '& .MuiChip-label': { px: 0.75, fontSize: '0.55rem' }
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                <Box>
                                  <Typography variant="body2" sx={{ color: '#c46494', fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    {paciente.especie || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                    {paciente.raza || 'N/A'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Typography variant="body2" sx={{ color: '#333', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                  {paciente.fechaNacimiento ? calcularEdad(paciente.fechaNacimiento) : 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    {paciente.propietario}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                    {paciente.propietarioTelefono}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Tooltip title="Clic para cambiar estado">
                                    <Chip
                                      label={paciente.estado?.replace('_', ' ') || 'activo'}
                                      color={getEstadoColor(paciente.estado)}
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setStatusMenuAnchorEl({
                                          ...statusMenuAnchorEl,
                                          [paciente.id]: e.currentTarget
                                        });
                                      }}
                                      sx={{
                                        fontWeight: 'bold',
                                        textTransform: 'capitalize',
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                        cursor: 'pointer',
                                        '&:hover': { 
                                          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                          opacity: 0.9
                                        }
                                      }}
                                    />
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Typography variant="body2" sx={{ color: '#333', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                  {formatearFecha(paciente.ultimaVisita) || formatearFecha(paciente.fechaCreacion)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ 
                                  display: 'flex', 
                                  gap: { xs: 0.25, sm: 1 }, 
                                  justifyContent: 'center', 
                                  flexWrap: 'nowrap',
                                  alignItems: 'center'
                                }}>
                                  <Tooltip title="Ver Expediente">
                                    <IconButton
                                      size="small"
                                      sx={{ 
                                        color: '#c46494',
                                        '&:hover': { backgroundColor: '#f5f0f5' },
                                        padding: { xs: 0.5, sm: 1 },
                                        fontSize: { xs: '0.75rem', sm: '1rem' },
                                        width: { xs: 28, sm: 'auto' },
                                        height: { xs: 28, sm: 'auto' },
                                        minWidth: { xs: 28, sm: 40 }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation(); // Evita que el onClick de la fila se active
                                        handleViewPatient(paciente);
                                      }}
                                    >
                                      <ViewIcon sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Editar">
                                    <IconButton
                                      size="small"
                                      sx={{ 
                                        color: '#1976d2',
                                        '&:hover': { backgroundColor: '#e3f2fd' },
                                        padding: { xs: 0.5, sm: 1 },
                                        fontSize: { xs: '0.75rem', sm: '1rem' }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditPatient(paciente);
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Eliminar">
                                    <IconButton
                                      size="small"
                                      sx={{ 
                                        color: '#d32f2f',
                                        '&:hover': { backgroundColor: '#ffebee' },
                                        padding: { xs: 0.5, sm: 1 },
                                        display: { xs: 'none', sm: 'inline-flex' },
                                        fontSize: { xs: '0.75rem', sm: '1rem' }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeletePatient(paciente.id);
                                      }}
                                    >
                                      <DeleteIcon sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                    
                  {/* Paginación simplificada - especialmente útil en móviles */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      p: 1.5,
                      borderTop: '1px solid #e0e0e0',
                      bgcolor: '#f8f2f7'
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#666', 
                        fontSize: { xs: '0.7rem', sm: '0.8rem' }
                      }}
                    >
                      Mostrando {filteredPatients.length} de {patients.length} pacientes
                    </Typography>
                  </Box>
                  
                  {/* Mensaje de ayuda para móviles */}
                  <Box sx={{ mt: 1, p: 1 }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        display: { xs: 'block', sm: 'none' },
                        fontSize: '0.65rem',
                        textAlign: 'center',
                        fontStyle: 'italic'
                      }}
                    >
                      Toque en un paciente para ver su expediente completo
                    </Typography>
                  </Box>
                </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Botón flotante para agregar paciente - visible solo en móvil */}
        <Fab
          color="primary"
          aria-label="add"
          size="medium"
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            bgcolor: '#c46494',
            '&:hover': { bgcolor: '#a05080' },
            display: { xs: 'flex', md: 'none' },
            width: { xs: 50, sm: 56 },
            height: { xs: 50, sm: 56 },
            boxShadow: '0 4px 10px rgba(196, 100, 148, 0.5)',
            zIndex: 1000
          }}
          onClick={handleAddPatient}
        >
          <AddIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
        </Fab>

        {/* Dialog del Expediente Completo */}
        <Dialog
          open={expedienteOpen}
          onClose={handleCloseExpediente}
          maxWidth="lg"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: { xs: 0, sm: 2 },
              maxHeight: { xs: '100vh', sm: '95vh' },
              height: { xs: '100vh', sm: '95vh' },
              margin: { xs: 0, sm: 2 },
              width: { xs: '100%', sm: 'calc(100% - 32px)' }
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: '#c46494', color: 'white', p: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                <PetsIcon sx={{ mr: 1, fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 'bold', 
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    📋 {selectedPatient?.nombre}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        opacity: 0.9, 
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {selectedPatient?.especie} • {selectedPatient?.raza} • {selectedPatient?.propietario}
                    </Typography>
                    <Tooltip title="Cambiar estado del paciente">
                      <Chip
                        label={selectedPatient?.estado?.replace('_', ' ') || 'activo'}
                        color={getEstadoColor(selectedPatient?.estado)}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setStatusMenuAnchorEl({
                            ...statusMenuAnchorEl,
                            [selectedPatient?.id]: e.currentTarget
                          });
                        }}
                        sx={{
                          fontWeight: 'bold',
                          textTransform: 'capitalize',
                          fontSize: '0.7rem',
                          cursor: 'pointer',
                          height: 20,
                          ml: 1,
                          '&:hover': { 
                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                            opacity: 0.9
                          }
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
              <Box>
                <Tooltip title="Cerrar Expediente">
                  <IconButton onClick={handleCloseExpediente} sx={{ color: 'white' }} size="small">
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 0, bgcolor: '#f8f9fa' }}>
            {/* Información Básica del Paciente */}
            <Box sx={{ bgcolor: 'white', p: { xs: 1.5, sm: 3 }, borderBottom: '1px solid #e0e0e0' }}>
              <Grid container spacing={2}>
                <Grid item xs={5} sm={4} md={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar 
                      sx={{ 
                        width: { xs: 80, sm: 100, md: 120 }, 
                        height: { xs: 80, sm: 100, md: 120 }, 
                        bgcolor: '#c46494', 
                        mb: { xs: 1, sm: 2 },
                        fontSize: { xs: '2rem', sm: '3rem' },
                        border: '4px solid #f0f0f0',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                      src={selectedPatient?.foto}
                    >
                      {!selectedPatient?.foto && '🐾'}
                    </Avatar>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 'bold', 
                      color: '#c46494', 
                      textAlign: 'center',
                      fontSize: { xs: '1.2rem', sm: '1.5rem' }
                    }}>
                      {selectedPatient?.nombre}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      textAlign: 'center',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      mb: { xs: 0.5, sm: 1 }
                    }}>
                      {selectedPatient?.especie} • {selectedPatient?.raza}
                    </Typography>
                    <Chip 
                      label={selectedPatient?.estado?.replace('_', ' ') || 'activo'}
                      color={getEstadoColor(selectedPatient?.estado)}
                      size="small"
                      sx={{ 
                        fontWeight: 'bold', 
                        textTransform: 'capitalize',
                        fontSize: { xs: '0.7rem', sm: '0.8rem' }
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={7} sm={8} md={4}>
                  <Typography variant="h6" sx={{ 
                    color: '#c46494', 
                    mb: { xs: 1, sm: 2 },
                    fontSize: { xs: '0.95rem', sm: '1.25rem' }
                  }}>
                    🐾 Información del Paciente
                  </Typography>
                  <List dense sx={{ '& .MuiListItemText-primary': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText 
                        primary="Sexo" 
                        secondary={selectedPatient?.sexo || 'N/A'}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText 
                        primary="Edad" 
                        secondary={`${calcularEdad(selectedPatient?.fechaNacimiento)}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText 
                        primary="Peso" 
                        secondary={`${selectedPatient?.peso || 'N/A'} kg`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText 
                        primary="Color" 
                        secondary={selectedPatient?.color || 'N/A'}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemText 
                        primary="Microchip" 
                        secondary={selectedPatient?.microchip || 'Sin microchip'}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                      />
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Typography variant="h6" sx={{ color: '#c46494', mb: 2 }}>
                    👤 Información del Propietario
                  </Typography>
                  <List dense>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', width: 30, height: 30 }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={`${selectedPatient?.propietario} ${selectedPatient?.propietarioApellido || ''}`}
                        secondary="Propietario"
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#e8f5e8', color: '#4caf50', width: 30, height: 30 }}>
                          <PhoneIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={selectedPatient?.propietarioTelefono || 'N/A'}
                        secondary="Teléfono"
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#fff3e0', color: '#ff9800', width: 30, height: 30 }}>
                          <EmailIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={selectedPatient?.propietarioEmail || 'N/A'}
                        secondary="Email"
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#f3e5f5', color: '#9c27b0', width: 30, height: 30 }}>
                          <LocationIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={`${selectedPatient?.propietarioDireccion || 'N/A'}, ${selectedPatient?.propietarioCiudad || 'N/A'}`}
                        secondary="Dirección"
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </Box>

            {/* Pestañas del Expediente */}
            <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', overflowX: 'auto', maxWidth: '100%' }}>
              <Tabs 
                value={expedienteTabValue} 
                onChange={(e, newValue) => setExpedienteTabValue(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ 
                  '& .MuiTab-root': { 
                    minHeight: { xs: 36, sm: 48, md: 60 },
                    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
                    fontWeight: 'bold',
                    minWidth: { xs: 80, sm: 100, md: 120 },
                    px: { xs: 1, sm: 2 }
                  },
                  '& .Mui-selected': {
                    color: '#c46494 !important'
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#c46494'
                  },
                  '& .MuiTabs-scrollButtons': {
                    width: { xs: 24, sm: 40 },
                    height: { xs: 24, sm: 40 }
                  }
                }}
              >
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon fontSize="small" />
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Historia Clínica</Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Historia</Box>
                        <Badge badgeContent={historiaClinica.length} color="primary" sx={{ ml: 1 }} />
                      </Box>
                    </Box>
                  } 
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PharmacyIcon fontSize="small" />
                      <Box component="span">Recetas</Box>
                      <Badge badgeContent={recetas.length} color="primary" />
                    </Box>
                  } 
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AssignmentIcon fontSize="small" />
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Consentimientos</Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Consent.</Box>
                        <Badge badgeContent={consentimientos.length} color="primary" sx={{ ml: 1 }} />
                      </Box>
                    </Box>
                  } 
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VaccinesIcon fontSize="small" />
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Medicina Preventiva</Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Med. Prev.</Box>
                        <Badge badgeContent={vacunas.length + desparasitaciones.length} color="primary" sx={{ ml: 1 }} />
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalHospitalIcon fontSize="small" />
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Remisión Clínica</Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Remisión</Box>
                        <Badge badgeContent={remisiones.length} color="primary" sx={{ ml: 1 }} />
                      </Box>
                    </Box>
                  }
                />
              </Tabs>
            </Box>

            {/* Contenido de las Pestañas */}
            <Box sx={{ 
              p: { xs: 2, sm: 3, md: 4 }, 
              minHeight: { xs: 300, sm: 350, md: 400 }, 
              maxHeight: { xs: '50vh', sm: '60vh', md: 400 }, 
              overflow: 'auto' 
            }}>
              {/* Pestaña Historia Clínica */}
              {expedienteTabValue === 0 && (
                <Box>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    gap: { xs: 2, sm: 1 },
                    mb: 3 
                  }}>
                    <Typography variant="h6" sx={{ 
                      color: '#c46494',
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                      📋 Historia Clínica
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: { xs: 1, sm: 1 },
                      width: { xs: '100%', sm: 'auto' }
                    }}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenNuevaHistoria}
                        sx={{ 
                          bgcolor: '#c46494', 
                          '&:hover': { bgcolor: '#a05080' }, 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          flex: { xs: '1 0 auto', sm: 'initial' }
                        }}
                      >
                        Nueva Historia
                      </Button>

                    </Box>
                  </Box>

                  {loadingHistorias ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                      <CircularProgress sx={{ color: '#c46494' }} />
                      <Typography variant="body2" sx={{ ml: 2, color: '#666' }}>
                        Cargando historias clínicas...
                      </Typography>
                    </Box>
                  ) : historiaClinica.length === 0 ? (
                    <Alert severity="info">
                      No hay historias clínicas registradas para este paciente.
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Box sx={{ width: '100%', maxWidth: '1200px' }}>
                        <Grid container spacing={3}>
                          {historiaClinica.map((historia) => (
                            <Grid item xs={12} key={historia.id}>
                              <Card sx={{ 
                                border: '1px solid #e0e0e0', 
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
                              }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between', 
                                    alignItems: { xs: 'flex-start', sm: 'start' }, 
                                    gap: { xs: 1, sm: 2 },
                                    mb: { xs: 2, sm: 3 }
                                  }}>
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="h6" sx={{ 
                                        color: '#c46494', 
                                        fontWeight: 'bold', 
                                        mb: 1,
                                        fontSize: { xs: '0.95rem', sm: '1.15rem' }
                                      }}>
                                        {historia.motivoConsulta || historia.motivo}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        📅 {formatearFecha(historia.fecha)} • 👨‍⚕️ {historia.veterinario}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ 
                                      display: 'flex', 
                                      gap: 1, 
                                      mt: { xs: 1, sm: 0 },
                                      alignSelf: { xs: 'flex-end', sm: 'flex-start' }
                                    }}>
                                      <Tooltip title="Editar">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => handleEditHistoria(historia)}
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            border: '1px solid rgba(196, 100, 148, 0.5)',
                                            borderRadius: 1.5
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Exportar">
                                        <IconButton
                                          size="small"
                                          color="secondary"
                                          onClick={() => handleExportHistoriaClinica(historia)}
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            border: '1px solid rgba(156, 39, 176, 0.4)',
                                            borderRadius: 1.5
                                          }}
                                        >
                                          <FileDownloadIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <IconButton 
                                        size="small" 
                                        color="error" 
                                        onClick={() => handleDeleteHistoria(historia.id)}
                                        sx={{
                                          width: 32,
                                          height: 32,
                                          border: '1px solid rgba(211, 47, 47, 0.5)',
                                          borderRadius: 1.5
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  
                                  <Grid container spacing={2}>
                                {/* Anamnesis */}
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2" sx={{ 
                                    fontWeight: 'bold', 
                                    color: '#666',
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' } 
                                  }}>
                                    📋 Anamnesis:
                                  </Typography>
                                  <Typography variant="body2" sx={{ 
                                    mb: 2,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    wordBreak: 'break-word'
                                  }}>
                                    {historia.anamnesis || 'No especificado'}
                                  </Typography>
                                </Grid>

                                {/* Examen Físico */}
                                {(historia.frecuenciaCardiaca || historia.temperatura || historia.frecuenciaRespiratoria) && (
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ 
                                      fontWeight: 'bold', 
                                      color: '#666', 
                                      mb: 1,
                                      fontSize: { xs: '0.8rem', sm: '0.875rem' } 
                                    }}>
                                      🔍 Examen Físico:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
                                      {historia.frecuenciaCardiaca && (
                                        <Typography variant="body2">
                                          <strong>F.C.:</strong> {historia.frecuenciaCardiaca} lpm
                                        </Typography>
                                      )}
                                      {historia.frecuenciaRespiratoria && (
                                        <Typography variant="body2">
                                          <strong>F.R.:</strong> {historia.frecuenciaRespiratoria} rpm
                                        </Typography>
                                      )}
                                      {historia.temperatura && (
                                        <Typography variant="body2">
                                          <strong>T°:</strong> {historia.temperatura}°C
                                        </Typography>
                                      )}
                                      {historia.mucosas && (
                                        <Typography variant="body2">
                                          <strong>Mucosas:</strong> {historia.mucosas}
                                        </Typography>
                                      )}
                                      {historia.estadoConciencia && (
                                        <Typography variant="body2">
                                          <strong>Conciencia:</strong> {historia.estadoConciencia}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Grid>
                                )}

                                {/* Antecedentes */}
                                <Grid container spacing={1} sx={{ ml: 0 }}>
                                  {historia.enfermedadesPrevias && (
                                    <Grid item xs={12} md={6}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666' }}>
                                        🏥 Enfermedades Previas:
                                      </Typography>
                                      <Typography variant="body2" sx={{ mb: 1 }}>
                                        {historia.enfermedadesPrevias}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {historia.dieta && (
                                    <Grid item xs={12} md={6}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666' }}>
                                        🍽️ Dieta:
                                      </Typography>
                                      <Typography variant="body2" sx={{ mb: 1 }}>
                                        {historia.dieta}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {historia.esquemaVacunal && (
                                    <Grid item xs={12} md={6}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666' }}>
                                        💉 Vacunas:
                                      </Typography>
                                      <Typography variant="body2" sx={{ mb: 1 }}>
                                        {historia.esquemaVacunal}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {historia.tratamientosRecientes && (
                                    <Grid item xs={12} md={6}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666' }}>
                                        💊 Tratamientos Recientes:
                                      </Typography>
                                      <Typography variant="body2" sx={{ mb: 1 }}>
                                        {historia.tratamientosRecientes}
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>

                                {/* Observaciones */}
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666' }}>
                                    📝 Observaciones:
                                  </Typography>
                                  <Typography variant="body2">
                                    {historia.observaciones || 'Sin observaciones adicionales'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                        </Grid>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Pestaña Recetario */}
              {expedienteTabValue === 1 && (
                <Box>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    gap: { xs: 2, sm: 1 },
                    mb: 3 
                  }}>
                    <Typography variant="h6" sx={{ 
                      color: '#c46494',
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                      💊 Recetario
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenNuevaReceta}
                      sx={{ 
                        bgcolor: '#c46494', 
                        '&:hover': { bgcolor: '#a05080' },
                        width: { xs: '100%', sm: 'auto' },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      Nueva Receta
                    </Button>
                  </Box>

                  {recetas.length === 0 ? (
                    <Alert severity="info">
                      No hay recetas registradas para este paciente.
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Box sx={{ width: '100%', maxWidth: '1200px' }}>
                        <Grid container spacing={3}>
                          {recetas.map((receta) => (
                            <Grid item xs={12} md={6} key={receta.id}>
                              <Card sx={{ 
                                border: '1px solid #e0e0e0', 
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
                              }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between', 
                                    alignItems: { xs: 'flex-start', sm: 'start' }, 
                                    gap: { xs: 1, sm: 2 },
                                    mb: { xs: 2, sm: 2 }
                                  }}>
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="h6" sx={{ 
                                        color: '#c46494', 
                                        fontWeight: 'bold', 
                                        mb: 1,
                                        fontSize: { xs: '0.9rem', sm: '1.1rem' }
                                      }}>
                                        Receta Médica
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        📅 {formatearFecha(receta.fecha)} • 👨‍⚕️ {receta.veterinario}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ 
                                      display: 'flex', 
                                      gap: 1, 
                                      mt: { xs: 1, sm: 0 },
                                      flexWrap: 'wrap',
                                      justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                                      width: { xs: '100%', sm: 'auto' }
                                    }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PrintIcon />}
                                        onClick={() => handleImprimirReceta(receta)}
                                        sx={{
                                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                          py: { xs: 0.5 },
                                          borderRadius: 1.5,
                                          borderColor: '#1976d2',
                                          color: '#1976d2',
                                          flex: { xs: '1 0 auto', sm: 'initial' }
                                        }}
                                      >
                                        Imprimir
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => handleEditReceta(receta)}
                                        sx={{
                                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                          py: { xs: 0.5 },
                                          borderRadius: 1.5,
                                          flex: { xs: '1 0 auto', sm: 'initial' }
                                        }}
                                      >
                                        Editar
                                      </Button>
                                      <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={() => handleDeleteReceta(receta.id)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  
                                  {/* Mostrar medicamentos - maneja tanto nuevo formato como antiguo */}
                                  {receta.medicamentos && Array.isArray(receta.medicamentos) ? (
                                    // Nuevo formato con múltiples medicamentos
                                    <Box sx={{ mb: 2 }}>
                                      {receta.medicamentos.map((med, idx) => (
                                        <Paper 
                                          key={idx}
                                          variant="outlined" 
                                          sx={{ 
                                            p: 1.5, 
                                            mb: 1.5, 
                                            bgcolor: '#fafafa',
                                            borderRadius: 1
                                          }}
                                        >
                                          <Typography variant="subtitle2" sx={{ color: '#c46494', fontWeight: 'bold' }}>
                                            {med.medicamento}
                                          </Typography>
                                          <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                            <Grid item xs={12}>
                                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                <strong>Cantidad:</strong> {med.dosis}
                                              </Typography>
                                            </Grid>
                                            {med.observacion && (
                                              <Grid item xs={12}>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontStyle: 'italic', mt: 0.5, px: 1 }}>
                                                  <strong>Observación:</strong> {med.observacion}
                                                </Typography>
                                              </Grid>
                                            )}
                                          </Grid>
                                        </Paper>
                                      ))}
                                    </Box>
                                  ) : (
                                    // Formato antiguo con un solo medicamento
                                    <Paper 
                                      variant="outlined" 
                                      sx={{ 
                                        p: 1.5, 
                                        mb: 2, 
                                        bgcolor: '#fafafa',
                                        borderRadius: 1
                                      }}
                                    >
                                      <Typography variant="subtitle2" sx={{ color: '#c46494', fontWeight: 'bold' }}>
                                        {receta.medicamento}
                                      </Typography>
                                      <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                        <Grid item xs={12}>
                                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                            <strong>Cantidad:</strong> {receta.dosis}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Paper>
                                  )}

                                  {/* Observaciones */}
                                  {receta.observaciones && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666' }}>
                                        📝 Observaciones:
                                      </Typography>
                                      <Typography variant="body2">
                                        {receta.observaciones}
                                      </Typography>
                                    </Box>
                                  )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                        </Grid>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Pestaña Consentimientos */}
              {expedienteTabValue === 2 && (
                <Box>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    gap: { xs: 2, sm: 1 },
                    mb: 3 
                  }}>
                    <Typography variant="h6" sx={{ 
                      color: '#c46494',
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                      📋 Consentimientos Firmados
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 2 },
                      width: { xs: '100%', sm: 'auto' }
                    }}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenNuevoConsentimiento}
                        sx={{ 
                          bgcolor: '#c46494', 
                          '&:hover': { bgcolor: '#a05080' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          width: { xs: '100%', sm: 'auto' }
                        }}
                      >
                        Nuevo Consentimiento
                      </Button>
                    </Box>
                  </Box>

                  {consentimientos.length === 0 ? (
                    <Alert severity="info">
                      No hay consentimientos firmados para este paciente.
                    </Alert>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      {loadingConsentimientos ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                          <CircularProgress color="secondary" />
                        </Box>
                      ) : (
                        <Grid container spacing={2}>
                          {consentimientos.map((consentimiento) => (
                            <Grid item xs={12} key={consentimiento.id}>
                              <Card sx={{ 
                                border: '1px solid #e0e0e0', 
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
                              }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between', 
                                    alignItems: { xs: 'flex-start', sm: 'start' }, 
                                    gap: { xs: 1, sm: 2 },
                                    mb: { xs: 2, sm: 2 }
                                  }}>
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="h6" sx={{ 
                                        color: '#c46494', 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                        mb: 1
                                      }}>
                                        {consentimiento.tipoConsentimiento || "Consentimiento Informado"}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        📅 {formatearFecha(consentimiento.fecha)} • 👨‍⚕️ {consentimiento.nombreVeterinario || "Sin asignar"}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ 
                                      display: 'flex', 
                                      gap: 1, 
                                      alignItems: 'center',
                                      flexDirection: { xs: 'column', sm: 'row' },
                                      width: { xs: '100%', sm: 'auto' }
                                    }}>
                                      <Chip 
                                        icon={<PolicyIcon fontSize="small" />}
                                        label="Firmado"
                                        color="success"
                                        size="small"
                                        sx={{ 
                                          alignSelf: { xs: 'flex-end', sm: 'auto' },
                                          fontSize: { xs: '0.65rem', sm: '0.75rem' }
                                        }}
                                      />
                                      <Box sx={{ 
                                        display: 'flex', 
                                        gap: 1, 
                                        width: { xs: '100%', sm: 'auto' },
                                        justifyContent: { xs: 'flex-end', sm: 'flex-start' }
                                      }}>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<PrintIcon />}
                                          onClick={() => handleImprimirConsentimiento(consentimiento)}
                                          sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                            py: { xs: 0.5 },
                                            borderRadius: 1.5,
                                            borderColor: '#1976d2',
                                            color: '#1976d2'
                                          }}
                                        >
                                          Imprimir
                                        </Button>
                                      <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<EditIcon />}
                                          onClick={() => handleEditConsentimiento(consentimiento)}
                                          sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                            py: { xs: 0.5 },
                                            borderRadius: 1.5
                                          }}
                                        >
                                          Editar
                                        </Button>
                                        <IconButton 
                                          size="small" 
                                          color="error"
                                          onClick={() => handleDeleteConsentimiento(consentimiento.id)}
                                          sx={{ width: 32, height: 32 }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  </Box>
                                  
                                  <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="body2">
                                      <strong>Veterinario:</strong> {consentimiento.nombreVeterinario}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>M.P.:</strong> {consentimiento.mpVeterinario}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Correo:</strong> {consentimiento.correoVeterinario}
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid #e0e0e0' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      👤 Firmado por: {consentimiento.propietarioNombre || selectedPatient.propietario}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      📋 ID: {consentimiento.id?.substring(0, 8) || ""}
                                    </Typography>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Pestaña Medicina Preventiva */}
              {expedienteTabValue === 3 && (
                <Box sx={{ p: { xs: 1, sm: 2 } }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#c46494', 
                      mb: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                  >
                    💉 Medicina Preventiva
                  </Typography>
                  
                  {/* Sección de Vacunas */}
                  <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: { xs: 'flex-start', sm: 'space-between' }, 
                      alignItems: { xs: 'stretch', sm: 'center' }, 
                      gap: { xs: 1, sm: 0 },
                      mb: { xs: 1, sm: 1.5 }
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#c46494',
                          fontSize: { xs: '1rem', sm: '1.1rem' }
                        }}
                      >
                        💉 Vacunas
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenNuevaVacuna}
                        fullWidth={isMobile}
                        sx={{ 
                          bgcolor: '#c46494', 
                          '&:hover': { bgcolor: '#a05080' },
                          fontSize: { xs: '0.875rem', sm: '0.95rem' },
                          py: { xs: 1, sm: 0.75 }
                        }}
                      >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          Nueva Vacuna
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                          Agregar Vacuna
                        </Box>
                      </Button>
                    </Box>
                    
                    {vacunas.length === 0 ? (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          '& .MuiAlert-message': {
                            width: '100%'
                          }
                        }}
                      >
                        No hay vacunas registradas para este paciente.
                      </Alert>
                    ) : (
                      <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                        {vacunas.map((vacuna) => (
                          <Grid item xs={12} sm={6} lg={4} key={vacuna.id}>
                            <Paper 
                              sx={{ 
                                p: { xs: 1, sm: 1.5 }, 
                                border: '1px solid #e0e0e0',
                                height: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 1
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'flex-start',
                                mb: 0.5,
                                flexWrap: 'wrap',
                                gap: 0.5
                              }}>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    color: '#c46494',
                                    fontSize: { xs: '0.95rem', sm: '1rem' },
                                    fontWeight: 'bold',
                                    lineHeight: 1.2,
                                    flex: 1,
                                    minWidth: 0,
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {vacuna.nombreVacuna}
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex',
                                  gap: 0.25,
                                  flexShrink: 0
                                }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleEditVacuna(vacuna)}
                                    sx={{ 
                                      p: { xs: 0.25, sm: 0.5 },
                                      '& .MuiSvgIcon-root': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                      }
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleDeleteVacuna(vacuna.id)}
                                    sx={{ 
                                      p: { xs: 0.25, sm: 0.5 },
                                      '& .MuiSvgIcon-root': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                              <Box sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25
                              }}>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                    lineHeight: 1.2
                                  }}
                                >
                                  <strong>Laboratorio:</strong> {vacuna.fabricante || 'No especificado'}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                    lineHeight: 1.2
                                  }}
                                >
                                  <strong>Aplicación:</strong> {vacuna.fechaAplicacion}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                    lineHeight: 1.2
                                  }}
                                >
                                  <strong>Próxima:</strong> {vacuna.proximaDosis || 'No programada'}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ 
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                    lineHeight: 1.2,
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  <strong>Dr.:</strong> {vacuna.veterinario || 'No especificado'}
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>
                  
                  {/* Sección de Desparasitaciones */}
                  <Box>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: { xs: 'flex-start', sm: 'space-between' }, 
                      alignItems: { xs: 'stretch', sm: 'center' }, 
                      gap: { xs: 1, sm: 0 },
                      mb: { xs: 1, sm: 1.5 }
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#c46494',
                          fontSize: { xs: '1rem', sm: '1.1rem' }
                        }}
                      >
                        🏷️ Desparasitaciones
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenNuevaDesparasitacion}
                        fullWidth={isMobile}
                        sx={{ 
                          bgcolor: '#c46494', 
                          '&:hover': { bgcolor: '#a05080' },
                          fontSize: { xs: '0.875rem', sm: '0.95rem' },
                          py: { xs: 1, sm: 0.75 }
                        }}
                      >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          Nueva Desparasitación
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                          Agregar Desparasitación
                        </Box>
                      </Button>
                    </Box>
                    
                    {desparasitaciones.length === 0 ? (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          '& .MuiAlert-message': {
                            width: '100%'
                          }
                        }}
                      >
                        No hay desparasitaciones registradas para este paciente.
                      </Alert>
                    ) : (
                      <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                        {desparasitaciones.map((desparasitacion) => (
                          <Grid item xs={12} sm={6} lg={4} key={desparasitacion.id}>
                            <Paper 
                              sx={{ 
                                p: { xs: 1, sm: 1.5 }, 
                                border: '1px solid #e0e0e0',
                                height: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 1
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'flex-start',
                                mb: 0.5,
                                flexWrap: 'wrap',
                                gap: 0.5
                              }}>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    color: '#c46494',
                                    fontSize: { xs: '0.95rem', sm: '1rem' },
                                    fontWeight: 'bold',
                                    lineHeight: 1.2,
                                    flex: 1,
                                    minWidth: 0,
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {desparasitacion.nombreProducto}
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex',
                                  gap: 0.25,
                                  flexShrink: 0
                                }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleEditDesparasitacion(desparasitacion)}
                                    sx={{ 
                                      p: { xs: 0.25, sm: 0.5 },
                                      '& .MuiSvgIcon-root': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                      }
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleDeleteDesparasitacion(desparasitacion.id)}
                                    sx={{ 
                                      p: { xs: 0.25, sm: 0.5 },
                                      '& .MuiSvgIcon-root': {
                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                              <Box sx={{ mb: 0.5 }}>
                                <Chip 
                                  label={
                                    desparasitacion.tipo === 'interno' ? 'Interno' : 
                                    desparasitacion.tipo === 'externo' ? 'Externo' : 
                                    'Interno y Externo'
                                  } 
                                  color={
                                    desparasitacion.tipo === 'interno' ? 'primary' : 
                                    desparasitacion.tipo === 'externo' ? 'secondary' : 
                                    'warning'
                                  }
                                  size="small"
                                  sx={{ 
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    height: { xs: '18px', sm: '20px' }
                                  }}
                                />
                              </Box>
                              <Box sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25
                              }}>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                    lineHeight: 1.2
                                  }}
                                >
                                  <strong>P. activo:</strong> {desparasitacion.principioActivo || 'No especificado'}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                    lineHeight: 1.2
                                  }}
                                >
                                  <strong>Aplicación:</strong> {desparasitacion.fechaAplicacion}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                    lineHeight: 1.2
                                  }}
                                >
                                  <strong>Próxima:</strong> {desparasitacion.proximaDosis || 'No programada'}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ 
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                    lineHeight: 1.2,
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  <strong>Dr.:</strong> {desparasitacion.veterinario || 'No especificado'}
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>
                </Box>
              )}

              {/* Pestaña Remisión Clínica */}
              {expedienteTabValue === 4 && (
                <Box>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 1 },
                    mb: 3
                  }}>
                    <Typography variant="h6" sx={{
                      color: '#c46494',
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                      🏥 Remisión Clínica
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenNuevaRemision}
                      sx={{
                        bgcolor: '#c46494',
                        '&:hover': { bgcolor: '#a05080' },
                        width: { xs: '100%', sm: 'auto' },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      Nueva Remisión
                    </Button>
                  </Box>

                  {remisiones.length === 0 ? (
                    <Alert severity="info">
                      No hay remisiones clínicas registradas para este paciente.
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Box sx={{ width: '100%', maxWidth: '1200px' }}>
                        <Grid container spacing={3}>
                          {remisiones.map((remision) => (
                            <Grid item xs={12} md={6} key={remision.id}>
                              <Card sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
                              }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                  <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'flex-start', sm: 'start' },
                                    gap: { xs: 1, sm: 2 },
                                    mb: { xs: 2, sm: 2 }
                                  }}>
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="h6" sx={{
                                        color: '#c46494',
                                        fontWeight: 'bold',
                                        mb: 1,
                                        fontSize: { xs: '0.9rem', sm: '1.1rem' }
                                      }}>
                                        Remisión Clínica
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        📅 {formatearFecha(remision.fecha)} • 👨‍⚕️ {remision.veterinario}
                                      </Typography>
                                    </Box>
                                    <Box sx={{
                                      display: 'flex',
                                      gap: 1,
                                      mt: { xs: 1, sm: 0 },
                                      flexWrap: 'wrap',
                                      justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                                      width: { xs: '100%', sm: 'auto' }
                                    }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PrintIcon />}
                                        onClick={() => handleImprimirRemision(remision)}
                                        sx={{
                                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                          py: { xs: 0.5 },
                                          borderRadius: 1.5,
                                          borderColor: '#1976d2',
                                          color: '#1976d2',
                                          flex: { xs: '1 0 auto', sm: 'initial' }
                                        }}
                                      >
                                        Imprimir
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => handleEditRemision(remision)}
                                        sx={{
                                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                          py: { xs: 0.5 },
                                          borderRadius: 1.5,
                                          flex: { xs: '1 0 auto', sm: 'initial' }
                                        }}
                                      >
                                        Editar
                                      </Button>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteRemision(remision.id)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Box>
                                  </Box>

                                  <Paper
                                    variant="outlined"
                                    sx={{ p: 1.5, mb: 2, bgcolor: '#fafafa', borderRadius: 1 }}
                                  >
                                    <Typography variant="subtitle2" sx={{ color: '#c46494', fontWeight: 'bold' }}>
                                      Se remite a:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem', mb: 1 }}>
                                      {remision.clinicaRemision}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ color: '#c46494', fontWeight: 'bold' }}>
                                      Motivo:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                      {remision.motivoRemision}
                                    </Typography>
                                  </Paper>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
            <Button 
              onClick={handleCloseExpediente}
              variant="outlined"
              startIcon={<ClearIcon />}
            >
              Cerrar Expediente
            </Button>
            <Button 
              onClick={() => handleEditPatient(selectedPatient)}
              variant="contained"
              startIcon={<EditIcon />}
              sx={{ 
                bgcolor: '#c46494',
                '&:hover': { bgcolor: '#a05080' }
              }}
            >
              Editar Paciente
            </Button>
          </DialogActions>
        </Dialog>

        </>
        )}

        {/* Dialog para agregar/editar paciente */}
        <PatientFormDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSave={handleSavePatient}
          editingPatient={editingPatient}
          initialData={patientFormInitialData}
          loading={loading}
          convertFileToBase64={convertFileToBase64}
          onSnackbar={(severity, message) => {
            setSnackbar({ open: true, message, severity });
          }}
        />

        {/* Diálogo para Nueva Historia Clínica */}
        <HistoriaClinicaDialog
          open={nuevaHistoriaOpen}
          onClose={handleCloseNuevaHistoria}
          onSave={handleSaveHistoriaClinica}
          editingHistoria={editingHistoria}
          initialData={historiaFormInitialData}
          savingHistoria={savingHistoria}
          selectedPatientName={selectedPatient?.nombre}
        />

        <RecetaDialog
          open={nuevaRecetaOpen}
          onClose={handleCloseNuevaReceta}
          onSave={handleSaveReceta}
          editingReceta={editingReceta}
          selectedPatientName={selectedPatient?.nombre}
          initialData={recetaFormInitialData}
        />

        <RemisionDialog
          open={nuevaRemisionOpen}
          onClose={handleCloseNuevaRemision}
          onSave={handleSaveRemision}
          editingRemision={editingRemision}
          selectedPatientName={selectedPatient?.nombre}
          initialData={remisionFormInitialData}
          saving={savingRemision}
        />

        {/* Modal de Vista Previa de Remisión Clínica */}
        <Modal
          open={imprimirRemisionModalOpen}
          onClose={() => setImprimirRemisionModalOpen(false)}
          aria-labelledby="modal-remision-titulo"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '80%', md: '70%' },
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}>
            {remisionParaImprimir && selectedPatient && (
              <>
                <Typography id="modal-remision-titulo" variant="h5" component="h2" sx={{ color: '#c46494', mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
                  Vista Previa de la Remisión
                </Typography>

                <Paper elevation={0} sx={{ p: 3, border: '1px solid #f0f0f0', mb: 3 }}>
                  {/* Encabezado */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, position: 'relative' }}>
                    <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
                      <img src="/logo192.png" alt="Logo" style={{ width: '60px', height: '60px' }} />
                    </Box>
                    <Typography variant="h4" sx={{ color: '#c46494', fontWeight: 'bold', textAlign: 'center' }}>
                      BELKYSVETERINARIA
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mb: 0 }}>
                      Dr(a). {remisionParaImprimir.veterinario || ''}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                      M.P.: {remisionParaImprimir.tarjetaProfesional || ''}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#c46494', fontWeight: 'bold' }}>
                      REMISIÓN CLÍNICA
                    </Typography>
                  </Box>

                  {/* Datos del propietario y paciente */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Propietario: <span style={{ fontWeight: 'normal' }}>{`${selectedPatient.propietario || ""} ${selectedPatient.propietarioApellido || ""}`.trim()}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Fecha: <span style={{ fontWeight: 'normal' }}>{formatearFecha(new Date())}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Paciente: <span style={{ fontWeight: 'normal' }}>{selectedPatient.nombre || ""}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Especie: <span style={{ fontWeight: 'normal' }}>{selectedPatient.especie || ""}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Raza: <span style={{ fontWeight: 'normal' }}>{selectedPatient.raza || ""}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Sexo: <span style={{ fontWeight: 'normal' }}>{selectedPatient.sexo || ""}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Edad: <span style={{ fontWeight: 'normal' }}>{calcularEdad(selectedPatient.fechaNacimiento)}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Color: <span style={{ fontWeight: 'normal' }}>{selectedPatient.color || ""}</span>
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ mb: 3 }} />

                  {/* Clínica de destino */}
                  <Typography variant="h6" sx={{ color: '#c46494', fontWeight: 'bold', mb: 1 }}>
                    SE REMITE A
                  </Typography>
                  <Typography variant="body1" sx={{ ml: 2, mb: 3 }}>
                    {remisionParaImprimir.clinicaRemision || ""}
                  </Typography>

                  {/* Motivo de remisión */}
                  <Typography variant="h6" sx={{ color: '#c46494', fontWeight: 'bold', mb: 1 }}>
                    MOTIVO DE LA REMISIÓN
                  </Typography>
                  <Typography variant="body1" sx={{ ml: 2, mb: 3, whiteSpace: 'pre-wrap' }}>
                    {remisionParaImprimir.motivoRemision || ""}
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  {/* Pie de página */}
                  <Typography variant="body2" sx={{ textAlign: 'center', color: 'gray' }}>
                    Contacto: +57 321 610 3829 • belkys.veterinaria@gmail.com
                  </Typography>
                </Paper>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    onClick={() => setImprimirRemisionModalOpen(false)}
                    variant="outlined"
                    color="inherit"
                    sx={{ mr: 2 }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={generarPDFRemision}
                    variant="contained"
                    color="primary"
                    startIcon={<PrintIcon />}
                    sx={{
                      bgcolor: '#c46494',
                      '&:hover': { bgcolor: '#a25279' }
                    }}
                  >
                    Generar PDF
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Modal>

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={1000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Diálogo de confirmación de eliminación (estilizado acorde al diseño) */}
        <Dialog
          open={confirmDialog.open}
          onClose={confirmDialog.loading ? undefined : closeConfirmDialog}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4, px: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#fdeef4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <WarningIcon sx={{ fontSize: 38, color: '#c46494' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#7a3b59', textAlign: 'center' }}>
              {confirmDialog.title}
            </Typography>
          </Box>
          <DialogContent sx={{ textAlign: 'center', px: 4, pb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {confirmDialog.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 1.5, px: 4, pb: 3, pt: 2 }}>
            <Button
              onClick={closeConfirmDialog}
              variant="outlined"
              disabled={confirmDialog.loading}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                color: '#7a3b59',
                borderColor: '#e0c3d3',
                '&:hover': { borderColor: '#c46494', bgcolor: '#fbf3f7' }
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDialog}
              variant="contained"
              disabled={confirmDialog.loading}
              startIcon={confirmDialog.loading
                ? <CircularProgress size={16} sx={{ color: 'white' }} />
                : <DeleteIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                bgcolor: '#c46494',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#a05080', boxShadow: 'none' }
              }}
            >
              {confirmDialog.loading ? 'Eliminando...' : (confirmDialog.confirmText || 'Eliminar')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Vista Previa de Impresión */}
        <Modal
          open={imprimirModalOpen}
          onClose={() => setImprimirModalOpen(false)}
          aria-labelledby="modal-impresion-titulo"
          aria-describedby="modal-impresion-descripcion"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '80%', md: '70%' },
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}>
            {recetaParaImprimir && selectedPatient && (
              <>
                <Typography id="modal-impresion-titulo" variant="h5" component="h2" sx={{ color: '#c46494', mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
                  Vista Previa de la Receta
                </Typography>
                
                <Paper elevation={0} sx={{ p: 3, border: '1px solid #f0f0f0', mb: 3 }}>
                  {/* Encabezado */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, position: 'relative' }}>
                    {/* Logo en la esquina superior derecha */}
                    <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
                      <img src="/logo192.png" alt="Logo" style={{ width: '60px', height: '60px' }} />
                    </Box>
                    <Typography variant="h4" sx={{ color: '#c46494', fontWeight: 'bold', textAlign: 'center' }}>
                      BELKYSVETERINARIA
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Dr(a). belkys.veterinaria@gmail.com
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#c46494', fontWeight: 'bold' }}>
                      FÓRMULA MÉDICA
                    </Typography>
                  </Box>
                  
                  {/* Datos del propietario y paciente */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Propietario: <span style={{ fontWeight: 'normal' }}>{selectedPatient.propietario || ""}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Fecha: <span style={{ fontWeight: 'normal' }}>{formatearFecha(new Date())}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Paciente: <span style={{ fontWeight: 'normal' }}>{selectedPatient.nombre || ""}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Peso: <span style={{ fontWeight: 'normal' }}>{selectedPatient.peso || ""} Kg</span>
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ mb: 3 }} />
                  
                  {/* Medicamentos */}
                  <Typography variant="h6" sx={{ color: '#c46494', fontWeight: 'bold', mb: 2 }}>
                    MEDICAMENTOS
                  </Typography>
                  
                  {/* Verificar formato de medicamentos */}
                  {recetaParaImprimir.medicamentos && Array.isArray(recetaParaImprimir.medicamentos) ? (
                    // Nuevo formato con múltiples medicamentos
                    <Box sx={{ ml: 2, mb: 3 }}>
                      {recetaParaImprimir.medicamentos.map((med, index) => (
                        <Box key={index} sx={{ mb: 2, pb: 1, borderBottom: index < recetaParaImprimir.medicamentos.length - 1 ? '1px dashed #e0e0e0' : 'none' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {index + 1}. {med.medicamento || ""}
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            <b>Cantidad:</b> {med.dosis || ""}
                          </Typography>
                          {med.frecuencia && (
                            <Typography variant="body2" sx={{ ml: 2 }}>
                              <b>Frecuencia:</b> {med.frecuencia}
                            </Typography>
                          )}
                          {med.duracion && (
                            <Typography variant="body2" sx={{ ml: 2 }}>
                              <b>Duración:</b> {med.duracion}
                            </Typography>
                          )}
                          {med.observacion && (
                            <Typography variant="body2" sx={{ ml: 2, fontStyle: 'italic', mt: 0.5 }}>
                              <b>Observación:</b> {med.observacion}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    // Formato antiguo con un solo medicamento
                    <Box sx={{ ml: 2, mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        1. {recetaParaImprimir.medicamento || ""}
                      </Typography>
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        <b>Cantidad:</b> {recetaParaImprimir.dosis || ""}
                      </Typography>
                      {recetaParaImprimir.frecuencia && (
                        <Typography variant="body2" sx={{ ml: 2 }}>
                          <b>Frecuencia:</b> {recetaParaImprimir.frecuencia}
                        </Typography>
                      )}
                      {recetaParaImprimir.duracion && (
                        <Typography variant="body2" sx={{ ml: 2 }}>
                          <b>Duración:</b> {recetaParaImprimir.duracion}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {/* Observaciones */}
                  {recetaParaImprimir.observaciones && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ color: '#c46494', fontWeight: 'bold', mb: 1 }}>
                        OBSERVACIONES
                      </Typography>
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        {recetaParaImprimir.observaciones}
                      </Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  {/* Pie de página */}
                  <Typography variant="body2" sx={{ textAlign: 'center', color: 'gray' }}>
                    Contacto: +57 321 63829 • belkys.veterinaria@gmail.com
                  </Typography>
                </Paper>
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button 
                    onClick={() => setImprimirModalOpen(false)} 
                    variant="outlined"
                    color="inherit"
                    sx={{ mr: 2 }}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={generarPDFReceta} 
                    variant="contained"
                    color="primary"
                    startIcon={<PrintIcon />}
                    sx={{
                      bgcolor: '#c46494',
                      '&:hover': { bgcolor: '#a25279' }
                    }}
                  >
                    Generar PDF
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Modal>

        {/* Modal para Crear/Editar Consentimiento */}
        <ConsentimientoDialog
          open={nuevoConsentimientoOpen}
          onClose={handleCloseNuevoConsentimiento}
          onSave={handleSaveConsentimiento}
          onPreview={(prevData) => {
            setConsentimientoParaImprimir(prevData);
            setImprimirConsentimientoModalOpen(true);
          }}
          onNotify={setSnackbar}
          editingConsentimiento={editingConsentimiento}
          initialData={consentimientoFormInitialData}
          selectedPatient={selectedPatient}
          selectedPatientName={selectedPatient?.nombre}
          selectedOwnerName={selectedPatient?.propietario}
          pacientesMismoPropietario={getPacientesMismoPropietario(selectedPatient)}
        />

        {/* Modal de Vista Previa del Consentimiento */}
        <Modal
          open={imprimirConsentimientoModalOpen}
          onClose={() => setImprimirConsentimientoModalOpen(false)}
          aria-labelledby="modal-consentimiento-titulo"
          aria-describedby="modal-consentimiento-descripcion"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '80%', md: '70%' },
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}>
            {consentimientoParaImprimir && selectedPatient && (
              <>
                <Typography id="modal-consentimiento-titulo" variant="h5" component="h2" sx={{ color: '#c46494', mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
                  Vista Previa del Consentimiento
                </Typography>
                
                <Paper elevation={0} sx={{ p: 3, border: '1px solid #f0f0f0', mb: 3 }}>
                  {/* Encabezado dinámico según tipo */}
                  {(() => {
                    const rawTipo = (consentimientoParaImprimir.tipoConsentimiento || '').trim();
                    const tipoNorm = (rawTipo && rawTipo.normalize)
                      ? rawTipo.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
                      : rawTipo.toLowerCase();
                    let tipoKey = 'domicilio';
                    if (tipoNorm.includes('vacun')) tipoKey = 'vacunacion';
                    else if (tipoNorm.includes('eutanas') || tipoNorm.includes('eutanasia')) tipoKey = 'eutanasia';
                    else if (tipoNorm.includes('hospital') || tipoNorm.includes('hospitaliz')) tipoKey = 'hospitalizacion';
                    else if (tipoNorm.includes('sedac') || tipoNorm.includes('sedat') || tipoNorm.includes('sedaci')) tipoKey = 'sedacion';
                    else if (tipoNorm.includes('alta') && tipoNorm.includes('volunt')) tipoKey = 'altaVoluntaria';

                    const title = tipoKey === 'vacunacion'
                      ? 'CONSENTIMIENTO INFORMADO PARA VACUNACIÓN'
                      : (tipoKey === 'eutanasia' ? 'CONSENTIMIENTO INFORMADO PARA PROCEDIMIENTO DE EUTANASIA' : (tipoKey === 'hospitalizacion' ? 'CONSENTIMIENTO INFORMADO PARA HOSPITALIZACIÓN' : (tipoKey === 'sedacion' ? 'CONSENTIMIENTO INFORMADO PARA SEDACIÓN' : (tipoKey === 'altaVoluntaria' ? 'CONSTANCIA DE ALTA VOLUNTARIA' : 'CONSENTIMIENTO INFORMADO'))));

                    return (
                      <Box sx={{ textAlign: 'center', mb: 3, position: 'relative' }}>
                        <Typography variant="h5" component="h2" sx={{ mb: 1, color: '#c46494', fontWeight: 'bold' }}>
                          {title}
                        </Typography>
                        <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
                          <img src="/logo192.png" alt="Logo" style={{ width: '50px', height: '50px' }} />
                        </Box>
                      </Box>
                    );
                  })()}
                  
                  {/* Información del veterinario */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1"><strong>Dr(a).</strong> {consentimientoParaImprimir.nombreVeterinario}</Typography>
                    <Typography variant="body1"><strong>M.P.:</strong> {consentimientoParaImprimir.mpVeterinario}</Typography>
                    <Typography variant="body1"><strong>Correo:</strong> {consentimientoParaImprimir.correoVeterinario}</Typography>
                    <Typography variant="body1">
                      {(() => {
                        const lista = consentimientoParaImprimir.pacientes || consentimientoParaImprimir.pacientesGrupo || [];
                        return (
                          <>
                            <strong>{lista.length > 1 ? 'Mascotas:' : 'Mascota:'}</strong> {buildNombresPacientes(lista)}
                          </>
                        );
                      })()}
                    </Typography>
                  </Box>
                  
                  {/* Texto del consentimiento dinámico */}
                  <Box sx={{ mb: 3 }}>
                    {(() => {
                      const rawTipo = (consentimientoParaImprimir.tipoConsentimiento || '').trim();
                      const tipoNorm = (rawTipo && rawTipo.normalize)
                        ? rawTipo.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
                        : rawTipo.toLowerCase();
                      let tipoKey = 'domicilio';
                      const cedula = getPropietarioCedula(selectedPatient);
                      const pacientesConsent = consentimientoParaImprimir.pacientes || consentimientoParaImprimir.pacientesGrupo || [selectedPatient];
                      const responsableFrase = buildResponsableFraseHtml(pacientesConsent);
                      if (tipoNorm.includes('vacun')) tipoKey = 'vacunacion';
                      else if (tipoNorm.includes('eutanas') || tipoNorm.includes('eutanasia')) tipoKey = 'eutanasia';
                      else if (tipoNorm.includes('hospital') || tipoNorm.includes('hospitaliz')) tipoKey = 'hospitalizacion';
                      else if (tipoNorm.includes('sedac') || tipoNorm.includes('sedat') || tipoNorm.includes('sedaci')) tipoKey = 'sedacion';
                      else if (tipoNorm.includes('alta') && tipoNorm.includes('volunt')) tipoKey = 'altaVoluntaria';

                      if (tipoKey === 'vacunacion') {
                        return (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', textAlign: 'justify' }}>
                            {`La vacunación es un procedimiento médico preventivo cuyo objetivo es estimular el sistema inmune del paciente frente a diferentes enfermedades infecciosas propias de la especie.

Como todo procedimiento médico veterinario, pueden presentarse efectos secundarios leves y transitorios posteriores a la aplicación de la vacuna, entre ellos:

Dolor o inflamación en el sitio de aplicación. Decaimiento, somnolencia o disminución temporal del apetito. Fiebre leve durante las primeras 24–48 horas posteriores a la vacunación. En casos poco frecuentes pueden presentarse reacciones adversas asociadas a la respuesta individual del paciente, tales como:
Vómito o diarrea, inflamación facial, urticaria o reacciones alérgicas, dificultad respiratoria.

Aunque estas reacciones son poco comunes y la vacunación continúa siendo un procedimiento seguro y altamente recomendado, en casos excepcionales pueden presentarse complicaciones graves que comprometan la vida del paciente.

En caso de observar cualquiera de estos signos, el propietario deberá comunicarse inmediatamente con el Médico Veterinario tratante o acudir al centro veterinario más cercano. El propietario declara haber suministrado información veraz sobre el estado de salud actual del paciente y comprende los beneficios y posibles riesgos asociados al procedimiento.`}
                          </Typography>
                        );
                      }

                      if (tipoKey === 'eutanasia') {
                        const fechaConsent = formatearFecha(consentimientoParaImprimir.fecha || new Date());
                        const ownerName = getPropietarioNombreCompleto(selectedPatient) || '[nombre del propietario]';
                        const ownerHtml = boldHtml(ownerName);
                        const idSegment = cedula ? ` identificado(a) con documento No. ${boldHtml(cedula)},` : '';
                        const eutText = `CONSENTIMIENTO INFORMADO PARA PROCEDIMIENTO DE EUTANASIA

La eutanasia es un procedimiento médico veterinario humanitario cuyo objetivo es evitar sufrimiento innecesario y proporcionar una muerte digna al paciente, de acuerdo con criterios médicos, éticos y de bienestar animal.

Yo, ${ownerHtml}${idSegment} en calidad de propietario(a) y/o responsable ${responsableFrase}, autorizo de manera libre y voluntaria la realización del procedimiento de eutanasia el día ${fechaConsent}.

Declaro haber recibido información clara y comprensible acerca de:
• El estado de salud y pronóstico del paciente.
• El objetivo y desarrollo del procedimiento.
• Las posibles alternativas terapéuticas, en caso de existir.
• El carácter irreversible de la eutanasia.

Comprendo que el procedimiento será realizado mediante la administración de medicamentos veterinarios destinados a inducir una pérdida de conciencia seguida del fallecimiento del paciente de manera tranquila y humanitaria.

Asimismo, entiendo que durante el procedimiento pueden presentarse algunas respuestas involuntarias del organismo, tales como respiraciones agónicas, movimientos reflejos, vocalizaciones, relajación de esfínteres u otras manifestaciones fisiológicas posteriores a la pérdida de conciencia, las cuales no indican dolor o sufrimiento.

Declaro haber leído y comprendido la información anteriormente descrita y autorizo voluntariamente la realización del procedimiento.`;
                        return (
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-line', mb: 2, textAlign: 'justify' }}
                            dangerouslySetInnerHTML={{ __html: eutText }}
                          />
                        );
                      }

                      if (tipoKey === 'hospitalizacion') {
                        const fechaConsent = formatearFecha(consentimientoParaImprimir.fecha || new Date());
                        const ownerName = getPropietarioNombreCompleto(selectedPatient) || '[nombre del propietario]';
                        const ownerHtml = boldHtml(ownerName);
                        const idSegment = cedula ? ` identificado(a) con documento No. ${boldHtml(cedula)},` : '';
                        const hospText = `Yo, ${ownerHtml}${idSegment} en calidad de propietario(a) y/o responsable ${responsableFrase}, autorizo voluntariamente la atención y manejo médico bajo modalidad de hospitalización domiciliaria el día ${fechaConsent}.

Declaro haber recibido información clara y comprensible acerca de:

• El estado de salud actual y diagnóstico presuntivo o definitivo del paciente.
• El plan médico y tratamientos recomendados.
• Los posibles riesgos, beneficios y complicaciones asociadas a la condición clínica y evolución del paciente.
• Las limitaciones que puede tener el manejo domiciliario en comparación con una hospitalización intrahospitalaria.

Comprendo que durante la hospitalización domiciliaria podrán requerirse procedimientos médicos complementarios, administración de medicamentos, monitoreo y controles periódicos según la evolución del paciente.

Entiendo que, aunque el Médico Veterinario brindará atención y cuidados profesionales de acuerdo con los principios médicos y de bienestar animal, no es posible garantizar la recuperación total del paciente debido a la naturaleza variable de cada enfermedad y respuesta individual al tratamiento.

Asimismo, comprendo que la condición clínica del paciente puede cambiar durante su evolución y que, en caso de deterioro, compromiso de la vida del paciente o necesidad de monitoreo continuo, podrá recomendarse traslado e ingreso a hospitalización en clínica veterinaria para un manejo más intensivo.

Me comprometo a:

• Seguir las recomendaciones médicas indicadas.
• Mantener comunicación activa con el Médico Veterinario tratante.
• Informar oportunamente cualquier cambio en el estado del paciente.
• Facilitar un ambiente adecuado y seguro para la atención médica domiciliaria.
• Asumir los costos derivados de la atención, tratamientos y procedimientos autorizados.

Declaro haber leído y comprendido la información anteriormente descrita y autorizo voluntariamente la hospitalización domiciliaria del paciente.`;
                        return (
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-line', mb: 2, textAlign: 'justify' }}
                            dangerouslySetInnerHTML={{ __html: hospText }}
                          />
                        );
                      }

                      if (tipoKey === 'sedacion') {
                        const ownerName = getPropietarioNombreCompleto(selectedPatient) || '[nombre del propietario]';
                        const ownerHtml = boldHtml(ownerName);
                        const idSegment = cedula ? ` identificado(a) con documento No. ${boldHtml(cedula)},` : '';
                        const sedText = `Yo, ${ownerHtml}${idSegment} en calidad de propietario(a) y/o responsable ${responsableFrase}, autorizo voluntariamente la realización del procedimiento médico y/o quirúrgico

Declaro haber recibido información clara y comprensible acerca de:

• El procedimiento recomendado y su finalidad.
• Los beneficios esperados y posibles riesgos asociados.
• El uso de sedación y/o anestesia requerido para la realización del procedimiento.
• Las posibles complicaciones relacionadas con la condición clínica del paciente, enfermedades preexistentes o respuesta individual a medicamentos y anestésicos.

Comprendo que, aunque se realizarán las valoraciones y medidas médicas necesarias para disminuir riesgos y brindar un manejo anestésico seguro, ningún procedimiento anestésico o quirúrgico está completamente exento de complicaciones.

Entiendo que durante o posterior al procedimiento pueden presentarse complicaciones como:

• Reacciones adversas o alérgicas a medicamentos.
• Hemorragias.
• Alteraciones respiratorias o cardiovasculares.
• Hipotensión, hipotermia o arritmias.
• Recuperación anestésica prolongada o alteraciones neurológicas.
• Infecciones, inflamación o dehiscencia postquirúrgica.
• Complicaciones derivadas de enfermedades no diagnosticadas previamente.
• Paro cardiorrespiratorio.
• Fallecimiento del paciente, aun tomando las medidas médicas y anestésicas correspondientes.

Asimismo, comprendo que los exámenes prequirúrgicos y la valoración clínica disminuyen riesgos anestésicos y quirúrgicos, pero no los eliminan completamente.

En caso de presentarse una situación médica inesperada que comprometa la vida o bienestar del paciente durante el procedimiento, autorizo al Médico Veterinario tratante a realizar las maniobras, procedimientos o tratamientos que considere necesarios según su criterio profesional.

Entiendo que no es posible garantizar resultados absolutos ni la recuperación total del paciente debido a la respuesta individual de cada animal y a la evolución propia de cada procedimiento o enfermedad.

Declaro haber tenido la oportunidad de resolver mis dudas, haber leído y comprendido la información anteriormente descrita y autorizo voluntariamente la realización del procedimiento mencionado.`;
                        return (
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-line', mb: 2, textAlign: 'justify' }}
                            dangerouslySetInnerHTML={{ __html: sedText }}
                          />
                        );
                      }

                      if (tipoKey === 'altaVoluntaria') {
                        const ownerName = getPropietarioNombreCompleto(selectedPatient) || '[nombre del propietario]';
                        const ownerHtml = boldHtml(ownerName);
                        const idSegment = cedula ? ` identificado(a) con documento No. ${boldHtml(cedula)},` : '';
                        const altaText = `Yo, ${ownerHtml}${idSegment} en calidad de propietario(a) y/o responsable ${responsableFrase}, autorizo voluntariamente la realización del procedimiento médico y/o quirúrgico.

Asimismo, declaro haber sido informado(a) sobre la necesidad y beneficios del manejo médico, hospitalización, monitoreo, procedimientos diagnósticos o tratamientos recomendados para el bienestar del paciente.

De manera libre y voluntaria decido no continuar y/o rechazar el manejo médico recomendado, incluyendo hospitalización domiciliaria, hospitalización en clínica veterinaria, procedimientos diagnósticos, tratamientos o monitoreo sugerido según la condición clínica del paciente.

Comprendo que esta decisión puede implicar riesgos para la salud del paciente, incluyendo empeoramiento de su condición clínica, progresión de la enfermedad, complicaciones médicas e incluso fallecimiento.

Declaro que el Médico Veterinario tratante me explicó las posibles consecuencias derivadas de esta decisión y que tuve la oportunidad de resolver mis dudas antes de tomarla.

Asimismo, asumo la responsabilidad del manejo y cuidados posteriores del paciente en casa y me comprometo a seguir las recomendaciones médicas entregadas.

Declaro haber leído y comprendido la información anteriormente descrita y firmo el presente documento de manera libre y voluntaria.`;
                        return (
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-line', mb: 2, textAlign: 'justify' }}
                            dangerouslySetInnerHTML={{ __html: altaText }}
                          />
                        );
                      }

                      // Por defecto: consentimiento a domicilio — organizar como solicitó el usuario
                      return (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 2, textAlign: 'justify' }}>
                          {`El objetivo de este documento es informar al propietario de la mascota sobre el procedimiento y obtener su consentimiento para la realización de servicios veterinarios a domicilio. Se realizarán visitas domiciliarias para proporcionar atención veterinaria que puede incluir, pero no se limita a, exámenes físicos, vacunaciones, tratamientos y procedimientos menores. Todos los procedimientos serán explicados detalladamente al propietario antes de su realización. Este servicio ayuda a disminuir el estrés de mascotas que no están acostumbradas a salir de sus casas y son sensibles al ruido, al contar con una atención personalizada en la comodidad de su hogar, evitamos desplazamientos y largas esperas en una recepción. Toda la información relacionada con la salud de su mascota será tratada con la máxima confidencialidad y solo se compartirá con terceros con su consentimiento expreso.`}
                          <br />
                          <br />
                          <span>
                            Yo, <strong>{getPropietarioNombreCompleto(selectedPatient) || 'Stefanny'}</strong>{cedula ? <> identificado(a) con documento No. <strong>{cedula}</strong>,</> : ','} propietario(a) <span dangerouslySetInnerHTML={{ __html: responsableFrase }} />, confirmo que he sido informado(a) de los procedimientos que se llevarán a cabo durante la consulta veterinaria a domicilio y comprendo los beneficios y riesgos asociados. Autorizo al veterinario a realizar los procedimientos mencionados y a administrar cualquier tratamiento necesario para la salud y el bienestar de mi mascota.
                          </span>
                          <br />
                          <br />
                          {`Quiero asegurarme de que entienda que:\n`}
                          <br />
                          {`1. En algunos casos, podría ser necesario trasladar a mi mascota a una clínica veterinaria para recibir atención adicional o más avanzada.`}
                          <br />
                          {`2. Aunque el objetivo es siempre el mejor resultado posible, pueden surgir complicaciones imprevistas durante o después del procedimiento.`}
                          <br />
                          {`3. El veterinario me ha explicado detalladamente todos los procedimientos propuestos y ha respondido a todas mis preguntas de manera satisfactoria.`}
                          <br />
                          <br />
                          {`Con esta firma, doy mi consentimiento para que se realicen los servicios veterinarios especificados anteriormente.`}
                        </Typography>
                      );
                    })()}
                  </Box>

                  {/* Mostrar texto almacenado si existe (ayuda para comprobar diferencias) */}
                  {(consentimientoParaImprimir.texto || consentimientoParaImprimir.contenido || consentimientoParaImprimir.descripcion || consentimientoParaImprimir.body) && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Texto almacenado en el consentimiento</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {consentimientoParaImprimir.texto || consentimientoParaImprimir.contenido || consentimientoParaImprimir.descripcion || consentimientoParaImprimir.body}
                      </Typography>
                    </Box>
                  )}

                  {/* debug JSON removed */}

                  {/* Confirmación del propietario */}
                  <Box sx={{ mb: 3 }}>
                    {(() => {
                      const rawTipo = (consentimientoParaImprimir.tipoConsentimiento || '').trim();
                      const tipoNorm = (rawTipo && rawTipo.normalize)
                        ? rawTipo.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
                        : rawTipo.toLowerCase();
                      const tipoKey = tipoNorm.includes('vacun')
                        ? 'vacunacion'
                        : (tipoNorm.includes('eutanas') || tipoNorm.includes('eutanasia') ? 'eutanasia' : 'domicilio');
                      const cedula2 = getPropietarioCedula(selectedPatient);

                      if (tipoKey === 'vacunacion') {
                        const fechaConsentimiento = formatearFecha(consentimientoParaImprimir.fecha || new Date());
                        return (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                            Yo, <strong>{getPropietarioNombreCompleto(selectedPatient) || ''}</strong>
                            {cedula2 ? <> identificado(a) con documento No. <strong>{cedula2}</strong>,</> : ''} propietario(a) del paciente <strong>{selectedPatient.nombre || ''}</strong>, especie {selectedPatient.especie || ''}, raza {selectedPatient.raza || ''}, autorizo voluntariamente el día {fechaConsentimiento} la aplicación del plan vacunal correspondiente. Declaro haber leído y comprendido la información anteriormente descrita, explicada por el médico tratante y acepto voluntariamente la realización del procedimiento.
                          </Typography>
                        );
                      }

                      

                      // Domicilio: ya incluido arriba en el texto principal, no repetir aquí
                      return null;
                    })()}
                  </Box>
                  
                  {/* Firma */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5, pt: 2 }}>
                    <Box sx={{ maxWidth: '200px' }}>
                      {consentimientoParaImprimir.firmaPropietario && (
                        <img 
                          src={consentimientoParaImprimir.firmaPropietario} 
                          alt="Firma del propietario" 
                          style={{ maxWidth: '100%', height: 'auto', maxHeight: '100px' }}
                        />
                      )}
                      <Typography variant="body2" sx={{ borderTop: '1px solid black', pt: 1, mt: 1 }}>
                        Firma del Propietario
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2">
                        Fecha: {formatearFecha(consentimientoParaImprimir.fecha) || new Date().toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button 
                    variant="outlined" 
                    color="secondary" 
                    startIcon={<ClearIcon />} 
                    onClick={() => setImprimirConsentimientoModalOpen(false)} 
                  >
                    Cerrar
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<PrintIcon />} 
                    onClick={generarPDFConsentimiento} 
                    sx={{ bgcolor: '#c46494', '&:hover': { bgcolor: '#a05080' } }}
                  >
                    Generar PDF
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Modal>

      </Box>

      {/* Menús para cambio de estado de pacientes en la lista */}
      {filteredPatients.map(paciente => (
        <Menu
          key={`status-menu-${paciente.id}`}
          id={`status-menu-${paciente.id}`}
          anchorEl={statusMenuAnchorEl[paciente.id] || null}
          open={Boolean(statusMenuAnchorEl[paciente.id])}
          onClose={() => {
            setStatusMenuAnchorEl({
              ...statusMenuAnchorEl,
              [paciente.id]: null
            });
          }}
          MenuListProps={{
            'aria-labelledby': 'status-button',
          }}
          sx={{ '& .MuiPaper-root': { boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 } }}
        >
          <Box sx={{ px: 1, py: 0.5 }}>
            <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 'bold', textAlign: 'center' }}>
              Cambiar estado
            </Typography>
          </Box>
          <MenuItem 
            onClick={() => {
              handleChangePatientStatus(paciente.id, 'activo');
              setStatusMenuAnchorEl({
                ...statusMenuAnchorEl,
                [paciente.id]: null
              });
            }}
            sx={{ color: 'success.main' }}
          >
            <ListItemIcon>
              <PetsIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            Activo
          </MenuItem>
          <MenuItem 
            onClick={() => {
              handleChangePatientStatus(paciente.id, 'en_tratamiento');
              setStatusMenuAnchorEl({
                ...statusMenuAnchorEl,
                [paciente.id]: null
              });
            }}
            sx={{ color: 'warning.main' }}
          >
            <ListItemIcon>
              <MedicalIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            En Tratamiento
          </MenuItem>
          <MenuItem 
            onClick={() => {
              handleChangePatientStatus(paciente.id, 'critico');
              setStatusMenuAnchorEl({
                ...statusMenuAnchorEl,
                [paciente.id]: null
              });
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <LocalHospitalIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            Crítico
          </MenuItem>
          <MenuItem 
            onClick={() => {
              handleChangePatientStatus(paciente.id, 'inactivo');
              setStatusMenuAnchorEl({
                ...statusMenuAnchorEl,
                [paciente.id]: null
              });
            }}
            sx={{ color: 'text.secondary' }}
          >
            <ListItemIcon>
              <BlockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </ListItemIcon>
            Inactivo
          </MenuItem>
        </Menu>
      ))}

      {/* Dialog para agregar/editar vacuna */}
      <Dialog 
        open={nuevaVacunaOpen} 
        onClose={handleCloseNuevaVacuna} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: { xs: 0, sm: 2 },
            margin: { xs: 0, sm: 2 },
            maxHeight: { xs: '100vh', sm: '95vh' },
            height: { xs: '100vh', sm: 'auto' }
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: '#c46494', 
            color: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 }
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <VaccinesIcon sx={{ mr: 2, fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    fontWeight: 'bold'
                  }}
                >
                  {editingVacuna ? 'Editar Vacuna' : 'Nueva Vacuna'}
                </Typography>
                {!isMobile && (
                  <Typography 
                    variant="body2" 
                    sx={{ opacity: 0.9, fontSize: '0.875rem' }}
                  >
                    {editingVacuna ? 'Actualiza la información de la vacuna' : 'Registra una nueva vacuna aplicada'}
                  </Typography>
                )}
              </Box>
            </Box>
            {isMobile && (
              <IconButton 
                onClick={handleCloseNuevaVacuna}
                sx={{ color: 'white' }}
              >
                <ClearIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent 
          sx={{ 
            mt: { xs: 1, sm: 2 },
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 }
          }}
        >
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre de la Vacuna"
                required
                value={vacunaData.nombreVacuna}
                onChange={(e) => handleVacunaDataChange('nombreVacuna', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Laboratorio"
                value={vacunaData.fabricante}
                onChange={(e) => handleVacunaDataChange('fabricante', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha de Aplicación"
                type="date"
                required
                value={vacunaData.fechaAplicacion}
                onChange={(e) => handleVacunaDataChange('fechaAplicacion', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Próxima Dosis"
                type="date"
                value={vacunaData.proximaDosis}
                onChange={(e) => handleVacunaDataChange('proximaDosis', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lote"
                value={vacunaData.lote}
                onChange={(e) => handleVacunaDataChange('lote', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Veterinario"
                required
                value={vacunaData.veterinario}
                onChange={(e) => handleVacunaDataChange('veterinario', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observaciones"
                multiline
                rows={isMobile ? 2 : 3}
                value={vacunaData.observaciones}
                onChange={(e) => handleVacunaDataChange('observaciones', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{ 
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            gap: { xs: 1, sm: 2 },
            borderTop: '1px solid #e0e0e0',
            bgcolor: '#f5f5f5'
          }}
        >
          <Button 
            onClick={handleCloseNuevaVacuna}
            fullWidth={isMobile}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '0.95rem' },
              py: { xs: 1, sm: 0.75 }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveVacuna} 
            variant="contained"
            fullWidth={isMobile}
            sx={{ 
              bgcolor: '#c46494', 
              '&:hover': { bgcolor: '#a05080' },
              fontSize: { xs: '0.875rem', sm: '0.95rem' },
              py: { xs: 1, sm: 0.75 }
            }}
          >
            {editingVacuna ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para agregar/editar desparasitación */}
      <Dialog 
        open={nuevaDesparasitacionOpen} 
        onClose={handleCloseNuevaDesparasitacion} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: { xs: 0, sm: 2 },
            margin: { xs: 0, sm: 2 },
            maxHeight: { xs: '100vh', sm: '95vh' },
            height: { xs: '100vh', sm: 'auto' }
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: '#c46494', 
            color: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 }
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MedicalIcon sx={{ mr: 2, fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    fontWeight: 'bold'
                  }}
                >
                  {editingDesparasitacion ? 'Editar Desparasitación' : 'Nueva Desparasitación'}
                </Typography>
                {!isMobile && (
                  <Typography 
                    variant="body2" 
                    sx={{ opacity: 0.9, fontSize: '0.875rem' }}
                  >
                    {editingDesparasitacion ? 'Actualiza la información de la desparasitación' : 'Registra una nueva desparasitación aplicada'}
                  </Typography>
                )}
              </Box>
            </Box>
            {isMobile && (
              <IconButton 
                onClick={handleCloseNuevaDesparasitacion}
                sx={{ color: 'white' }}
              >
                <ClearIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent 
          sx={{ 
            mt: { xs: 1, sm: 2 },
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 }
          }}
        >
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre del Producto"
                required
                value={desparasitacionData.nombreProducto}
                onChange={(e) => handleDesparasitacionDataChange('nombreProducto', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                required
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              >
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={desparasitacionData.tipo}
                  onChange={(e) => handleDesparasitacionDataChange('tipo', e.target.value)}
                  sx={{
                    '& .MuiSelect-select': {
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                >
                  <MenuItem value="interno">Interno</MenuItem>
                  <MenuItem value="externo">Externo</MenuItem>
                  <MenuItem value="interno y externo">Interno y Externo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Principio Activo"
                value={desparasitacionData.principioActivo}
                onChange={(e) => handleDesparasitacionDataChange('principioActivo', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Dosis"
                value={desparasitacionData.dosis}
                onChange={(e) => handleDesparasitacionDataChange('dosis', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha de Aplicación"
                type="date"
                required
                value={desparasitacionData.fechaAplicacion}
                onChange={(e) => handleDesparasitacionDataChange('fechaAplicacion', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Próxima Dosis"
                type="date"
                value={desparasitacionData.proximaDosis}
                onChange={(e) => handleDesparasitacionDataChange('proximaDosis', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Veterinario"
                required
                value={desparasitacionData.veterinario}
                onChange={(e) => handleDesparasitacionDataChange('veterinario', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observaciones"
                multiline
                rows={isMobile ? 2 : 3}
                value={desparasitacionData.observaciones}
                onChange={(e) => handleDesparasitacionDataChange('observaciones', e.target.value)}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{ 
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            gap: { xs: 1, sm: 2 },
            borderTop: '1px solid #e0e0e0',
            bgcolor: '#f5f5f5'
          }}
        >
          <Button 
            onClick={handleCloseNuevaDesparasitacion}
            fullWidth={isMobile}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '0.95rem' },
              py: { xs: 1, sm: 0.75 }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveDesparasitacion} 
            variant="contained"
            fullWidth={isMobile}
            sx={{ 
              bgcolor: '#c46494', 
              '&:hover': { bgcolor: '#a05080' },
              fontSize: { xs: '0.875rem', sm: '0.95rem' },
              py: { xs: 1, sm: 0.75 }
            }}
          >
            {editingDesparasitacion ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default GestionPacientes;
